import React, { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = "https://kmesrtgnkohmflyoestm.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImttZXNydGdua29obWZseW9lc3RtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4MDA1OTEsImV4cCI6MjA4ODM3NjU5MX0.yHWtnNd-jOSfXvJaArKPA24knWwVEeirCNuR8PK5uho";
const VAPID_PUBLIC_KEY = "BMAN5oPGbxRoC9BrzkJYcHhP6kIiRSbB9CgAfIDwrHN62qI12E22l4NlNpnZn1hLsRtAgEKElTJYVmYL-IlYx94";

// ── Converte base64url in Uint8Array (per VAPID) ──
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return new Uint8Array([...raw].map(c => c.charCodeAt(0)));
}

// ── Registra service worker e sottoscrivi push ──
async function subscribeToPush(supabase, userId) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    const sub = existing || await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    if (!sub) return null;
    const json = sub.toJSON();
    // Prima prova a cancellare la vecchia subscription per questo utente/endpoint
    await fetch(`${SUPABASE_URL}/rest/v1/push_subscriptions?user_id=eq.${userId}&endpoint=eq.${encodeURIComponent(json.endpoint)}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY, "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
    });
    // Poi inserisce la nuova
    const res = await fetch(`${SUPABASE_URL}/rest/v1/push_subscriptions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
        "Prefer": "return=minimal",
      },
      body: JSON.stringify({ user_id: userId, endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth }),
    });
    if (res.ok || res.status === 201) {
      console.log("[Push] Subscription salvata ✓");
    } else {
      const err = await res.text();
      console.warn("[Push] Errore salvataggio:", res.status, err);
    }
    return sub;
  } catch (e) {
    console.warn("[Push] Errore sottoscrizione:", e.message);
    return null;
  }
}

// ── Invia push tramite edge function ──
async function sendPushNotification(userIdOrIds, title, body, url = "/") {
  const isArray = Array.isArray(userIdOrIds);
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/send-push`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify(isArray
        ? { user_ids: userIdOrIds, title, body, url }
        : { user_id: userIdOrIds, title, body, url }
      ),
    });
  } catch (e) { console.warn("[Push] Errore invio:", e.message); }
}
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Safe localStorage wrapper (fallback for Brave/strict privacy browsers)
const _memStore = {};
const safeStore = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return _memStore[k]||null; } },
  set: (k,v) => { try { localStorage.setItem(k,v); } catch {} _memStore[k]=v; },
  del: (k) => { try { localStorage.removeItem(k); } catch {} delete _memStore[k]; },
};


// ─────────────────────────────────────────────────────────────
// COSTANTI
// ─────────────────────────────────────────────────────────────
const FIXED_EMAILS = [
  "a.grillo@likeyousrl.com","a.celato@likeyousrl.com","n.iannicelli@likeyousrl.com",
  "c.giampieri@likeyousrl.com","e.verdecchia@likeyousrl.com","m.arnaldo@likeyousrl.com",
  "n.mulas@likeyousrl.com","s.cilla@likeyousrl.com","g.rago@likeyousrl.com"
];
const FIXED_NAMES = ["Alessia Grillo","Alessia Celato","Nicola Iannicelli","Camilla Giampieri",
  "Erica Verdecchia","Martina Arnaldo","Nicolò Mulas","Sara Cilla","Giovanni Rago"];

const FULL_REMOTE_EMAILS = [
  "g.campo@likeyousrl.com",        // Giulia
  "b.manfioletti@likeyousrl.com",  // Beatrice
  "s.castelluccio@likeyousrl.com", // Samuela
  "l.massaro@likeyousrl.com",      // Leonardo Massaro
];
const FULL_REMOTE_NAMES = ["Giulia Campo","Beatrice Manfioletti","Samuela Castelluccio","Leonardo Massaro"];

const isFixed = p => {
  if (!p) return false;
  if (p.sw_permission != null) return p.sw_permission;
  if (p.email) return FIXED_EMAILS.includes(p.email);
  return FIXED_NAMES.includes(p.name);
};

const isFullRemote = p => {
  if (!p) return false;
  if (p.is_full_remote != null) return p.is_full_remote;
  if (p.email) return FULL_REMOTE_EMAILS.includes(p.email);
  return FULL_REMOTE_NAMES.includes(p.name);
};

const LYB  = "#7A9BAD"; // fallback statico (usato fuori App)
const BLK  = "#1A1A1A";
const WHT  = "#FFFFFF";
const BG   = "#F4F4F4";
const BDR  = "#E2E2E2";
const SUB  = "#888";
const AVCOLS = ["#7A9BAD","#8A7F9D","#6A9B8F","#A08060","#9A7080","#7A8FA8","#6A8A7A","#A0806A"];

const today      = new Date();
const todayStr   = (()=>{ const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
const fmtDate    = d => { const r = new Date(d); return `${r.getFullYear()}-${String(r.getMonth()+1).padStart(2,"0")}-${String(r.getDate()).padStart(2,"0")}`; };

// Calcola Pasqua (algoritmo di Butcher)
const easterDate = (year) => {
  const a=year%19,b=Math.floor(year/100),c=year%100,d=Math.floor(b/4),e=b%4;
  const f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3),h=(19*a+b-d-g+15)%30;
  const i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7;
  const m=Math.floor((a+11*h+22*l)/451);
  const month=Math.floor((h+l-7*m+114)/31);
  const day=((h+l-7*m+114)%31)+1;
  return new Date(year, month-1, day);
};

// Festivi italiani per un anno dato
const italianHolidays = (year) => {
  const easter = easterDate(year);
  const easterStr = fmtDate(easter);
  const easterMonday = new Date(easter); easterMonday.setDate(easter.getDate()+1);
  const easterMondayStr = fmtDate(easterMonday);
  return new Set([
    `${year}-01-01`, // Capodanno
    `${year}-01-06`, // Epifania
    easterStr,       // Pasqua
    easterMondayStr, // Pasquetta
    `${year}-04-25`, // Liberazione
    `${year}-05-01`, // Festa del Lavoro
    `${year}-06-02`, // Repubblica
    `${year}-08-15`, // Ferragosto
    `${year}-11-01`, // Ognissanti
    `${year}-12-08`, // Immacolata
    `${year}-12-25`, // Natale
    `${year}-12-26`, // Santo Stefano
  ]);
};

const isItalianHoliday = (ds) => {
  const year = parseInt(ds.substring(0,4));
  return italianHolidays(year).has(ds);
};
const addD       = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const shortName  = (name) => { if(!name) return ""; const p=name.trim().split(/\s+/); return p.length===1?p[0]:p[0]+" "+p[p.length-1][0]+"."; };
const DAY    = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
const MON    = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
                "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

// ─────────────────────────────────────────────────────────────
// LOGO SVG — fedele al PDF vettoriale LikeYou
// ─────────────────────────────────────────────────────────────
function LYLogo({ size = 40, light = false }) {
  const ring = light ? "#FFF" : "#6096AB";
  const body = light ? "#FFF" : "#231F20";
  const dot  = light ? "#BBCFDC" : "#6096AB";
  // Path originali dal PDF vettoriale, scalati con matrix precisa a viewBox 100x100
  // matrix(0.352777, 0, 0, -0.349773, tx, ty) — stessa trasformazione del PDF
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      {/* Cerchio aperto azzurro — path originale PDF */}
      <path
        transform="matrix(0.352777,0,0,-0.349773,47.804,99.953)"
        d="M0 0C-74.03-3.186-133.628-63.808-135.461-137.883-136.407-176.083-122.748-212.282-97.25-239.415-73.893-264.271-54.581-275.093-22.345-283.327-21.634-283.509-20.884-283.177-20.544-282.527L8.205-234.207-.51-219.758-26.495-262.784C-27.123-263.914-28.437-264.496-29.689-264.174-55.557-257.528-74.557-242.65-91.64-221.846-110.143-199.31-120.332-170.812-120.332-141.598-120.332-71.814-63.559-15.04 6.225-15.04 76.009-15.04 132.782-71.814 132.782-141.598 132.782-171.988 121.849-204.068 101.995-227.025 82.343-249.749 55.26-266.128 25.735-270.695 23.784-270.998 22.441-272.817 22.727-274.771 22.967-276.412 23.19-278.057 23.387-279.702 23.466-280.371 23.536-281.042 23.606-281.714L23.694-282.552C23.798-283.527 24.297-284.419 25.076-285.017 25.708-285.506 26.48-285.766 27.269-285.766 27.45-285.766 27.632-285.752 27.813-285.723 60.943-280.659 91.336-262.498 113.396-237.037 135.683-211.312 147.958-175.678 147.958-141.598 147.958-61.382 80.972 3.484 0 0"
        fill={ring}
      />
      {/* Punto quadrato azzurro (sopra la i) — path originale PDF */}
      <path
        transform="matrix(0.352777,0,0,-0.349773,0,99.953)"
        d="M174.484 235.289H189.027V253.103H174.484Z"
        fill={dot}
      />
      {/* Y — path originale PDF */}
      <path
        transform="matrix(0.352777,0,0,-0.349773,152.1505,76.98)"
        d="M0 0 36.876 61.941 19.959 61.955-8.462 14.215-36.884 61.955-53.801 61.941-16.925 0-8.471-14.2Z"
        fill={body}
      />
      {/* L e i — path originale PDF */}
      <path
        transform="matrix(0.352777,0,0,-0.349773,174.4836,23.07)"
        d="M0 0V-72.465L-61.409-72.464V33.154H-76.134V-87.007-87.008H0 14.543V0Z"
        fill={body}
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// SQL DA ESEGUIRE UNA VOLTA IN SUPABASE (poi puoi togliere)
// ─────────────────────────────────────────────────────────────
// alter table requests add column if not exists is_emergency boolean default false;
// alter table requests add column if not exists forced_by_admin boolean default false;
// alter table requests add column if not exists cancel_requested boolean default false;
// alter table requests add column if not exists cancel_note text;
// alter table profiles  add column if not exists sw_permission boolean default null;
// alter table profiles  add column if not exists extra_days int default 0;
// alter table profiles  add column if not exists extra_note text;
// alter table profiles  add column if not exists email text;
// alter table office_desks add column if not exists room text default 'Ufficio';
// create table if not exists user_sw_settings (
//   id uuid default gen_random_uuid() primary key,
//   user_id uuid references profiles(id) on delete cascade unique,
//   days_per_month int, days_per_year int, emergency_days int, note text
// );
// alter table user_sw_settings enable row level security;
// create policy "all_settings" on user_sw_settings for all using (true);
// create table if not exists swap_requests (
//   id uuid default gen_random_uuid() primary key,
//   requester_id uuid references profiles(id) on delete cascade,
//   target_id uuid references profiles(id) on delete cascade,
//   requester_date text, target_date text,
//   note text, status text default 'pending',
//   created_at timestamptz default now()
// );
// alter table swap_requests enable row level security;
// create policy "all_swap" on swap_requests for all using (true);
// create table if not exists messages (
//   id uuid default gen_random_uuid() primary key,
//   user_id uuid references profiles(id) on delete cascade,
//   content text not null,
//   is_private boolean default false,
//   admin_reply text,
//   created_at timestamptz default now()
// );
// alter table messages enable row level security;
// create policy "all_messages" on messages for all using (true);
// -- FIX RLS per requests (permetti delete al proprio owner)
// drop policy if exists "Users can delete own requests" on requests;
// create policy "Users can delete own requests" on requests
//   for delete using (user_id = auth.uid() or
//     exists(select 1 from profiles where id = auth.uid() and role='admin'));

// ─────────────────────────────────────────────────────────────
// LOGIN
// ─────────────────────────────────────────────────────────────
function ForcePwChange({ supabase, user, onDone }) {
  const [newPw,  setNewPw]  = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [err,    setErr]    = useState("");
  const [busy,   setBusy]   = useState(false);

  const save = async () => {
    if (!newPw) return;
    if (newPw !== newPw2) { setErr("Le password non coincidono"); return; }
    if (newPw.length < 8)  { setErr("Minimo 8 caratteri"); return; }
    setBusy(true);
    const hash = await hashPw(newPw);
    const { error } = await supabase.from("profiles").update({ password: hash, must_change_password: false }).eq("id", user.id);
    setBusy(false);
    if (error) { setErr("Errore: " + error.message); return; }
    onDone({ ...user, must_change_password: false });
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="Nuova password (min. 8 caratteri)" style={{background:"#FAFAFA",border:"1px solid #E2E2E2",borderRadius:9,padding:"11px 13px",fontSize:14,fontFamily:"'DM Sans',sans-serif",outline:"none",width:"100%"}}/>
      <input type="password" value={newPw2} onChange={e=>setNewPw2(e.target.value)} onKeyDown={e=>e.key==="Enter"&&save()} placeholder="Ripeti password" style={{background:"#FAFAFA",border:"1px solid #E2E2E2",borderRadius:9,padding:"11px 13px",fontSize:14,fontFamily:"'DM Sans',sans-serif",outline:"none",width:"100%"}}/>
      {err&&<div style={{background:"#FFF0F0",border:"1px solid #F5C0C0",borderRadius:9,padding:"9px 12px",color:"#C0392B",fontSize:12}}>{err}</div>}
      <button onClick={save} disabled={busy} style={{background:"#1A1A1A",color:"white",border:"none",borderRadius:11,padding:13,fontSize:14,fontWeight:700,cursor:busy?"wait":"pointer",fontFamily:"'DM Sans',sans-serif",opacity:busy?.7:1}}>
        {busy?"Salvataggio...":"Imposta password →"}
      </button>
    </div>
  );
}

function hashPw(pw) {
  return crypto.subtle.digest("SHA-256", new TextEncoder().encode(pw))
    .then(buf => Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,"0")).join(""));
}

function ProfilePwSection({ supabase, cu, showToast }) {
  const [newPw,  setNewPw]  = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [busy,   setBusy]   = useState(false);
  const [show,   setShow]   = useState(false);

  const save = async () => {
    if (!newPw) return;
    if (newPw !== newPw2) { showToast("Le password non coincidono", false); return; }
    if (newPw.length < 8)  { showToast("Minimo 8 caratteri", false); return; }
    setBusy(true);
    // Prova prima Auth Supabase, poi fallback DB
    const { error: authErr } = await supabase.auth.updateUser({ password: newPw });
    if (authErr) {
      // Utente senza Auth — salva hash nel DB
      const hash = await hashPw(newPw);
      const { error: dbErr } = await supabase.from("profiles").update({ password: hash, must_change_password: false }).eq("id", cu.id);
      if (dbErr) { showToast("Errore: " + dbErr.message, false); setBusy(false); return; }
    } else {
      // Aggiorna anche DB per coerenza
      await supabase.from("profiles").update({ must_change_password: false }).eq("id", cu.id);
    }
    setBusy(false);
    showToast("Password aggiornata ✓");
    setNewPw(""); setNewPw2(""); setShow(false);
  };

  if (!show) return (
    <button onClick={()=>setShow(true)} style={{background:"#F5F5F5",border:"1px solid #E2E2E2",borderRadius:9,padding:"8px 14px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",color:"#444",width:"100%",textAlign:"left"}}>
      Reimposta password →
    </button>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="Nuova password (min. 8 caratteri)" style={{background:"#FAFAFA",border:"1px solid #E2E2E2",borderRadius:9,padding:"9px 12px",fontSize:13,fontFamily:"'DM Sans',sans-serif",outline:"none",width:"100%"}}/>
      <input type="password" value={newPw2} onChange={e=>setNewPw2(e.target.value)} placeholder="Ripeti nuova password" style={{background:"#FAFAFA",border:"1px solid #E2E2E2",borderRadius:9,padding:"9px 12px",fontSize:13,fontFamily:"'DM Sans',sans-serif",outline:"none",width:"100%"}}/>
      <div style={{display:"flex",gap:8}}>
        <button onClick={save} disabled={busy} style={{flex:2,background:"#1A1A1A",color:"white",border:"none",borderRadius:9,padding:"9px 0",fontSize:12,fontWeight:700,cursor:busy?"wait":"pointer",fontFamily:"'DM Sans',sans-serif",opacity:busy?.7:1}}>
          {busy?"Salvataggio...":"Aggiorna password"}
        </button>
        <button onClick={()=>{setShow(false);setNewPw("");setNewPw2("");}} style={{flex:1,background:"#F5F5F5",border:"1px solid #E2E2E2",borderRadius:9,padding:"9px 0",fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",color:"#666"}}>
          Annulla
        </button>
      </div>
    </div>
  );
}

function DbResetScreen({ dbResetToken, onDone, BG, WHT, BDR, SUB, BLK }) {
  const [newPw,  setNewPw]  = React.useState("");
  const [newPw2, setNewPw2] = React.useState("");
  const [busy,   setBusy]   = React.useState(false);
  const [err,    setErr]    = React.useState("");
  const [ok,     setOk]     = React.useState(false);

  const save = async () => {
    if (!newPw) return;
    if (newPw !== newPw2) { setErr("Le password non coincidono"); return; }
    if (newPw.length < 8)  { setErr("Minimo 8 caratteri"); return; }
    setBusy(true); setErr("");

    if (dbResetToken) {
      // Utente DB: valida token e aggiorna password
      const { data: prof } = await supabase.from("profiles")
        .select("id, reset_token_exp")
        .eq("reset_token", dbResetToken)
        .maybeSingle();
      if (!prof) { setErr("Link non valido o scaduto."); setBusy(false); return; }
      if (new Date(prof.reset_token_exp) < new Date()) { setErr("Link scaduto. Richiedine uno nuovo."); setBusy(false); return; }
      const hash = await hashPw(newPw);
      await supabase.from("profiles").update({
        password: hash, must_change_password: false,
        reset_token: null, reset_token_exp: null
      }).eq("id", prof.id);
    } else {
      // Utente Supabase Auth: usa updateUser
      const { error } = await supabase.auth.updateUser({ password: newPw });
      if (error) { setErr("Errore: " + error.message); setBusy(false); return; }
    }

    setBusy(false);
    setOk(true);
    setTimeout(() => onDone(), 2000);
  };

  return (
    <div style={{minHeight:"100vh",background:BG||"#F5F5F5",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',Arial,sans-serif",padding:20}}>
      <div style={{width:"100%",maxWidth:400}}>
        <div style={{background:WHT||"white",border:"1px solid "+(BDR||"#E2E2E2"),borderRadius:18,padding:32,boxShadow:"0 4px 20px rgba(0,0,0,.06)"}}>
          <div style={{fontSize:28,marginBottom:10}}>🔐</div>
          <h2 style={{fontSize:19,fontWeight:700,marginBottom:5,color:BLK||"#1A1A1A"}}>Reimposta la password</h2>
          <p style={{color:SUB||"#888",fontSize:13,marginBottom:22,lineHeight:1.6}}>Scegli una nuova password per il tuo account.</p>
          {ok
            ? <div style={{background:"#F0FFF4",border:"1px solid #A5D6A7",borderRadius:9,padding:"12px 14px",color:"#1A7A3A",fontSize:13,fontWeight:600}}>✅ Password aggiornata! Reindirizzamento...</div>
            : <>
              {err && <div style={{background:"#FFF0F0",border:"1px solid #F5C0C0",borderRadius:9,padding:"10px 13px",color:"#C0392B",fontSize:12,marginBottom:12}}>{err}</div>}
              <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="Nuova password (min. 8 caratteri)" style={{width:"100%",background:"#FAFAFA",border:"1px solid "+(BDR||"#E2E2E2"),borderRadius:9,padding:"11px 13px",fontSize:14,fontFamily:"inherit",outline:"none",marginBottom:10}}/>
              <input type="password" value={newPw2} onChange={e=>setNewPw2(e.target.value)} onKeyDown={e=>e.key==="Enter"&&save()} placeholder="Ripeti nuova password" style={{width:"100%",background:"#FAFAFA",border:"1px solid "+(BDR||"#E2E2E2"),borderRadius:9,padding:"11px 13px",fontSize:14,fontFamily:"inherit",outline:"none",marginBottom:16}}/>
              <button onClick={save} disabled={busy} style={{width:"100%",background:BLK||"#1A1A1A",color:"white",border:"none",borderRadius:11,padding:13,fontSize:14,fontWeight:700,cursor:busy?"wait":"pointer",opacity:busy?.7:1,fontFamily:"inherit"}}>
                {busy?"Salvataggio...":"Imposta password →"}
              </button>
            </>
          }
        </div>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [email,    setEmail]    = useState("");
  const [pw,       setPw]       = useState("");
  const [err,      setErr]      = useState("");
  const [busy,     setBusy]     = useState(false);
  const [loginLogo,   setLoginLogo]   = useState(()=>{ try{return safeStore.get("ly_logo")||null;}catch{return null;} });
  const [loginLogoReady, setLoginLogoReady] = useState(()=>{ try{return !!safeStore.get("ly_logo");}catch{return false;} });
  const [resetSent,setResetSent]= useState(false);

  // Carica il logo dal DB pubblicamente (prima del login)
  useEffect(() => {
    supabase.from("app_settings").select("value").eq("key","logo_url").maybeSingle()
      .then(({data}) => {
        if(data?.value){ setLoginLogo(data.value); try{safeStore.set("ly_logo",data.value);}catch{} }
        else { try{localStorage.removeItem("ly_logo");}catch{} }
        setLoginLogoReady(true);
      });
  }, []);

  const go = async () => {
    if (!email || !pw) { setErr("Inserisci email e password"); return; }
    setBusy(true); setErr("");

    // Tentativo 1: Supabase Auth (utenti originali)
    const { data: authData, error: authErr } = await supabase.auth.signInWithPassword({ email, password: pw });
    if (!authErr && authData?.user) {
      const { data: prof } = await supabase.from("profiles").select("*").eq("id", authData.user.id).single();
      setBusy(false);
      if (prof) onLogin({ ...prof, email: authData.user.email, _authMode: "supabase" });
      else setErr("Profilo non trovato. Contatta l'amministratore.");
      return;
    }

    // Tentativo 2: Login DB custom (utenti aggiunti dall'app)
    const hash = await hashPw(pw);
    // Usiamo RPC per bypassare la RLS (utente non ancora autenticato)
    const { data: rpcData, error: rpcErr } = await supabase.rpc("login_with_password", {
      p_email: email.toLowerCase().trim(),
      p_hash: hash
    });
    setBusy(false);
    if (rpcErr || !rpcData) { setErr("Credenziali non corrette. Riprova o contatta l'amministratore."); return; }
    // la RPC può tornare stringa JSON o oggetto, gestiamo entrambi
    const prof = typeof rpcData === "string" ? JSON.parse(rpcData) : rpcData;
    if (!prof) { setErr("Credenziali non corrette. Riprova o contatta l'amministratore."); return; }
    onLogin({ ...prof, _authMode: "db" });
  };

  return (
    <div style={{ minHeight:"100vh", background:BG, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif", padding:20 }}>
      <style>{"@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap');*{box-sizing:border-box;margin:0;padding:0;}"}</style>
      <div style={{ width:"100%", maxWidth:400 }}>
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <div style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", background:BLK, borderRadius:18, width:68, height:68, marginBottom:14, overflow:"hidden" }}>
            {loginLogoReady && loginLogo
              ? <img src={loginLogo} alt="logo" style={{width:68,height:68,objectFit:"cover",borderRadius:18}}/>
              : <LYLogo size={48} light/>}
          </div>
          <div style={{ fontFamily:"'Space Mono',monospace", fontSize:15, fontWeight:700, color:BLK, letterSpacing:3 }}>LIKEYOU SW</div>
          <div style={{ color:SUB, fontSize:12, marginTop:4 }}>Portale Smart Working</div>
        </div>
        <div style={{ background:WHT, border:"1px solid "+BDR, borderRadius:18, padding:32, boxShadow:"0 4px 20px rgba(0,0,0,.06)" }}>
          <h2 style={{ fontSize:19, fontWeight:700, marginBottom:5 }}>Accedi</h2>
          <p style={{ color:SUB, fontSize:13, marginBottom:22 }}>Inserisci le tue credenziali aziendali</p>
          <label style={{ fontSize:10, color:SUB, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:5 }}>Email</label>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&go()} placeholder="nome@likeyousrl.com" autoComplete="username" style={{ background:"#FAFAFA", border:"1px solid "+BDR, borderRadius:9, padding:"11px 13px", fontFamily:"inherit", fontSize:14, outline:"none", width:"100%", marginBottom:13 }}/>
          <label style={{ fontSize:10, color:SUB, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:5 }}>Password</label>
          <input type="password" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&go()} placeholder="••••••••" autoComplete="current-password" style={{ background:"#FAFAFA", border:"1px solid "+BDR, borderRadius:9, padding:"11px 13px", fontFamily:"inherit", fontSize:14, outline:"none", width:"100%", marginBottom:20 }}/>
          {err && <div style={{ background:"#FFF0F0", border:"1px solid #F5C0C0", borderRadius:9, padding:"10px 13px", color:"#C0392B", fontSize:12, marginBottom:14 }}>{err}</div>}
          {resetSent && <div style={{ background:"#F0FFF4", border:"1px solid #A5D6A7", borderRadius:9, padding:"10px 13px", color:"#1A7A3A", fontSize:12, marginBottom:14 }}>📧 Email inviata! Controlla la tua casella e segui il link per reimpostare la password.</div>}
          <button onClick={go} disabled={busy} style={{ width:"100%", background:BLK, color:WHT, border:"none", borderRadius:11, padding:13, fontFamily:"inherit", fontSize:14, fontWeight:700, cursor:busy?"wait":"pointer", opacity:busy?.7:1 }}>
            {busy ? "Accesso in corso..." : "Accedi →"}
          </button>
          <button onClick={async()=>{
            if(!email){setErr("Inserisci prima l'email");return;}
            setBusy(true);
            try {
              await fetch(`${SUPABASE_URL}/functions/v1/send-reset-email`,{
                method:"POST",
                headers:{"Content-Type":"application/json","Authorization":`Bearer ${SUPABASE_ANON_KEY}`},
                body:JSON.stringify({email:email.toLowerCase().trim()})
              });
            } catch(e){}
            setBusy(false);
            setResetSent(true); setErr("");
          }} style={{ width:"100%", background:"none", border:"none", color:SUB, fontSize:12, marginTop:12, cursor:"pointer", fontFamily:"inherit", textDecoration:"underline" }}>
            Password dimenticata?
          </button>
        </div>
        <p style={{ textAlign:"center", marginTop:14, color:"#BBB", fontSize:11 }}>Per problemi contatta l'amministratore</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// APP PRINCIPALE
// ─────────────────────────────────────────────────────────────
export default function App() {

  // AUTH & SIMULAZIONE
  const [realUser, setRealUser]     = useState(()=>{ try{ const s=safeStore.get("sw_user"); return s?JSON.parse(s):null; }catch{return null;} });
  const [viewAs,   setViewAs]       = useState(null);
  const cu = viewAs || realUser;
  const simulating = viewAs !== null;
  const isAdmin = realUser?.role === "admin" && !simulating;

  // DATI
  const [team,      setTeam]      = useState([]);
  const [reqs,      setReqs]      = useState([]);
  const [blocked,   setBlocked]   = useState([]);
  const [desks,     setDesks]     = useState([]);
  const [uSet,      setUSet]      = useState([]);
  const [swaps,     setSwaps]     = useState([]);
  const [messages,  setMessages]  = useState([]);
  const [excPeriods, setExcPeriods] = useState([]);
  const [rules,     setRules]     = useState({ days_per_month:4, days_per_year:48, max_per_day:3, advance_days:60, carry_over:true });
  const [debugLogs, setDebugLogs] = useState([]);
  const addLog = (category, status, message, detail="") => {
    const ts = new Date().toLocaleTimeString("it-IT");
    setDebugLogs(prev => [{ ts, category, status, message, detail, id: Date.now()+Math.random() }, ...prev].slice(0, 100));
  };
  const [editRules, setEditRules] = useState(null);

  // NAVIGAZIONE
  const [tab,     setTab]     = useState("dashboard");
  const [tutStep, setTutStep] = useState(() => {
    try { return safeStore.get("sw_tutorial_done") ? -1 : 0; } catch { return -1; }
  });
  const [aTab,    setATab]    = useState("requests");
  const [reqSearch, setReqSearch] = useState("");
  const [mobileOpen, setMO]   = useState(false);
  const [darkMode,   setDarkMode] = useState(()=>{ try{return safeStore.get("sw_dark")==="1";}catch{return false;} });
  const toggleDark = () => setDarkMode(d=>{ const n=!d; try{safeStore.set("sw_dark",n?"1":"0");}catch{} return n; });

  // Colori reattivi al tema
  const BLK = darkMode ? "#F0F0F0" : "#1A1A1A";
  const WHT = darkMode ? "#1E1E1E" : "#FFFFFF";
  const BG  = darkMode ? "#111111" : "#F4F4F4";
  const BDR = darkMode ? "#2E2E2E" : "#E2E2E2";
  const SUB = darkMode ? "#999"    : "#888";
  const LYB = "#7A9BAD";

  // FORM — uso useRef per la textarea del commento (fix bug focus)
  const [tab_selM, setSelM]   = useState(today.getMonth());
  const [tab_selY, setSelY]   = useState(today.getFullYear());
  const [nrDate,   setNrDate] = useState("");
  const [nrDateTo, setNrDateTo] = useState("");
  const [nrNote,   setNrNote] = useState("");
  const [isEm,     setIsEm]   = useState(false);
  const [wkOff,    setWkOff]  = useState(0);
  const [wkFilter, setWkFilter] = useState("all"); // all | sw | office | fr
  const [deskWkOff,setDeskWkOff]=useState(0);
  const [deskDayModal,setDeskDayModal]=useState(null); // {ds, label}
  const [editBooking,setEditBooking]=useState(null); // {id, user_id, half_day, name}
  const [deskTimeSlot,setDeskTimeSlot]=useState("morning"); // morning | afternoon
  const [mvM,      setMvM]    = useState(today.getMonth());
  const [mvY,      setMvY]    = useState(today.getFullYear());

  // MODAL STATE (ref per textarea → evita re-render bug)
  const [cancelTarget, setCancelTarget] = useState(null);
  const cnNoteRef = useRef("");
  const [cnNoteVal, setCnNoteVal] = useState(""); // solo per display

  const [editU,    setEditU]   = useState(null);
  const [usTgt,    setUsTgt]   = useState(null);
  const [vacTgt,   setVacTgt]  = useState(null); // user id per ferie admin
  const [vacFrom,  setVacFrom] = useState("");
  const [vacTo,    setVacTo]   = useState("");
  const [usDm,     setUsDm]    = useState(4);
  const [usDy,     setUsDy]    = useState(48);
  const [usEm,     setUsEm]    = useState(4);
  const [usNote,   setUsNote]  = useState("");
  const [exUid,    setExUid]   = useState("");
  const [exDays,   setExDays]  = useState(0);
  const [exNote,   setExNote]  = useState("");

  // ADMIN FORMS
  const [blDate,   setBlDate]  = useState("");
  const [blDateTo, setBlDateTo]= useState("");
  const [blReas,   setBlReas]  = useState("");
  const [fUser,    setFUser]   = useState("");
  const [fDate,    setFDate]   = useState("");
  const [fNote,    setFNote]   = useState("");
  // SW RICORRENTE
  const [recUser,  setRecUser]  = useState("");
  const [recDay,   setRecDay]   = useState("5");
  const [recFrom,  setRecFrom]  = useState("");
  const [recTo,    setRecTo]    = useState("");
  const [recNote,  setRecNote]  = useState("");
  const [ndName,     setNdName]     = useState("");
  const [ndRoom,     setNdRoom]     = useState("Ufficio");
  const [ndAssigned, setNdAssigned] = useState("");
  const [ndExpires,  setNdExpires]  = useState("");
  const [nuName,   setNuName]  = useState("");
  const [nuEmail,  setNuEmail] = useState("");
  const [nuRole,   setNuRole]  = useState("employee");

  // SCAMBIO
  const [swapModal,   setSwapModal]   = useState(false);
  const [swapMyDate,  setSwapMyDate]  = useState("");
  const [swapTgtUser, setSwapTgtUser] = useState("");
  const [swapTgtDate, setSwapTgtDate] = useState("");
  const swapNoteRef = useRef("");
  const [swapNoteVal, setSwapNoteVal] = useState("");

  // PROFILO UTENTE
  const [profileModal,   setProfileModal]   = useState(false);
  const [profileBday,    setProfileBday]    = useState("");
  const [profileVacation,setProfileVacation]= useState(null);
  const [profileSwLoc,   setProfileSwLoc]   = useState("");
  const [logoUrl,        setLogoUrl]        = useState(()=>{ try{return safeStore.get("ly_logo")||null;}catch{return null;} });
  const [logoReady,      setLogoReady]      = useState(()=>{ try{return !!safeStore.get("ly_logo");}catch{return false;} });

  // MEZZA GIORNATA e GCAL
  const [nrHalf,    setNrHalf]    = useState("full");   // "full"|"morning"|"afternoon"
  const [gcalModal, setGcalModal] = useState(false);

  // MESSAGGI
  const msgRef = useRef("");
  const [msgVal, setMsgVal] = useState("");
  const [guideOpen,    setGuideOpen]    = useState(false);
  const [msgTab,       setMsgTab]       = useState("msg"); // msg | assistenza | storico
  const [storicoUser,  setStoricoUser]  = useState("");
  const [suppCat,      setSuppCat]      = useState("domanda");
  const [suppText,     setSuppText]     = useState("");
  const [suppBusy,     setSuppBusy]     = useState(false);

  // NOTIFICHE
  const [notifSettings, setNotifSettings] = useState({
    enabled: true,
    new_sw_request:    true,   // admin riceve richiesta nuova
    sw_approved:       true,   // utente riceve approvazione
    sw_rejected:       true,   // utente riceve rifiuto
    cancel_approved:   true,   // utente riceve conferma cancellazione
    cancel_rejected:   true,   // utente riceve rifiuto cancellazione
    swap_request:      true,   // utente riceve proposta scambio
    birthday:          true,   // email auguri compleanno
  });

  // TOAST
  const [toast, setToast] = useState(null);
  const [inAppNotifs, setInAppNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const showToast = useCallback((msg, ok=true) => {
    setToast({msg,ok}); setTimeout(() => setToast(null), 3600);
  }, []);

  // ── LOAD ────────────────────────────────────────────────────
  const load = useCallback(async () => {
    const [a,b,c,d,e,f,g,h,logo,notifRaw,excP] = await Promise.all([
      supabase.from("profiles").select("*"),
      supabase.from("requests").select("*").order("created_at",{ascending:false}),
      supabase.from("blocked_days").select("*").order("date"),
      supabase.from("office_desks").select("*").order("room").order("name"),
      supabase.from("sw_rules").select("*").eq("id",1).maybeSingle(),
      supabase.from("user_sw_settings").select("*"),
      supabase.from("swap_requests").select("*").order("created_at",{ascending:false}),
      supabase.from("messages").select("*").order("created_at",{ascending:false}),
      supabase.from("app_settings").select("*").eq("key","logo_url").maybeSingle(),
      supabase.from("app_settings").select("*").eq("key","notif_settings").maybeSingle(),
      supabase.from("user_exception_periods").select("*"),
    ]);
    if (a.data) setTeam(a.data);
    if (b.data) setReqs(b.data);
    if (c.data) setBlocked(c.data);
    if (d.data) setDesks(d.data);
    if (e.data) { setRules(e.data); setEditRules({...e.data}); }
    if (f.data) setUSet(f.data);
    if (g.data) setSwaps(g.data);
    if (h.data) setMessages(h.data);
    if (logo?.data?.value){ setLogoUrl(logo.data.value); try{safeStore.set("ly_logo",logo.data.value);}catch{} }
    else { try{localStorage.removeItem("ly_logo");}catch{} }
    setLogoReady(true);
    if (notifRaw?.data?.value) { try { setNotifSettings(JSON.parse(notifRaw.data.value)); } catch(ex){} }
    if (excP.data) setExcPeriods(excP.data);
  }, []);

  useEffect(() => { if (realUser) load(); }, [realUser, load]);

  // Auto-disattiva vacation quando vacation_to è passato
  useEffect(() => {
    if (!team.length) return;
    const today = new Date().toISOString().substring(0, 10);
    const expired = team.filter(u =>
      u.vacation && u.vacation_to && u.vacation_to.substring(0, 10) < today
    );
    if (!expired.length) return;
    expired.forEach(async (u) => {
      await supabase.from("profiles").update({ vacation: false, vacation_from: null, vacation_to: null }).eq("id", u.id);
    });
    setTeam(prev => prev.map(u =>
      expired.find(e => e.id === u.id) ? { ...u, vacation: false, vacation_from: null, vacation_to: null } : u
    ));
  }, [team.length]);

  // Chiudi pannello notifiche cliccando fuori
  const notifPopupRef = useRef(null);
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (notifPopupRef.current && !notifPopupRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    setTimeout(() => document.addEventListener("click", handler), 100);
    return () => document.removeEventListener("click", handler);
  }, [notifOpen]);

  // ── IN-APP NOTIFICATIONS ─────────────────────────────────────
  const loadNotifs = useCallback(async (uid) => {
    const { data } = await supabase.from("user_notifications").select("*").eq("user_id", uid).order("created_at", {ascending:false}).limit(30);
    if (data) setInAppNotifs(data);
  }, []);

  useEffect(() => { if (realUser) loadNotifs(realUser.id); }, [realUser, loadNotifs]);

  // Realtime: ascolta nuove notifiche in tempo reale
  useEffect(() => {
    if (!realUser) return;
    const channel = supabase
      .channel("user_notifications_"+realUser.id)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "user_notifications",
        filter: `user_id=eq.${realUser.id}`
      }, (payload) => {
        setInAppNotifs(ns => [payload.new, ...ns].slice(0, 30));
      })
      .on("postgres_changes", {
        event: "DELETE",
        schema: "public",
        table: "user_notifications",
        filter: `user_id=eq.${realUser.id}`
      }, (payload) => {
        setInAppNotifs(ns => ns.filter(n => n.id !== payload.old.id));
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [realUser]);

  const addInAppNotif = async (userId, type, title, body) => {
    await supabase.from("user_notifications").insert({ user_id: userId, type, title, body });
    // Ricarica notifiche per l'utente corrente
    if (realUser?.id === userId) loadNotifs(userId);
  };

  const markNotifsRead = async () => {
    const unread = inAppNotifs.filter(n => !n.read).map(n => n.id);
    if (!unread.length) return;
    await supabase.from("user_notifications").update({ read: true }).in("id", unread);
    setInAppNotifs(ns => ns.map(n => ({ ...n, read: true })));
  };

  const deleteNotif = async (id) => {
    await supabase.from("user_notifications").delete().eq("id", id);
    setInAppNotifs(ns => ns.filter(n => n.id !== id));
  };

  // ── PUSH: sottoscrivi al login se il browser lo supporta ──
  useEffect(() => {
    if (!realUser) return;
    if (typeof Notification === "undefined") return; // iOS Safari non supporta Notification
    if (Notification.permission === "denied") return;
    if (Notification.permission === "default") {
      Notification.requestPermission().then(p => {
        if (p === "granted") subscribeToPush(supabase, realUser.id);
      });
    } else {
      subscribeToPush(supabase, realUser.id);
    }
  }, [realUser?.id]);

  // ── BIRTHDAY CHECK — scatta una volta al login se admin ────
  useEffect(() => {
    if (!realUser || !isAdmin) return;
    if (!notifSettings.enabled || notifSettings.birthday === false) return;
    const todayMD = todayStr.slice(5); // "MM-DD"
    const bdayPeople = team.filter(u => u.birthday && u.birthday.slice(5) === todayMD);
    if (bdayPeople.length === 0) return;
    // Controlla se già inviato oggi (localStorage per evitare duplicati)
    const sentKey = `sw_bday_sent_${todayStr}`;
    try { if (safeStore.get(sentKey)) return; } catch(e) {}
    bdayPeople.forEach(u => {
      // Notifica all'interessato
      sendNotification("birthday_self", u.id, { date: todayStr });
      // Notifica a tutti gli altri
      team.filter(t => t.id !== u.id && t.role !== "collaborator").forEach(t => {
        sendNotification("birthday", t.id, { date: todayStr, nome: u.name.split(" ")[0] });
      });
    });
    try { safeStore.set(sentKey, "1"); } catch(e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, realUser]);

  // ── REALTIME ─────────────────────────────────────────────────
  useEffect(() => {
    if (!realUser) return;
    const channel = supabase.channel("sw-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "requests" }, (payload) => {
        setReqs(prev => {
          if (payload.eventType === "INSERT") return [payload.new, ...prev];
          if (payload.eventType === "DELETE") return prev.filter(r => r.id !== payload.old.id);
          if (payload.eventType === "UPDATE") return prev.map(r => r.id === payload.new.id ? payload.new : r);
          return prev;
        });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "profiles" }, () => {
        supabase.from("profiles").select("*").then(({ data }) => { if (data) setTeam(data); });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, () => {
        supabase.from("messages").select("*").order("created_at", { ascending: false }).then(({ data }) => { if (data) setMessages(data); });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [realUser]);

  // ── HELPERS CALCOLO ──────────────────────────────────────────
  const getUR = uid => {
    const c = uSet.find(s => s.user_id === uid);
    return {
      days_per_month: c?.days_per_month ?? rules.days_per_month ?? 4,
      days_per_year:  c?.days_per_year  ?? rules.days_per_year  ?? 48,
      emergency_days: c?.emergency_days ?? 4,
    };
  };

  // giorni usati nell'anno (ordinari, NO eccezioni)
  const yUsed  = (uid,y)   => reqs.filter(r=>r.user_id===uid&&r.status!=="rejected"&&!r.is_exception&&r.smart_date&&new Date(r.smart_date).getFullYear()===y).length;
  // solo ordinari nel mese
  const mUsed  = (uid,y,m) => reqs.filter(r=>r.user_id===uid&&r.status!=="rejected"&&!r.is_emergency&&!r.is_exception&&r.smart_date&&new Date(r.smart_date).getFullYear()===y&&new Date(r.smart_date).getMonth()===m).length;
  // solo emergenza nell'anno
  const emUsed = (uid,y)   => reqs.filter(r=>r.user_id===uid&&r.status!=="rejected"&&r.is_emergency&&r.smart_date&&new Date(r.smart_date).getFullYear()===y).length;

  // ── LOGICA GIORNI SW ─────────────────────────────────────────
  // Budget: 48 giorni/anno, max 4 usabili per mese.
  // I giorni NON usati in un mese NON si perdono (carry_over),
  // ma rimangono disponibili per i mesi successivi entro il tetto annuo.
  // L'utente vede sempre "X/48 rimasti" come budget totale dell'anno.
  //
  // avail(uid, upTo) = giorni usabili oggi considerando:
  //   - tetto annuo: 48
  //   - tetto mensile: max 4/mese (ma i non usati si accumulano)
  //   - usati nell'anno fino a upTo
  const avail = (uid, upTo) => {
    const up = new Date(upTo+"T12:00:00");
    const y  = up.getFullYear();
    const uR = getUR(uid);
    const extra = team.find(t => t.id === uid)?.extra_days || 0;
    // usati nell'anno intero, NO eccezioni (conteggio mezze giornate come 0.5)
    const used = reqs.filter(r =>
      r.user_id === uid && r.status !== "rejected" && r.type !== "desk" &&
      !r.is_exception &&
      r.smart_date && new Date(r.smart_date+"T12:00:00").getFullYear() === y
    ).reduce((acc, r) => acc + (r.half_day===true ? 0.5 : 1), 0);
    return uR.days_per_year - used + extra;
  };

  // giorni usati nel mese (per il controllo del limite mensile)
  // le mezze giornate contano 0.5
  const mUsedFull = (uid,y,m) => reqs.filter(r =>
    r.user_id===uid && r.status!=="rejected" && !r.is_emergency && !r.is_exception && r.type!=="desk" &&
    r.smart_date && new Date(r.smart_date+"T12:00:00").getFullYear()===y &&
    new Date(r.smart_date+"T12:00:00").getMonth()===m
  ).reduce((acc,r) => acc + (r.half_day===true ? 0.5 : 1), 0);

  // Settimana ISO di una data (lunedì = inizio)
  const isoWeek = ds => {
    const d = new Date(ds+"T12:00:00");
    const thu = new Date(d); thu.setDate(d.getDate() - ((d.getDay()+6)%7) + 3);
    const firstThu = new Date(thu.getFullYear(),0,4);
    return 1 + Math.round(((thu-firstThu)/86400000 - 3 + ((firstThu.getDay()+6)%7)) / 7);
  };

  // Controlla se l'utente ha già uno SW nella settimana della data ds
  const hasSwInWeek = (uid, ds) => {
    const week = isoWeek(ds);
    const year = new Date(ds+"T12:00:00").getFullYear();
    return reqs.some(r =>
      r.user_id === uid && r.status !== "rejected" &&
      r.smart_date && r.smart_date !== ds &&
      new Date(r.smart_date+"T12:00:00").getFullYear() === year &&
      isoWeek(r.smart_date) === week
    );
  };

  const scnt = ds => reqs.filter(r=>r.smart_date===ds&&r.status!=="rejected"&&!r.is_exception).length;
  const isBl = ds => blocked.some(b=>b.date===ds);

  // Controlla se una data è in un periodo speciale (principale o extra)
  const getConsecPeriod = (ds) => {
    if (!rules.consec_enabled) return null;
    const cf = (rules.consec_from||"").substring(0,10);
    const ct = (rules.consec_to||"").substring(0,10);
    if (cf && ct && ds >= cf && ds <= ct) return {from:cf, to:ct, max:rules.consec_max||5};
    const extras = rules.extra_periods || [];
    for (const ep of extras) {
      const ef = (ep.from||"").substring(0,10);
      const et = (ep.to||"").substring(0,10);
      if (ef && et && ds >= ef && ds <= et) return {from:ef, to:et, max:ep.max||5};
    }
    return null;
  };
  const dim  = (y,m) => new Date(y,m+1,0).getDate();
  const fd   = (y,m) => new Date(y,m,1).getDay();

  // ── ACTIONS ─────────────────────────────────────────────────
  // Invia notifica email — rispetta le impostazioni admin
  const sendNotification = async (type, targetUserId, data={}) => {
    if (!notifSettings.enabled) { addLog("📧 Email","⚠️",`Notifica ${type} bloccata`,"Le notifiche sono disabilitate"); return; }
    if (notifSettings[type] === false) { addLog("📧 Email","⚠️",`Notifica ${type} bloccata`,"Tipo disabilitato nelle impostazioni"); return; }
    try {
      const target = team.find(t=>t.id===targetUserId);
      if (!target?.email) { addLog("📧 Email","❌","Nessuna email per utente",`ID: ${targetUserId}`); console.warn("[SW Notif] utente senza email:", targetUserId); return; }
      const savedTexts = (() => { try { const s=localStorage.getItem("sw_notif_texts"); return s?JSON.parse(s):{}; } catch(e){return{};} })();
      const savedBrand = (() => { try { const s=localStorage.getItem("sw_notif_brand"); return s?JSON.parse(s):{}; } catch(e){return{};} })();
      const tpl = { ...DEFAULT_NOTIF_TEXTS[type], ...(savedTexts[type]||{}) };
      const replace = (s="") => s
        .replace(/{{nome}}/g, target.name)
        .replace(/{{data}}/g, data.date||"")
        .replace(/{{sede}}/g, data.note||data.sede||"")
        .replace(/{{richiedente}}/g, data.requester||data.richiedente||"")
        .replace(/{{data_richiedente}}/g, data.data_richiedente||"")
        .replace(/{{data_target}}/g, data.data_target||"");
      const subject = replace(tpl.subject);
      const body    = replace(tpl.body);
      const isAdmin = type==="new_sw_request"||type==="swap_request";
      addLog("📧 Email","⏳",`Invio ${type} → ${target.email}`,`Oggetto: ${subject} | Logo: ${logoUrl?"✓":"✗"} | Brand: ${savedBrand.color||"#1A1A1A"}`);
      console.log("[SW Notif] invio →", type, "a", target.email);
      const fnRes = await fetch("https://kmesrtgnkohmflyoestm.supabase.co/functions/v1/send-notification", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ type, to: target.email, name: target.name, subject, body, showCta: isAdmin, logoUrl: logoUrl||null, brandColor: savedBrand.color||"#1A1A1A", brandName: savedBrand.name||"LikeYou SW", brandTextColor: savedBrand.textColor||"#FFFFFF", brandSepColor: savedBrand.sepColor||"#1A7A3A", brandFooterText: savedBrand.footerText||"LikeYou SW — Piattaforma Smart Working", ...data })
      });
      const fnData = await fnRes.json().catch(()=>({}));
      if (!fnRes.ok) { addLog("📧 Email","❌",`Errore ${fnRes.status} — ${type}`,JSON.stringify(fnData)); console.error("[SW Notif] ERRORE:", fnRes.status, fnData); }
      else if (fnData?.simulated) { addLog("📧 Email","⚠️",`Simulata (no RESEND_KEY) → ${target.email}`,type); }
      else { addLog("📧 Email","✅",`Inviata a ${target.email}`,`Tipo: ${type} | ID: ${fnData?.result?.id||"?"}`); console.log("[SW Notif] OK:", fnData); }
    } catch(e) { addLog("📧 Email","❌","Eccezione durante invio",e.message); console.error("[SW Notif] eccezione:", e.message); }
  };
  const logout = async () => { await supabase.auth.signOut(); try{safeStore.del("sw_user");}catch{} setRealUser(null); setViewAs(null); };

  const submitConsecRange = async () => {
    const dates = [];
    const cur = new Date(nrDate + "T12:00:00");
    const end = new Date(nrDateTo + "T12:00:00");
    while (cur <= end) {
      const dow = cur.getDay();
      if (dow >= 1 && dow <= 5) {
        const ds = cur.toISOString().split("T")[0];
        if (!isBl(ds) && !reqs.some(r=>r.user_id===cu.id&&r.smart_date===ds&&r.status!=="rejected"))
          dates.push(ds);
      }
      cur.setDate(cur.getDate() + 1);
    }
    if (dates.length === 0) { showToast("Nessun giorno disponibile nell'intervallo", false); return; }
    const cp = getConsecPeriod(nrDate);
    if (cp && cp.max && dates.length > cp.max) {
      showToast(`Puoi selezionare massimo ${cp.max} giorni consecutivi per volta`, false); return;
    }
    const note = nrNote.trim() || cu?.sw_location || "";
    const { error } = await supabase.from("requests").insert(
      dates.map(ds => ({user_id:cu.id,type:"Smart Working",date_from:ds,date_to:ds,smart_date:ds,note,status:"pending",advance_days:0,forced_by_admin:false,is_emergency:false,is_exception:false,cancel_requested:false,half_day:null,company_id:cu.company_id||null}))
    );
    if (error) { showToast("Errore: "+error.message, false); return; }
    setNrDate(""); setNrDateTo(""); setNrNote("");
    showToast(`${dates.length} richieste inviate ✓`);
    const adminUser = team.find(t=>t.role==="admin");
    if (adminUser) addInAppNotif(adminUser.id,"new_sw_request","📋 Richieste SW periodo speciale",`${cu.name} ha richiesto ${dates.length} giorni SW (${nrDate} → ${nrDateTo}).`);
    load();
  };

  const submitReq = async () => {
    console.log("[SW Debug] submitReq →", {nrDate, nrNote, sw_location: cu?.sw_location, rules});
    if (!nrDate) { showToast("Seleziona una data",false); return; }
    const dsInConsecCheck = rules.consec_enabled && (rules.consec_from||"").substring(0,10) && nrDate >= (rules.consec_from||"").substring(0,10) && nrDate <= (rules.consec_to||"").substring(0,10);
    if (dsInConsecCheck && nrDateTo && nrDateTo > nrDate) { await submitConsecRange(); return; }
    // Sede: usa quella del profilo se non specificata diversamente
    if (!nrNote.trim() && !cu?.sw_location) {
      console.log("[SW Debug] BLOCCATO: nessuna sede");
      showToast("Prima di procedere imposta la tua sede nel profilo (icona in basso a sinistra)",false);
      return;
    }
    // Salva subito la sede nel profilo se è la prima volta
    if (!cu?.sw_location && nrNote.trim()) {
      const {error: locErr} = await supabase.from("profiles").update({sw_location: nrNote.trim()}).eq("id", cu.id);
      if (locErr) { showToast("Errore salvataggio sede: "+locErr.message, false); return; }
      const updUser = {...cu, sw_location: nrNote.trim()};
      try { safeStore.set('sw_user', JSON.stringify(updUser)); } catch {}
      setRealUser(updUser);
      setTeam(prev => prev.map(t => t.id===cu.id ? updUser : t));
    }
    const diff = Math.floor((new Date(nrDate+"T12:00:00")-today)/86400000);
    const minAdv = isEm ? 2 : (isInExcPeriod(cu.id, nrDate) ? 0 : rules.advance_days);
    console.log("[SW Debug] diff:", diff, "| minAdv:", minAdv, "| isBl:", isBl(nrDate), "| scnt:", scnt(nrDate), "| max_per_day:", rules.max_per_day);
    if (diff < minAdv) { showToast(`Anticipo minimo: ${minAdv} giorni`,false); return; }
    if (isBl(nrDate)) { showToast("Data non disponibile",false); return; }
    if (scnt(nrDate) >= rules.max_per_day) { showToast("Limite giornaliero raggiunto",false); return; }
    const uR = getUR(cu.id);
    const isHalf = nrHalf !== "full";
    const cost = isHalf ? 0.5 : 1;
    console.log("[SW Debug] uR:", JSON.stringify(uR), "| avail:", avail(cu.id, nrDate), "| cost:", cost, "| isEm:", isEm);
    // Controlla se la data è in un periodo eccezione per questo utente
    const dateInExcPeriod = isInExcPeriod(cu.id, nrDate);
    // Se in periodo eccezione: il 1° SW della settimana scala dal budget, gli altri sono eccezioni
    const weekSwCount = reqs.filter(r=>{
      const d=new Date(nrDate+"T12:00:00"); const dow=d.getDay();
      const mon=new Date(d); mon.setDate(d.getDate()-(dow===0?6:dow-1));
      const sun=new Date(mon); sun.setDate(mon.getDate()+6);
      return r.user_id===cu.id&&r.status!=="rejected"&&r.type!=="desk"&&r.smart_date&&
        r.smart_date>=fmtDate(mon)&&r.smart_date<=fmtDate(sun);
    }).length;
    const isExcBooking = dateInExcPeriod && weekSwCount >= 1;

    if (isEm) {
      if (emUsed(cu.id,today.getFullYear()) >= uR.emergency_days) { showToast("Giorni emergenza esauriti",false); return; }
    } else if (!isExcBooking) {
      // Controlla budget annuo residuo (solo per giorni ordinari)
      if (avail(cu.id, nrDate) < cost) {
        if (!dateInExcPeriod) {
          const doExtra = window.confirm(
            "Hai esaurito il budget SW annuo ("+uR.days_per_year+" giorni).\n\nVuoi inviare una richiesta speciale che verrà valutata dall\'amministratore?\n\nNota: puoi anche usare la tab Comunicazioni per richiedere giorni extra."
          );
          if (!doExtra) return;
          const { error: eExtra } = await supabase.from("requests").insert({
            user_id:cu.id, type:"Smart Working", date_from:nrDate, date_to:nrDate,
            smart_date:nrDate, note:(nrNote.trim()||cu?.sw_location||"")+" [RICHIESTA EXTRA BUDGET]",
            status:"pending", advance_days:diff, forced_by_admin:false, is_emergency:false,
            cancel_requested:false, half_day: nrHalf !== "full" ? nrHalf : null, company_id: cu.company_id||null
          });
          if (eExtra) { showToast("Errore: "+eExtra.message, false); return; }
          showToast("Richiesta extra inviata — l'admin valuterà la tua richiesta");
          const adminUser = team.find(t=>t.role==="admin");
          if (adminUser) sendNotification("new_sw_request", adminUser, { date: nrDate, requester: cu.name, note: "RICHIESTA EXTRA BUDGET" });
          setNrDate(""); setNrNote(""); setNrHalf("full"); load();
          return;
        }
      }
      // Controlla limite mensile (max days_per_month per mese)
      const m = new Date(nrDate+"T12:00:00").getMonth();
      const y = new Date(nrDate+"T12:00:00").getFullYear();
      const mUsedThisMonth = mUsedFull(cu.id, y, m);
      if (mUsedThisMonth + cost > uR.days_per_month) {
        showToast(`Limite mensile raggiunto (max ${uR.days_per_month} giorni a ${["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"][m]})`,false); return;
      }
    }
    // Regola: max 1 SW per settimana (salvo periodo multi-giorno o eccezione)
    const inConsecPeriod = getConsecPeriod(nrDate);
    if (!inConsecPeriod && !isExcBooking && hasSwInWeek(cu.id, nrDate)) {
      showToast("Hai già uno smart working questa settimana (max 1 a settimana)",false); return;
    }
    if (reqs.some(r=>r.user_id===cu.id&&r.smart_date===nrDate&&r.status!=="rejected")) { showToast("Hai già una richiesta per questa data",false); return; }
    let error;
    try {
      const res = await supabase.from("requests").insert({
        user_id:cu.id, type:"Smart Working", date_from:nrDate, date_to:nrDate,
        smart_date:nrDate, note:nrNote.trim()||cu?.sw_location||"", 
        status: isExcBooking ? "approved" : "pending",
        advance_days:diff,
        forced_by_admin:false, is_emergency:isEm, 
        is_exception: isExcBooking || false,
        cancel_requested:false,
        half_day: isHalf ? nrHalf : null, company_id: cu.company_id||null
      });
      error = res.error;
      console.log("[SW Debug] INSERT result → error:", error ? error.message : "null (OK)", "| status:", res.status);
    } catch(ex) {
      console.error("[SW Debug] INSERT ECCEZIONE:", ex?.message, ex);
      showToast("Errore imprevisto: "+ex?.message, false);
      return;
    }
    if (error) { showToast("Errore DB: "+error.message,false); return; }
    // Aggiorna subito lo stato locale con la nuova richiesta
    const newReq = {
      user_id:cu.id, type:"Smart Working", date_from:nrDate, date_to:nrDate,
      smart_date:nrDate, note:nrNote.trim()||cu?.sw_location||"", status:"pending", advance_days:diff,
      forced_by_admin:false, is_emergency:isEm, cancel_requested:false,
      half_day: isHalf ? nrHalf : null, company_id: cu.company_id||null,
      created_at: new Date().toISOString(), id: "temp-"+Date.now()
    };
    setReqs(prev => [newReq, ...prev]);
    showToast("Richiesta inviata. Riceverai conferma a breve.");
    // Notifica email all'admin
    const adminUser = team.find(t=>t.role==="admin");
    if (adminUser) sendNotification("new_sw_request", adminUser.id, {
      requester: cu.name, date: nrDate, note: nrNote,
      half_day: isHalf ? nrHalf : null, is_emergency: isEm
    });
    // Push all'admin
    if (adminUser) sendPushNotification(
      adminUser.id,
      isEm ? "🚨 Nuova Emergenza SW" : "📋 Nuova Richiesta SW",
      `${cu.name} ha richiesto SW per il ${nrDate}.`
    );
    // Notifica in-app all'admin
    if (adminUser) addInAppNotif(
      adminUser.id,
      "new_sw_request",
      isEm ? "🚨 Nuova Emergenza SW" : "📋 Nuova Richiesta SW",
      `${cu.name} ha richiesto SW per il ${nrDate}.`
    );
    setNrDate(""); setNrNote(""); setIsEm(false); setNrHalf("full"); load();
  };


  const hReq = async (id, action) => {
    const req = reqs.find(r=>r.id===id);
    const u = team.find(t=>t.id===req?.user_id);
    const { error } = await supabase.from("requests").update({ status:action }).eq("id", id);
    if (error) { addLog("⚙️ Admin","❌",`Errore ${action}`,error.message); showToast("Errore: "+error.message,false); return; }
    addLog("⚙️ Admin", action==="approved"?"✅":"❌", `Richiesta ${action==="approved"?"approvata":"rifiutata"}`, `${u?.name} | ${req?.smart_date}`);
    if (req) sendNotification(
      action==="approved" ? "sw_approved" : "sw_rejected",
      req.user_id,
      { date: req.smart_date, note: req.note, half_day: req.half_day }
    );
    // Push istantanea all'utente
    if (req) sendPushNotification(
      req.user_id,
      action==="approved" ? "✅ Smart Working Approvato" : "❌ Smart Working Non Approvato",
      `Il tuo SW del ${req.smart_date} è stato ${action==="approved"?"approvato":"rifiutato"}.`
    );
    // Notifica in-app
    if (req) addInAppNotif(
      req.user_id,
      action==="approved" ? "sw_approved" : "sw_rejected",
      action==="approved" ? "✅ Smart Working Approvato" : "❌ Smart Working Non Approvato",
      `Il tuo SW del ${req.smart_date} è stato ${action==="approved"?"approvato":"rifiutato"}.`
    );
    showToast(action==="approved"?"Approvata ✓":"Rifiutata"); load();
  };

  // FIX: delete con gestione errori esplicita
  const delReq = async (id) => {
    const req = reqs.find(r=>r.id===id);
    const u = team.find(t=>t.id===req?.user_id);
    const { error } = await supabase.from("requests").delete().eq("id", id);
    if (error) {
      addLog("⚙️ Admin","❌","Errore eliminazione",error.message);
      showToast("Errore eliminazione. Esegui il fix RLS in Supabase (vedi commenti SQL in cima al file)",false);
      return;
    }
    addLog("⚙️ Admin","✅","Richiesta eliminata",`${u?.name} | ${req?.smart_date}`);
    if (req) sendNotification(req.type==="desk"?"desk_deleted":"sw_rejected", req.user_id, { date: req.smart_date });
    showToast("Eliminata"); load();
  };

  const reqCancel = async (reqId) => {
    const note = cnNoteRef.current;
    await supabase.from("requests").update({ cancel_requested:true, cancel_note:note }).eq("id", reqId);
    addLog("📋 Prenotazione","⏳","Richiesta cancellazione inviata",`ID: ${reqId}`);
    showToast("Richiesta di cancellazione inviata."); setCancelTarget(null); cnNoteRef.current=""; setCnNoteVal(""); load();
  };

  const hCancel = async (id, approve) => {
    const req = reqs.find(r=>r.id===id);
    const u = team.find(t=>t.id===req?.user_id);
    if (approve) await supabase.from("requests").update({ status:"rejected", cancel_requested:false }).eq("id",id);
    else         await supabase.from("requests").update({ cancel_requested:false }).eq("id",id);
    addLog("⚙️ Admin", approve?"✅":"❌", `Cancellazione ${approve?"approvata":"rifiutata"}`, `${u?.name} | ${req?.smart_date}`);
    if (req) {
      const isDesk = req.type==="desk";
      sendNotification(
        approve ? (isDesk?"cancel_approved_desk":"cancel_approved") : (isDesk?"cancel_rejected_desk":"cancel_rejected"),
        req.user_id,
        { date: req.smart_date }
      );
      sendPushNotification(
        req.user_id,
        approve ? "🗑️ Cancellazione Approvata" : "ℹ️ Cancellazione Non Approvata",
        `La richiesta di cancellazione del ${req.smart_date} è stata ${approve?"approvata":"rifiutata"}.`
      );
      addInAppNotif(
        req.user_id,
        approve ? "cancel_approved" : "cancel_rejected",
        approve ? "🗑️ Cancellazione Approvata" : "ℹ️ Cancellazione Non Approvata",
        `La richiesta di cancellazione del ${req.smart_date} è stata ${approve?"approvata":"rifiutata"}.`
      );
    }
    showToast(approve?"Cancellazione approvata ✓":"Rifiutata"); load();
  };

  // FIX: forceSW — imposta tutti i campi obbligatori
  // SW Ricorrente: genera tutte le settimane nel range per il giorno scelto
  const forceRecurring = async () => {
    if (!recUser || !recFrom || !recTo) { showToast("Compila tutti i campi", false); return; }
    const DOW = parseInt(recDay); // 1=Lun...7=Dom (ISO)
    const from = new Date(recFrom+"T12:00:00");
    const to   = new Date(recTo+"T12:00:00");
    let cur = new Date(from);
    while(((cur.getDay()||7)) !== DOW) { cur.setDate(cur.getDate()+1); }
    const dates = [];
    while(cur <= to) {
      const ds = cur.toISOString().slice(0,10);
      const isBlocked = blocked.some(b=>b.date===ds);
      const alreadyExists = reqs.some(r=>r.user_id===recUser&&r.smart_date===ds&&r.status!=="rejected");
      if (!isBlocked && !alreadyExists) dates.push(ds);
      cur.setDate(cur.getDate()+7);
    }
    if (dates.length===0) { showToast("Nessuna data disponibile nel range (tutte bloccate o già inserite)", false); return; }
    const u = team.find(t=>t.id===recUser);
    const rows = dates.map(d => ({
      user_id:recUser, type:"Smart Working",
      date_from:d, date_to:d, smart_date:d,
      note: recNote||u?.sw_location||"SW ricorrente",
      status:"approved", advance_days:0,
      forced_by_admin:true, is_emergency:false, cancel_requested:false, half_day:null,
      company_id: u?.company_id||null
    }));
    const{error}=await supabase.from("requests").insert(rows);
    if(error){showToast("Errore: "+error.message,false);return;}
    showToast(`✓ ${dates.length} giornate SW ricorrenti inserite`);
    setRecUser(""); setRecFrom(""); setRecTo(""); setRecNote(""); load();
  };

  const forceSW = async () => {
    if (!fUser||!fDate) { showToast("Seleziona utente e data",false); return; }
    const { error } = await supabase.from("requests").insert({
      user_id:fUser, type:"Smart Working",
      date_from:fDate, date_to:fDate, smart_date:fDate,
      note:fNote||"Assegnato dall'amministratore",
      status:"approved", advance_days:0,
      forced_by_admin:true, is_emergency:false, cancel_requested:false, company_id: cu.company_id||null
    });
    if (error) { showToast("Errore: "+error.message+" — controlla che le colonne esistano (vedi SQL)",false); return; }
    showToast("Assegnato ✓"); setFUser(""); setFDate(""); setFNote(""); load();
  };

  const blDay = async (reasonOverride, dateTo) => {
    if(!blDate){showToast("Seleziona data",false);return;}
    const reason = reasonOverride || blReas;
    const endDate = dateTo || blDateTo || blDate;
    // Genera tutte le date nel range
    const dates = [];
    let cur = new Date(blDate+"T12:00:00");
    const end = new Date(endDate+"T12:00:00");
    while(cur <= end) {
      dates.push(cur.toISOString().slice(0,10));
      cur.setDate(cur.getDate()+1);
    }
    const rows = dates.map(d => ({date:d, reason, created_by:cu.id}));
    const{error}=await supabase.from("blocked_days").insert(rows);
    if(error){showToast("Errore: "+error.message,false);return;}
    showToast(dates.length===1 ? blDate+" bloccato" : dates.length+" giorni bloccati");
    setBlDate(""); setBlDateTo(""); setBlReas(""); load();
  };
  const unblDay = async id  => { await supabase.from("blocked_days").delete().eq("id",id); showToast("Sbloccato"); load(); };
  const togDesk = async (id,a) => { await supabase.from("office_desks").update({active:!a}).eq("id",id); load(); };
  const delDesk = async id  => { await supabase.from("office_desks").delete().eq("id",id); showToast("Rimossa"); load(); };
  const saveEditBooking = async (id, half_day) => {
    await supabase.from("requests").update({ half_day: half_day || null }).eq("id", id);
    setEditBooking(null);
    showToast("Prenotazione aggiornata ✓");
    load();
  };
  const delBooking = async (id) => {
    await supabase.from("requests").delete().eq("id", id);
    showToast("Prenotazione eliminata");
    load();
  };
  const addDesk = async () => { if(!ndName.trim()){showToast("Inserisci nome",false);return;} await supabase.from("office_desks").insert({name:ndName.trim(),active:true,room:ndRoom,assigned_to:ndAssigned||null,expires_date:ndExpires||null}); showToast("Aggiunta ✓"); setNdName(""); setNdAssigned(""); setNdExpires(""); load(); };
  const saveRules = async () => { await supabase.from("sw_rules").update(editRules).eq("id",1); showToast("Regole aggiornate ✓"); load(); };
  const saveEx  = async () => { if(!exUid){showToast("Seleziona utente",false);return;} await supabase.from("profiles").update({extra_days:exDays,extra_note:exNote}).eq("id",exUid); showToast("Salvato ✓"); setExUid(""); setExDays(0); setExNote(""); load(); };
  const saveUS  = async () => {
    if (!usTgt){showToast("Seleziona utente",false);return;}
    const ex = uSet.find(s=>s.user_id===usTgt);
    const p = {days_per_month:parseInt(usDm)||0, days_per_year:parseInt(usDy)||0, emergency_days:parseInt(usEm)||0, note:usNote};
    console.log("[saveUS] usTgt:", usTgt, "ex:", ex?.user_id, "p:", p);
    let error;
    if (ex) ({ error } = await supabase.from("user_sw_settings").update(p).eq("user_id", usTgt));
    else    ({ error } = await supabase.from("user_sw_settings").insert({user_id:usTgt,...p}));
    if (error) { console.error("[saveUS] errore:", error); showToast("Errore: "+error.message, false); return; }
    showToast("Impostazioni salvate ✓"); setUsTgt(null); load();
  };
  const resetUS = async uid => { await supabase.from("user_sw_settings").delete().eq("user_id",uid); showToast("Ripristinato"); load(); };
  const updUser = async () => {
    if (!editU) return;
    await supabase.from("profiles").update({name:editU.name,role:editU.role,sw_permission:editU.sw_permission}).eq("id",editU.id);
    showToast("Aggiornato ✓"); setEditU(null); load();
  };
  const delUser = async uid => {
    if (uid===realUser.id){showToast("Non puoi eliminare il tuo profilo",false);return;}
    const{error}=await supabase.from("profiles").delete().eq("id",uid);
    if(error)showToast("Errore: "+error.message,false); else{showToast("Rimosso"); load();}
  };

  // SCAMBIO
  const submitSwap = async () => {
    if(!swapMyDate||!swapTgtUser||!swapTgtDate){showToast("Completa tutti i campi",false);return;}
    const note = swapNoteRef.current;
    const{error}=await supabase.from("swap_requests").insert({requester_id:cu.id,target_id:swapTgtUser,requester_date:swapMyDate,target_date:swapTgtDate,note,status:"pending"});
    if(error){showToast("Errore: "+error.message,false);return;}
    const target = team.find(t=>t.id===swapTgtUser);
    if (target) sendNotification("swap_request", target.id, { richiedente: cu.name, data_richiedente: swapMyDate, data_target: swapTgtDate });
    showToast("Proposta di scambio inviata!"); setSwapModal(false); setSwapMyDate(""); setSwapTgtUser(""); setSwapTgtDate(""); swapNoteRef.current=""; setSwapNoteVal(""); load();
  };
  const hSwap = async (id, action) => {
    if (action==="approved") {
      const sw = swaps.find(s=>s.id===id);
      if (sw) {
        // Approva: sposta le date reali nelle richieste
        const rReq = reqs.find(r=>r.user_id===sw.requester_id&&r.smart_date===sw.requester_date&&r.status!=="rejected");
        const tReq = reqs.find(r=>r.user_id===sw.target_id&&r.smart_date===sw.target_date&&r.status!=="rejected");
        if (rReq) await supabase.from("requests").update({smart_date:sw.target_date,date_from:sw.target_date,date_to:sw.target_date}).eq("id",rReq.id);
        if (tReq) await supabase.from("requests").update({smart_date:sw.requester_date,date_from:sw.requester_date,date_to:sw.requester_date}).eq("id",tReq.id);
      }
    }
    await supabase.from("swap_requests").update({status:action}).eq("id",id);
    showToast(action==="approved"?"Scambio approvato ✓":"Scambio rifiutato"); load();
  };

  // MESSAGGI
  const sendSupport = async () => {
    if (!suppText.trim()) return;
    setSuppBusy(true);
    await supabase.from("messages").insert({user_id:cu.id, content:suppText.trim(), is_private:false, msg_type: suppCat});
    setSuppBusy(false);
    showToast("Richiesta inviata! Ti risponderemo presto ✓");
    setSuppText(""); setSuppCat("domanda"); load();
  };

  const sendMessage = async () => {
    const content = msgRef.current.trim();
    if (!content){showToast("Scrivi un messaggio",false);return;}
    await supabase.from("messages").insert({user_id:cu.id,content,is_private:false});
    showToast("Messaggio inviato!"); msgRef.current=""; setMsgVal(""); load();
  };
  const replyMessage = async (id, reply) => {
    await supabase.from("messages").update({admin_reply:reply}).eq("id",id);
    showToast("Risposta inviata ✓"); load();
  };
  const delMessage = async id => { await supabase.from("messages").delete().eq("id",id); load(); };

  // Imposta fascia (mattina/pomeriggio/intera) per utente fisso in un giorno
  const setFixedSlot = async (userId, date, newSlot) => {
    // Cerca prenotazione desk esistente per questo utente fisso in questo giorno
    const existing = reqs.find(r => r.user_id === userId && r.smart_date === date && r.type === "desk" && r.forced_by_admin === true);
    if (newSlot === "full") {
      if (existing) await supabase.from("requests").update({ half_day: null }).eq("id", existing.id);
      // se non esiste, non fare nulla (full è il default)
    } else {
      if (existing) {
        await supabase.from("requests").update({ half_day: newSlot }).eq("id", existing.id);
      } else {
        await supabase.from("requests").insert({ user_id: userId, type: "desk", smart_date: date, date_from: date, date_to: date, status: "approved", half_day: newSlot, forced_by_admin: true, is_emergency: false, cancel_requested: false, company_id: cu.company_id||null });
      }
    }
    load();
  };

  // ── RECOVERY LINK HANDLER ────────────────────────────────────
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryDone, setRecoveryDone] = useState(false);
  const [dbResetToken, setDbResetToken] = useState(null);

  useEffect(() => {
    // Caso 1: link Supabase Auth (#access_token=...&type=recovery)
    const h = window.location.hash;
    if (h.includes("type=recovery") || h.includes("type=passwordRecovery")) {
      const params = new URLSearchParams(h.substring(1));
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      if (accessToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken || "" })
          .then(() => { setRecoveryMode(true); window.history.replaceState(null, "", window.location.pathname); });
        return;
      }
    }
    // Caso 2: link custom DB (?reset_token=xxx)
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get("reset_token");
    if (resetToken) {
      setDbResetToken(resetToken);
      setRecoveryMode(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  // ── GUARD ────────────────────────────────────────────────────
  if (!realUser && !recoveryMode) return <LoginScreen onLogin={u=>{ try{localStorage.setItem("sw_user",JSON.stringify(u));}catch{} setRealUser(u);}}/>;

  // Recovery password via link email (tutti gli utenti)
  if (recoveryMode && !recoveryDone) {
    return <DbResetScreen dbResetToken={dbResetToken} onDone={()=>{ setRecoveryDone(true); setRecoveryMode(false); setDbResetToken(null); }} BG={BG} WHT={WHT} BDR={BDR} SUB={SUB} BLK={BLK}/>;
  }

  // Forza cambio password al primo accesso (utenti DB)
  if (realUser?.must_change_password && realUser?._authMode === "db") {
    return (
      <div style={{minHeight:"100vh",background:BG,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif",padding:20}}>
        <style>{"@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');*{box-sizing:border-box;margin:0;padding:0;}"}</style>
        <div style={{width:"100%",maxWidth:400}}>
          <div style={{background:WHT,border:"1px solid "+BDR,borderRadius:18,padding:32,boxShadow:"0 4px 20px rgba(0,0,0,.06)"}}>
            <div style={{fontSize:24,marginBottom:8}}>🔐</div>
            <h2 style={{fontSize:19,fontWeight:700,marginBottom:5}}>Imposta la tua password</h2>
            <p style={{color:SUB,fontSize:13,marginBottom:22,lineHeight:1.6}}>È il tuo primo accesso. Scegli una password personale per proteggere il tuo account.</p>
            <ForcePwChange supabase={supabase} user={realUser} onDone={(u)=>{ const nu={...u,must_change_password:false}; try{safeStore.set('sw_user',JSON.stringify(nu));}catch{} setRealUser(nu); }}/>
          </div>
        </div>
      </div>
    );
  }

  // ── DERIVATI ─────────────────────────────────────────────────
  const myUR    = getUR(cu.id);
  const myYU    = yUsed(cu.id, today.getFullYear());
  const myMU    = mUsedFull(cu.id, today.getFullYear(), today.getMonth());
  const myAv    = avail(cu.id, fmtDate(today));
  const myEmU   = emUsed(cu.id, today.getFullYear());
  const myEmAv  = myUR.emergency_days - myEmU;
  const pendCnt = reqs.filter(r=>r.status==="pending").length;
  const cnCnt   = reqs.filter(r=>r.cancel_requested).length;
  const swapPend= swaps.filter(s=>s.status==="pending"&&(s.target_id===realUser.id||isAdmin)).length;
  const msgUnread=messages.filter(m=>!m.admin_reply&&m.user_id!==realUser.id).length;
  const myCanSW = isFixed(cu);
  const wkStart = addD(today, wkOff);
  const wkDays  = Array.from({length:7},(_,i)=>addD(wkStart,i));
  const myUD    = team.find(t=>t.id===cu.id);
  const myExtra = myUD?.extra_days||0;
  const myExcCount = reqs.filter(r=>r.user_id===cu.id&&r.is_exception&&r.status==="approved"&&r.smart_date&&new Date(r.smart_date).getFullYear()===today.getFullYear()).length;
  // Controlla se una data è in un periodo eccezione per un utente
  const isInExcPeriod = (uid, ds) => excPeriods.some(p => p.user_id===uid && ds>=p.date_from && ds<=p.date_to);
  // Periodo eccezione attivo per l'utente corrente
  const myActiveExcPeriod = excPeriods.find(p => p.user_id===cu.id && fmtDate(today)>=p.date_from && fmtDate(today)<=p.date_to);
  const smartToday = reqs.filter(r=>r.smart_date===todayStr&&r.status==="approved"&&r.type!=="desk"&&!(r.is_exception&&r.forced_by_admin)&&!isFullRemote(team.find(t=>t.id===r.user_id)||{})).length;
  // Postazioni libere oggi = totale attive - assegnate a chi è in ufficio - prenotate
  const deskReqsToday = reqs.filter(r=>r.smart_date===todayStr&&r.type==="desk"&&r.status==="approved");
  const swToday = reqs.filter(r=>r.smart_date===todayStr&&r.status==="approved"&&r.type!=="desk"&&!(r.is_exception&&r.forced_by_admin)&&!isFullRemote(team.find(t=>t.id===r.user_id)||{})).map(r=>r.user_id);
  const fixedInOfficeToday = team.filter(u=>isFixed(u)&&!isFullRemote(u)&&!swToday.includes(u.id)&&!u.vacation);
  const fixedIdsTodaySet = new Set(fixedInOfficeToday.map(u=>u.id));
  const activeDesks = desks.filter(d=>d.active);
  const swTodaySet = new Set(swToday);
  // FR/collab con prenotazione postazione oggi (vengono in ufficio) — dedup per user
  const deskBookersTodaySet = new Set(deskReqsToday.filter(r=>!fixedIdsTodaySet.has(r.user_id)&&!swTodaySet.has(r.user_id)).map(r=>r.user_id));
  const bookedTodayNonFixed = deskBookersTodaySet.size;
  const freeDesks = Math.max(0, activeDesks.length - fixedInOfficeToday.length - bookedTodayNonFixed);

  const deskRooms = () => {
    const map = {};
    desks.forEach(d => { const r=d.room||"Ufficio"; if(!map[r])map[r]=[]; map[r].push(d); });
    return map;
  };

  const mvData = () => Array.from({length:dim(mvY,mvM)},(_,i)=>{
    const day=i+1;
    const ds=`${mvY}-${String(mvM+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    return {day,ds,rqs:reqs.filter(r=>r.smart_date===ds&&r.status!=="rejected"),bl:isBl(ds)};
  });

  const amIFullRemote = isFullRemote(cu);
  const nav = [
    {id:"dashboard", label:"Dashboard"},
    ...(myCanSW ? [{id:"request", label:"Prenota Smart Working"}] : []),
    ...(amIFullRemote ? [{id:"desk_booking", label:"Prenota Postazione"}] : []),
    ...(myCanSW ? [{id:"swap", label:"Scambia SW", badge:swaps.filter(s=>s.target_id===cu.id&&s.status==="pending").length}] : []),
    {id:"piano",     label:"Piano Settimanale"},
    ...(myCanSW ? [{id:"summary", label:"Il mio riepilogo"}] : []),
    {id:"mystatus",  label:"Le mie richieste"},
    {id:"messages",  label:"Comunicazioni",  badge:isAdmin?msgUnread:messages.filter(m=>m.user_id===cu.id&&m.admin_reply&&!m._read).length},
    {id:"guide",     label:"📖 Guida & Regole"},
    ...(isAdmin?[{id:"admin",label:"Pannello Admin",adm:true,badge:pendCnt+cnCnt+swapPend}]:[]),
  ];

  // ── CSS ──────────────────────────────────────────────────────
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;}
    @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    .page{animation:fadeIn .22s ease}
    ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:#F0F0F0}::-webkit-scrollbar-thumb{background:#CCC;border-radius:3px}
    .nb{background:none;border:none;cursor:pointer;padding:8px 11px;border-radius:9px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:500;color:#888;transition:all .15s;display:flex;align-items:center;gap:9px;width:100%;text-align:left;}
    .nb:hover{background:#F0F0F0;color:${BLK}}.nb.act{background:${BLK};color:#FFF;}
    .nb.adm{color:#7A5A00}.nb.adm:hover{background:#FFF8E0}.nb.adm.act{background:${BLK};color:#FFD700}
    .card{background:${WHT};border:1px solid ${BDR};border-radius:13px;padding:22px}
    .bp{background:${BLK};color:white;border:none;border-radius:9px;padding:10px 18px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:700;cursor:pointer;transition:background .15s}
    .bp:hover{background:#333}
    .bs{padding:5px 11px;font-size:12px;border-radius:7px;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;font-weight:600}
    .ba{background:#E8F8EE;color:#1A7A3A}.ba:hover{background:#D0F0DC}
    .br{background:#FEE8E8;color:#C0392B}.br:hover{background:#FDD0D0}
    .bd{background:#FEE8E8;color:#C0392B;border:none;border-radius:7px;padding:5px 11px;font-size:12px;cursor:pointer;font-family:'DM Sans',sans-serif;font-weight:600}
    .bsec{background:#F5F5F5;border:1px solid ${BDR};color:#666;border-radius:7px;padding:5px 11px;font-size:12px;cursor:pointer;font-family:'DM Sans',sans-serif;font-weight:600}
    .tag{display:inline-flex;align-items:center;padding:2px 9px;border-radius:20px;font-size:11px;font-weight:600}
    .tp{background:#FFF3CD;color:#856404}.ta{background:#D4EDDA;color:#1A7A3A}.tr{background:#F8D7DA;color:#721C24}.tem{background:#FFE8CC;color:#C05A00}
    input,select{background:#FAFAFA;border:1px solid ${BDR};border-radius:9px;color:${BLK};padding:9px 12px;font-family:'DM Sans',sans-serif;font-size:13px;outline:none;transition:border .15s;width:100%}
    textarea{background:#FAFAFA;border:1px solid ${BDR};border-radius:9px;color:${BLK};padding:9px 12px;font-family:'DM Sans',sans-serif;font-size:13px;outline:none;transition:border .15s;width:100%}
    input:focus,select:focus,textarea:focus{border-color:${LYB};background:#FFF}
    select option{background:#FFF}
    .av{width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;color:#FFF}
    @keyframes fi{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}.page{animation:fi .18s ease}
    @keyframes si{from{transform:translateY(10px);opacity:0}to{transform:translateY(0);opacity:1}}.tel{animation:si .22s ease}
    .pb{height:5px;border-radius:3px;background:#E8E8E8;overflow:hidden}.pf{height:100%;border-radius:3px;transition:width .3s}
    .cd{width:100%;aspect-ratio:1;max-width:44px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:12px;cursor:pointer;transition:all .1s;position:relative;color:${BLK};user-select:none}
    .cd:hover{background:#EEE}.cd.bl{background:#FDECEA;color:#C0392B;cursor:not-allowed}.cd.ho{background:#FFF0F0;color:#C0392B;cursor:not-allowed}
    .day-slider{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
    @media(max-width:700px){.day-slider{display:flex;overflow-x:auto;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;gap:10px;padding-bottom:6px;padding-left:2px;margin-left:-2px}.day-slider>div{flex:0 0 82vw;scroll-snap-align:start}}
    .cd.wu{background:#F5F0FF;color:#7A5A9A;cursor:not-allowed;opacity:.7}.cd.hs{background:#D4EDDA;color:#1A7A3A;font-weight:600}
    .cd.pa{opacity:.2;cursor:not-allowed}.cd.ts{opacity:.3;cursor:not-allowed}
    .cd.rng{background:#FFF3CD;border-color:#F0C040;}.cd.sel{background:${BLK};color:#FFF;font-weight:700}.cd.em:not(.pa):not(.bl){outline:1px dashed #C05A00;outline-offset:1px}
    .dot{position:absolute;bottom:2px;right:2px;width:5px;height:5px;border-radius:50%}
    .tgl{width:42px;height:22px;border-radius:11px;cursor:pointer;position:relative;transition:background .15s;flex-shrink:0;border:none}
    .tglk{width:16px;height:16px;border-radius:50%;background:white;position:absolute;top:3px;transition:left .15s;box-shadow:0 1px 3px rgba(0,0,0,.2)}
    .mo{position:fixed;inset:0;background:rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;z-index:1000;padding:16px}
    .desk-modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:9998;display:flex;align-items:center;justify-content:center;padding:20px}
    .desk-modal-box{background:white;border-radius:18px;padding:24px 22px;width:100%;max-width:460px;box-shadow:0 20px 60px rgba(0,0,0,.25);max-height:88vh;overflow-y:auto}
    @media(max-width:600px){.desk-modal-bg{align-items:flex-end;padding:0}.desk-modal-box{border-radius:18px 18px 0 0;max-width:100%;padding:20px 16px;padding-bottom:calc(20px + env(safe-area-inset-bottom));max-height:88dvh}}
    .md{background:${WHT};border:1px solid ${BDR};border-radius:16px;padding:26px;width:460px;max-width:100%;box-shadow:0 14px 50px rgba(0,0,0,.13);max-height:92vh;overflow-y:auto}
    .lt{background:none;border:1px solid ${BDR};cursor:pointer;padding:5px 11px;border-radius:7px;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:600;transition:all .12s;color:#888;white-space:nowrap}
    .lt.act{background:${BLK};color:#FFF;border-color:${BLK}}
    .ri{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid #F2F2F2}
    .lb{font-size:10px;color:${SUB};display:block;margin-bottom:4px;text-transform:uppercase;letter-spacing:.8px}
    .st{font-size:14px;font-weight:700;color:${BLK};margin-bottom:14px}
    .sim-bar{background:#FFF8E0;border-bottom:1px solid #F0C040;padding:8px 18px;display:flex;align-items:center;gap:12px;font-size:12px;font-family:'DM Sans',sans-serif;flex-shrink:0;z-index:100}
    .sidebar{width:235px;background:${WHT};border-right:1px solid ${BDR};display:flex;flex-direction:column;padding:16px 10px;flex-shrink:0;overflow-y:auto;overflow-x:hidden;height:100%;box-sizing:border-box}
    .nb-badge{background:#C0392B;color:white;border-radius:20px;padding:1px 7px;font-size:10px;font-weight:700;min-width:18px;text-align:center}
    @media(max-width:768px){
      .sidebar{position:fixed;left:0;top:0;height:100%;height:100dvh;z-index:200;transform:translateX(-100%);transition:transform .22s;box-shadow:3px 0 20px rgba(0,0,0,.1);overflow-y:auto!important;overflow-x:hidden;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding-top:max(16px,env(safe-area-inset-top))!important;padding-bottom:max(24px,env(safe-area-inset-bottom))!important}
      .sidebar.open{transform:translateX(0)}
      .sidebar.open{transform:translateX(0)}
      .mob-bar{display:flex!important}
      .g4{grid-template-columns:repeat(2,1fr)!important}
      .g2{grid-template-columns:1fr!important}
      .gcal{grid-template-columns:repeat(7,1fr)!important}
      .gwk{overflow-x:auto;-webkit-overflow-scrolling:touch}
      .main-pad{padding:16px!important}
      .hide-mob{display:none!important}
      .main-scroll{overflow-x:hidden!important;max-width:100vw;-webkit-overflow-scrolling:touch;overscroll-behavior-y:contain}
      html,body{overflow:hidden!important;max-width:100vw!important;height:100%!important;position:fixed;width:100%}
      .mob-bar{position:fixed!important;top:0;left:0;right:0;z-index:150;padding-top:env(safe-area-inset-top)!important}
      .main-scroll{padding-top:calc(44px + env(safe-area-inset-top))!important;height:100dvh!important;overflow-y:scroll!important;-webkit-overflow-scrolling:touch!important;padding-bottom:calc(40px + env(safe-area-inset-bottom))!important;box-sizing:border-box!important}.simulating .main-scroll{height:calc(100dvh - 42px)!important;padding-top:calc(44px + env(safe-area-inset-top))!important}
      .card{padding:14px!important}
      input,select,textarea{font-size:16px!important}
    }
    .mob-bar{display:none;align-items:center;justify-content:space-between;background:${WHT};border-bottom:1px solid ${BDR};padding:11px 16px;position:sticky;top:0;z-index:150;flex-shrink:0}
    /* Dark mode overrides per classi CSS */
    .dark .card{background:${darkMode?"#1E1E1E":"#FFFFFF"};border-color:${darkMode?"#2E2E2E":"#E2E2E2"}}
    .dark .sidebar{background:${darkMode?"#161616":"#FFFFFF"};border-color:${darkMode?"#2E2E2E":"#E2E2E2"}}
    .dark .mob-bar{background:${darkMode?"#161616":"#FFFFFF"};border-color:${darkMode?"#2E2E2E":"#E2E2E2"}}
    .dark input,.dark select,.dark textarea{background:${darkMode?"#252525":"#FAFAFA"};border-color:${darkMode?"#333":"#E2E2E2"};color:${darkMode?"#F0F0F0":"#1A1A1A"}}
    .dark .lt{border-color:${darkMode?"#333":"#E2E2E2"};color:${darkMode?"#999":"#888"}}
    .dark .lt.act{background:${darkMode?"#F0F0F0":"#1A1A1A"};color:${darkMode?"#111":"#FFF"};border-color:${darkMode?"#F0F0F0":"#1A1A1A"}}
    .dark .bsec{background:${darkMode?"#252525":"#F5F5F5"};border-color:${darkMode?"#333":"#E2E2E2"};color:${darkMode?"#CCC":"#666"}}
    .dark .ri{border-color:${darkMode?"#252525":"#F2F2F2"}}
    .dark .nb{color:${darkMode?"#CCC":"inherit"}}
    .dark .nb.act{background:${darkMode?"#2A2A2A":"#F4F4F4"};color:${darkMode?"#F0F0F0":"#1A1A1A"}}
    .dark .md{background:${darkMode?"#1E1E1E":"#FFFFFF"};border-color:${darkMode?"#2E2E2E":"#E2E2E2"}}
    .dark .mo{background:rgba(0,0,0,.6)}
    .dark .sim-bar{background:${darkMode?"#2A2200":"#FFF8E0"};border-color:${darkMode?"#554400":"#F0C040"}}
    .dark select option{background:${darkMode?"#1E1E1E":"#FFF"}}
    .dark .cd{color:${darkMode?"#EEE":"#1A1A1A"}}
    .dark .cd:hover{background:${darkMode?"#2A2A2A":"#EEE"}}
    .dark .cd.hs{background:${darkMode?"#1A2E3A":"#E8F0F5"}}.dark .cd.ho{background:${darkMode?"#3A1A1A":"#FFF0F0"};color:#FF8080}
  `;

  // ─ mini helper componenti ─
  // Lbl e Row definiti come componenti globali fuori da App
  const PBar = ({pct,col=BLK}) => <div className="pb"><div className="pf" style={{width:`${Math.min(pct||0,100)}%`,background:col}}/></div>;
  const Tog = ({on,set}) => <button className="tgl" style={{background:on?BLK:"#DDD"}} onClick={set}><div className="tglk" style={{left:on?23:3}}/></button>;
  const SC = (l,v,s,c=BLK) => (
    <div style={{background:WHT,border:"1px solid "+BDR,borderRadius:13,padding:18}}>
      <div style={{fontSize:10,color:SUB,textTransform:"uppercase",letterSpacing:1}}>{l}</div>
      <div style={{fontSize:28,fontWeight:700,fontFamily:"'Space Mono',monospace",color:c,marginTop:6,lineHeight:1}}>{v}</div>
      <div style={{fontSize:11,color:SUB,marginTop:5}}>{s}</div>
    </div>
  );

  const tagCls = st => `tag t${(st||"p")[0]}`;
  const tagTxt = st => st==="pending"?"In attesa":st==="approved"?"Approvata":"Rifiutata";

  // ─────────────────────────────────────────────────────────────
  const TUT_STEPS = [
    { emoji:"👋", title:"Benvenuto in LikeYou SW!", body:"Questa piattaforma ti permette di gestire le tue giornate di smart working in modo semplice e veloce. Ti guidiamo in pochi passi!" },
    { emoji:"📅", title:"Richiedi uno smart working", body:"Dalla sezione «Richiedi SW» puoi prenotare i tuoi giorni di lavoro da casa. Scegli la data e invia — il responsabile riceverà la notifica." },
    { emoji:"🏢", title:"Prenota una postazione", body:"Quando vieni in ufficio puoi prenotare la tua scrivania dalla sezione «Postazione». Vedi in tempo reale quante sono disponibili." },
    { emoji:"📊", title:"Dashboard", body:"Nella home trovi sempre la situazione del giorno: chi è in ufficio, chi è in SW, i tuoi giorni disponibili e le prossime scadenze." },
    { emoji:"🔔", title:"Tutto pronto!", body:"Sei a posto! Puoi sempre rileggere questa guida dal menu. Buon lavoro 🚀" },
  ];

  return (
    <div className={(darkMode?"dark":"")+(simulating?" simulating":"")} style={{fontFamily:"'DM Sans',sans-serif",height:"100dvh",minHeight:"100vh",background:BG,color:BLK,display:"flex",flexDirection:"column",overflow:"hidden",maxWidth:"100vw",position:"relative"}}>
      <style>{css}</style>

      {/* Tutorial primo accesso */}
      {tutStep>=0&&tutStep<TUT_STEPS.length&&(()=>{
        const s=TUT_STEPS[tutStep];
        const isLast=tutStep===TUT_STEPS.length-1;
        const close=()=>{ try{safeStore.set("sw_tutorial_done","1");}catch{} setTutStep(-1); };
        return(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:"white",borderRadius:20,padding:"32px 28px",maxWidth:380,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.3)",textAlign:"center"}}>
              <div style={{fontSize:52,marginBottom:12}}>{s.emoji}</div>
              <div style={{fontSize:17,fontWeight:800,marginBottom:10,color:BLK}}>{s.title}</div>
              <div style={{fontSize:13,color:SUB,lineHeight:1.6,marginBottom:24}}>{s.body}</div>
              {/* dots */}
              <div style={{display:"flex",justifyContent:"center",gap:6,marginBottom:20}}>
                {TUT_STEPS.map((_,i)=>(
                  <div key={i} style={{width:i===tutStep?20:7,height:7,borderRadius:4,background:i===tutStep?BLK:"#DDD",transition:"width .3s"}}/>
                ))}
              </div>
              <div style={{display:"flex",gap:10}}>
                <button onClick={close} style={{flex:1,padding:"9px 0",borderRadius:10,border:"1px solid #DDD",background:"white",color:SUB,fontSize:13,cursor:"pointer"}}>Salta</button>
                <button onClick={()=>isLast?close():setTutStep(s=>s+1)} style={{flex:2,padding:"9px 0",borderRadius:10,border:"none",background:BLK,color:"white",fontSize:13,fontWeight:700,cursor:"pointer"}}>{isLast?"Inizia! 🚀":"Avanti →"}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Desk day overview modal */}
      {deskDayModal&&(()=>{
        const {ds,label}=deskDayModal;
        const slot=deskTimeSlot; // "morning" | "afternoon"
        const allDesksMod=desks.filter(d=>d.active);
        const totalD=allDesksMod.length;
        const deskReqs=reqs.filter(r=>r.smart_date===ds&&r.type==="desk"&&r.status==="approved");
        const deskBookerIds=new Set(deskReqs.map(r=>r.user_id));
        // SW quel giorno nella fascia — ma se ha anche prenotato postazione, è in ufficio
        const swThatDay=reqs.filter(r=>r.smart_date===ds&&r.status==="approved"&&r.type!=="desk"&&(!r.half_day||r.half_day===slot)&&!deskBookerIds.has(r.user_id)).map(r=>r.user_id);

        // Calcola postazioni occupate per la fascia oraria selezionata
        // Una persona occupa la fascia se: full-day OPPURE half_day corrisponde alla fascia
        // Persone fisse in ufficio oggi (non in SW, non in ferie)
        const allFixedInOffice=team.filter(u=>isFixed(u)&&!isFullRemote(u)&&!swThatDay.includes(u.id)&&!u.vacation);
        const fixedInOfficeIds=new Set(allFixedInOffice.map(u=>u.id));
        const frInSwToday=new Set(reqs.filter(r=>r.smart_date===ds&&r.status==="approved"&&r.type!=="desk"&&(!r.half_day||r.half_day===slot)).map(r=>r.user_id));
        // Prenotazioni scrivania libera da non-fissi che non sono in SW
        const bookedSlot=deskReqs.filter(r=>(!r.half_day||r.half_day===slot)&&!fixedInOfficeIds.has(r.user_id)&&!frInSwToday.has(r.user_id)).length;
        // Conta persone fissi in ufficio nella fascia (non postazioni assegnate, più robusto)
        const fixedInOfficeSlot=allFixedInOffice.filter(u=>{
          if(slot==="full") return true;
          const uSw=reqs.find(r=>r.user_id===u.id&&r.smart_date===ds&&r.status==="approved"&&r.type!=="desk");
          if(!uSw) return true; // non in SW → in ufficio tutto il giorno
          return uSw.half_day && uSw.half_day!==slot; // in SW mezza giornata → libero nell'altra fascia
        }).length;
        const freeCount=Math.max(0,totalD-fixedInOfficeSlot-bookedSlot);

        // Chi è in ufficio per fascia
        const normRoom=r=>{ 
          if(!r) return "Sala Coworking"; 
          const t=r.trim().toLowerCase();
          if(t.includes("coworking")||t.includes("co-working")) return "Sala Coworking";
          if(t.includes("call")) return "Sala Call";
          if(t.includes("ufficio")||t.includes("office")) return "Ufficio";
          return r.trim().charAt(0).toUpperCase()+r.trim().slice(1);
        };
        const allFixed=team.filter(u=>isFixed(u)&&!isFullRemote(u)&&!swThatDay.includes(u.id)&&!u.vacation);
        const inOfficeByRoom={};
        const desksByRoom={};
        allDesksMod.forEach(d=>{ const r=normRoom(d.room); if(!desksByRoom[r])desksByRoom[r]=0; desksByRoom[r]++; });
        allFixed.forEach(u=>{
          const myDesk=allDesksMod.find(d=>d.assigned_to&&(d.assigned_to===u.name||d.assigned_to===u.email||d.assigned_to===u.id));
          const room=normRoom(myDesk?.room);
          if(!inOfficeByRoom[room])inOfficeByRoom[room]=[];
          inOfficeByRoom[room].push(u);
        });
        // Aggiungi FR/collab con prenotazione desk alla loro stanza
        deskReqs.filter(r=>(!r.half_day||r.half_day===slot)).forEach(r=>{
          const u=team.find(t=>t.id===r.user_id);
          if(!u||fixedInOfficeIds.has(u.id)) return; // skip fissi (già aggiunti)
          if(frInSwToday.has(u.id)) return; // in SW → non in ufficio
          // trova la postazione prenotata per determinare la stanza
          const bookedDesk=allDesksMod.find(d=>!d.assigned_to&&reqs.some(rq=>rq.id===r.id)); // scrivania libera
          const room=normRoom(null); // scrivania libera → Sala Coworking di default
          if(!inOfficeByRoom[room])inOfficeByRoom[room]=[];
          if(!inOfficeByRoom[room].find(x=>x.id===u.id)) inOfficeByRoom[room].push(u);
        });

        // Prenotazioni visibili per la fascia (full-day + chi è nella fascia)
        const fixedIds=new Set(allFixed.map(u=>u.id));
        // FR users in ufficio = solo se hanno una prenotazione DESK (SW = lavorano da casa)
        const frInOffice=team.filter(u=>isFullRemote(u)&&
          deskReqs.some(r=>r.user_id===u.id&&(!r.half_day||r.half_day===slot))
        );
        const bookedUsers=[
          ...deskReqs
            .filter(r=>!r.half_day||r.half_day===slot)
            .map(r=>{const u=team.find(t=>t.id===r.user_id);return{u,half:r.half_day};})
            .filter(x=>x.u&&!fixedIds.has(x.u.id)&&!isFullRemote(x.u)),
          ...frInOffice.map(u=>{
            const dr=deskReqs.find(r=>r.user_id===u.id);
            const sr=reqs.find(r=>r.user_id===u.id&&r.smart_date===ds&&r.status==="approved"&&r.type!=="desk");
            return{u,half:(dr||sr)?.half_day};
          })
        ].filter((x,i,arr)=>arr.findIndex(y=>y.u?.id===x.u?.id)===i);

        const overflowRooms=Object.entries(inOfficeByRoom).filter(([room,users])=>desksByRoom[room]&&users.length>desksByRoom[room]).map(([room])=>room);
        const vacPeopleModal=team.filter(u=>{
          const from=(u.vacation_from||"").substring(0,10);
          const to=(u.vacation_to||"").substring(0,10);
          return u.vacation&&(!from||!to||(ds>=from&&ds<=to)||(!from&&!to));
        });

        const isMob=window.innerWidth<=600;
        return(
          <div onClick={()=>setDeskDayModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9998,display:"flex",alignItems:isMob?"flex-end":"center",justifyContent:"center",padding:isMob?0:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"white",borderRadius:isMob?"18px 18px 0 0":"18px",padding:isMob?"20px 16px":"24px 22px",paddingBottom:isMob?"calc(20px + env(safe-area-inset-bottom))":undefined,width:"100%",maxWidth:isMob?"100%":"460px",boxShadow:isMob?"0 -8px 40px rgba(0,0,0,0.2)":"0 20px 60px rgba(0,0,0,0.25)",maxHeight:isMob?"88dvh":"88vh",overflowY:"auto"}}>

              {/* Header */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <div>
                  <div style={{fontSize:15,fontWeight:800}}>🪑 Postazioni — {label}</div>
                  <div style={{fontSize:11,color:"#888",marginTop:2}}>{freeCount} libere su {totalD} ({slot==="morning"?"mattina":"pomeriggio"})</div>
                </div>
                <button onClick={()=>setDeskDayModal(null)} style={{background:"#F5F5F5",border:"none",borderRadius:8,width:28,height:28,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
              </div>

              {/* Toggle mattina/pomeriggio */}
              <div style={{display:"flex",background:"#F0F0F0",borderRadius:10,padding:3,marginBottom:16,gap:3}}>
                {[["morning","🌅 Mattina"],["afternoon","🌇 Pomeriggio"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setDeskTimeSlot(v)} style={{flex:1,padding:"7px 0",borderRadius:8,border:"none",background:slot===v?"white":"transparent",color:slot===v?BLK:SUB,fontSize:12,fontWeight:slot===v?700:500,cursor:"pointer",boxShadow:slot===v?"0 1px 4px rgba(0,0,0,.1)":"none",transition:"all .15s"}}>{l}</button>
                ))}
              </div>

              {/* Disponibilità fascia */}
              <div style={{background:freeCount>0?"#F0F8F0":"#FEF2F2",border:"1px solid "+(freeCount>0?"#A5D6A7":"#FECACA"),borderRadius:10,padding:"8px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:20}}>{freeCount>0?"✅":"❌"}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:freeCount>0?"#1A7A3A":"#DC2626"}}>{freeCount>0?`${freeCount} postazion${freeCount===1?"e":"i"} liber${freeCount===1?"a":"e"}`:"Nessuna postazione libera"}</div>
                  <div style={{fontSize:10,color:"#888"}}>{slot==="morning"?"Fascia mattutina":"Fascia pomeridiana"}</div>
                </div>
              </div>

              {/* Alert overflow */}
              {overflowRooms.length>0&&<div style={{background:"#FEF3C7",border:"1px solid #FCD34D",borderRadius:10,padding:"8px 12px",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:16}}>⚠️</span>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:"#92400E"}}>Capienza superata</div>
                  <div style={{fontSize:11,color:"#B45309"}}>{overflowRooms.join(", ")} — più persone che postazioni</div>
                </div>
              </div>}

              {/* Chi è in ufficio per zona */}
              {Object.keys(inOfficeByRoom).length>0&&<div style={{marginBottom:14}}>
                <div style={{fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>🏢 In ufficio</div>
                {Object.entries(inOfficeByRoom).map(([room,users])=>{
                  const cap=desksByRoom[room];
                  const over=cap&&users.length>cap;
                  return(
                  <div key={room} style={{marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                      <div style={{fontSize:10,fontWeight:700,color:over?"#92400E":"#555",textTransform:"uppercase",letterSpacing:.7}}>{room}</div>
                      <div style={{fontSize:10,color:over?"#EF4444":"#AAA"}}>{users.length}{cap?"/"+cap:""}</div>
                      {over&&<span style={{fontSize:10,background:"#FEE2E2",color:"#DC2626",borderRadius:4,padding:"0 5px",fontWeight:700}}>PIENO</span>}
                    </div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                      {users.map(u=>{
                        const hl=u.desk_slot||"full";
                        const inThisSlot=hl==="full"||hl===slot;
                        if(!inThisSlot) return null;
                        return(
                        <div key={u.id} style={{display:"flex",alignItems:"center",gap:5,background:over?"#FEF2F2":"#F0F8FF",border:"1px solid "+(over?"#FECACA":"#BFDBFE"),borderRadius:8,padding:"4px 8px"}}>
                          <div style={{width:20,height:20,borderRadius:"50%",background:u.color||"#AAA",display:"flex",alignItems:"center",justifyContent:"center",fontSize:7,fontWeight:700,color:"white",flexShrink:0}}>{u.avatar_initials}</div>
                          <span style={{fontSize:11,fontWeight:600}}>{shortName(u.name)}</span>
                          {hl!=="full"&&<span style={{background:"#F3E8FF",border:"1px solid #C084FC",color:"#7C3AED",borderRadius:3,padding:"0 3px",fontSize:8}}>{hl==="morning"?"🌅":"🌇"}</span>}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>}

              {/* Postazioni prenotate nella fascia */}
              {bookedUsers.length>0&&<div>
                <div style={{fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>🪑 Postazioni prenotate</div>
                {bookedUsers.map(({u,half},i)=>{
                  const req=deskReqs.find(r=>r.user_id===u.id&&(!r.half_day||r.half_day===slot));
                  const isEditing=editBooking?.id===req?.id;
                  return(
                  <div key={i} style={{marginBottom:8,padding:"7px 10px",background:"#F8F8F8",borderRadius:9}}>
                    <div style={{display:"flex",alignItems:"center",gap:9}}>
                      <div style={{width:28,height:28,borderRadius:"50%",background:u.color||"#AAA",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"white",flexShrink:0}}>{u.avatar_initials}</div>
                      <span style={{fontSize:13,fontWeight:600,flex:1}}>{shortName(u.name)}</span>
                      {!isEditing&&half&&<span style={{background:"#F3E8FF",border:"1px solid #C084FC",color:"#7C3AED",borderRadius:4,padding:"1px 6px",fontSize:10}}>{half==="morning"?"🌅 Mattina":"🌇 Pomeriggio"}</span>}
                      {!isEditing&&!half&&<span style={{background:"#E0E0E0",color:"#555",borderRadius:4,padding:"1px 6px",fontSize:10}}>Tutto il giorno</span>}
                      {req&&!isEditing&&<button onClick={()=>setEditBooking({id:req.id,half_day:req.half_day||"",name:u.name})} style={{background:"#EEF2FF",border:"none",borderRadius:6,padding:"3px 8px",fontSize:11,color:"#4F46E5",cursor:"pointer",fontWeight:600}}>✏️</button>}
                      {req&&!isEditing&&<button onClick={()=>delBooking(req.id)} style={{background:"#FEE8E8",border:"none",borderRadius:6,padding:"3px 8px",fontSize:11,color:"#C0392B",cursor:"pointer",fontWeight:600}}>✕</button>}
                    </div>
                    {isEditing&&<div style={{marginTop:8,display:"flex",gap:6,alignItems:"center"}}>
                      <select value={editBooking.half_day} onChange={e=>setEditBooking(b=>({...b,half_day:e.target.value}))} style={{flex:1,fontSize:12,padding:"5px 8px",borderRadius:7,border:"1px solid #DDD"}}>
                        <option value="">Tutto il giorno</option>
                        <option value="morning">🌅 Mattina</option>
                        <option value="afternoon">🌇 Pomeriggio</option>
                      </select>
                      <button onClick={()=>saveEditBooking(editBooking.id,editBooking.half_day)} style={{background:"#1A7A3A",color:"white",border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Salva</button>
                      <button onClick={()=>setEditBooking(null)} style={{background:"#F0F0F0",border:"none",borderRadius:7,padding:"5px 10px",fontSize:12,cursor:"pointer"}}>✕</button>
                    </div>}
                  </div>
                  );
                })}
              </div>}

              {/* In ferie */}
              {vacPeopleModal.length>0&&<div style={{marginTop:14}}>
                <div style={{fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>🌴 In ferie</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {vacPeopleModal.map(u=>(
                    <div key={u.id} style={{display:"flex",alignItems:"center",gap:5,background:"#F1F8F2",border:"1px solid #A5D6A7",borderRadius:8,padding:"4px 8px"}}>
                      <div style={{width:20,height:20,borderRadius:"50%",background:u.color||LYB,display:"flex",alignItems:"center",justifyContent:"center",fontSize:7,fontWeight:700,color:"white",flexShrink:0}}>{u.avatar_initials}</div>
                      <span style={{fontSize:11,fontWeight:600,color:"#2E7D32"}}>{shortName(u.name)}</span>
                    </div>
                  ))}
                </div>
              </div>}

            </div>
          </div>
        );
      })()}


      {/* Toast */}
      {toast && <div className="tel" style={{position:"fixed",bottom:20,right:20,background:toast.ok?BLK:"#C0392B",color:WHT,padding:"11px 17px",borderRadius:11,zIndex:9999,fontSize:13,fontWeight:600,boxShadow:"0 5px 20px rgba(0,0,0,.17)",maxWidth:400}}>{toast.msg}</div>}

      {/* Sim banner */}
      {simulating && <div className="sim-bar">
        <span>👁 Visualizzando come:</span>
        <div className="av" style={{background:cu?.color||LYB,width:24,height:24,fontSize:10}}>{cu?.avatar_initials}</div>
        <strong>{cu?.name}</strong>
        <span style={{color:SUB}}>— azioni disabilitate</span>
        <button className="bp" style={{marginLeft:"auto",padding:"5px 12px",fontSize:12}} onClick={()=>{setViewAs(null);setTab("dashboard");}}>← Admin</button>
      </div>}

      {/* Mobile topbar */}
      <div className="mob-bar">
        <button style={{background:"none",border:"none",cursor:"pointer",padding:4}} onClick={()=>setMO(o=>!o)}>
          <svg width="22" height="16" viewBox="0 0 22 16"><rect y="0" width="22" height="2" rx="1" fill={BLK}/><rect y="7" width="22" height="2" rx="1" fill={BLK}/><rect y="14" width="22" height="2" rx="1" fill={BLK}/></svg>
        </button>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{background:BLK,borderRadius:8,width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
            {logoReady && logoUrl ? <img src={logoUrl} alt="logo" style={{width:30,height:30,objectFit:"cover",borderRadius:8}}/> : <LYLogo size={22} light/>}
          </div>
          <span style={{fontFamily:"'Space Mono',monospace",fontSize:11,fontWeight:700,letterSpacing:2}}>LIKEYOU SW</span>
        </div>
        <div className="av" onClick={()=>!simulating&&setProfileModal(true)} style={{background:cu?.color||LYB,width:30,height:30,fontSize:10,cursor:simulating?"default":"pointer"}}>{cu?.avatar_initials}</div>
        {/* Campanella notifiche mob-bar */}
        <div style={{position:"relative"}}>
          <button onClick={()=>{setNotifOpen(o=>!o); if(!notifOpen) markNotifsRead();}} style={{background:"none",border:"none",cursor:"pointer",padding:4,fontSize:20,lineHeight:1,position:"relative"}}>
            🔔
            {inAppNotifs.filter(n=>!n.read).length>0&&<span style={{position:"absolute",top:0,right:0,background:"#C0392B",color:"white",borderRadius:"50%",width:14,height:14,fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>{inAppNotifs.filter(n=>!n.read).length}</span>}
          </button>
          {notifOpen&&<div ref={notifPopupRef} style={{position:"fixed",top:56,right:8,width:320,background:darkMode?"#1E1E1E":"white",border:"1px solid "+BDR,borderRadius:14,boxShadow:"0 8px 40px rgba(0,0,0,.18)",zIndex:9999,maxHeight:"70vh",display:"flex",flexDirection:"column"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderBottom:"1px solid "+BDR,flexShrink:0}}>
              <span style={{fontSize:13,fontWeight:700,color:BLK}}>🔔 Notifiche</span>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {inAppNotifs.length>0&&<button onClick={()=>{setInAppNotifs([]);supabase.from("user_notifications").delete().eq("user_id",realUser.id);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:SUB}}>Cancella tutte</button>}
                <button onClick={()=>setNotifOpen(false)} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:"#AAA",lineHeight:1}}>✕</button>
              </div>
            </div>
            <div style={{overflowY:"auto",flex:1,WebkitOverflowScrolling:"touch"}}>
              {inAppNotifs.length===0&&<div style={{padding:"24px 16px",textAlign:"center",fontSize:12,color:SUB}}>Nessuna notifica</div>}
              {inAppNotifs.map(n=>{
                const goTo = n.type==="new_sw_request"||n.type==="cancel_approved"||n.type==="cancel_rejected"?"_admin"
                  :n.type==="sw_approved"||n.type==="sw_rejected"?"request"
                  :n.type==="swap_request"?"swap"
                  :n.type==="desk_deleted"?"desk_booking"
                  :n.type==="birthday"||n.type==="birthday_self"?"dashboard"
                  :null;
                const navigate = goTo ? ()=>{
                  if(goTo==="_admin"){setTab("admin");setTimeout(()=>setATab("requests"),50);}
                  else setTab(goTo);
                  setNotifOpen(false); setMO(false);
                } : null;
                return(
                <div key={n.id} style={{display:"flex",alignItems:"flex-start",gap:10,borderBottom:"1px solid #F7F7F7",background:n.read?(darkMode?"#1E1E1E":"white"):(darkMode?"#2A2400":"#FFFBF0")}}>
                  <button onClick={navigate||undefined} style={{flex:1,background:"none",border:"none",textAlign:"left",padding:"11px 0 11px 16px",cursor:navigate?"pointer":"default",fontFamily:"'DM Sans',sans-serif"}}>
                    <div style={{fontSize:12,fontWeight:n.read?500:700,color:BLK,marginBottom:2}}>{n.title}</div>
                    {n.body&&<div style={{fontSize:11,color:SUB,lineHeight:1.4}}>{n.body}</div>}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:4}}>
                      <span style={{fontSize:10,color:"#CCC"}}>{new Date(n.created_at).toLocaleDateString("it-IT",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</span>
                      {navigate&&<span style={{fontSize:10,color:LYB,fontWeight:600}}>Vai →</span>}
                    </div>
                  </button>
                  <button onClick={()=>deleteNotif(n.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:"#CCC",flexShrink:0,padding:"11px 14px 11px 4px",alignSelf:"flex-start"}}>✕</button>
                </div>
                );
              })}
            </div>
          </div>}
        </div>
        <button onClick={toggleDark} title={darkMode?"Modalità chiara":"Modalità scura"} style={{background:"none",border:"none",cursor:"pointer",padding:4,fontSize:18,lineHeight:1}}>{darkMode?"☀️":"🌙"}</button>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden",position:"relative",maxWidth:"100vw",minHeight:0}}>
        {/* Overlay mobile — chiude la sidebar toccando fuori */}
        {mobileOpen && <div onClick={()=>setMO(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",zIndex:199}}/>}

        {/* ═══ SIDEBAR ═══ */}
        <div className={"sidebar"+(mobileOpen?" open":"")}>
          <div style={{padding:"4px 6px 16px",borderBottom:"1px solid "+BDR,display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <div style={{background:BLK,borderRadius:11,width:40,height:40,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden",flexShrink:0}}>
              {logoReady && logoUrl ? <img src={logoUrl} alt="logo" style={{width:40,height:40,objectFit:"cover",borderRadius:11}}/> : <LYLogo size={28} light/>}
            </div>
            <div><div style={{fontFamily:"'Space Mono',monospace",fontSize:10,fontWeight:700,letterSpacing:2}}>LIKEYOU SW</div><div style={{fontSize:9,color:SUB,marginTop:1}}>Smart Working</div></div>
          </div>
          <nav style={{display:"flex",flexDirection:"column",gap:1,marginTop:12,flex:1}}>
            {nav.map(n=>(
              <button key={n.id} className={`nb${tab===n.id?" act":""}${n.adm?" adm":""}`} onClick={()=>{
                setTab(n.id);
                setMO(false);
                // Segna messaggi come letti quando l'utente apre Comunicazioni
                if(n.id==="messages" && !isAdmin) {
                  setMessages(prev=>prev.map(m=>m.user_id===cu.id&&m.admin_reply?{...m,_read:true}:m));
                }
              }}>
                <span style={{width:6,height:6,borderRadius:"50%",background:tab===n.id?"white":n.adm?"#C09000":SUB,flexShrink:0}}/>
                <span style={{flex:1}}>{n.label}</span>
                {n.badge>0&&<span className="nb-badge">{n.badge}</span>}
              </button>
            ))}
          </nav>
          {myCanSW&&(()=>{
            const y=today.getFullYear(); const m=today.getMonth();
            const swThisWeek=hasSwInWeek(cu.id, todayStr);
            const mLimitOk = myMU < myUR.days_per_month;
            const pctAnno  = Math.min((myYU/myUR.days_per_year)*100, 100);
            const pctMese  = Math.min((myMU/myUR.days_per_month)*100, 100);
            return(
            <div style={{background:"#F7F7F7",border:"1px solid "+BDR,borderRadius:11,padding:12,margin:"10px 0"}}>
              <div style={{fontSize:9,color:SUB,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>📊 I tuoi giorni SW</div>

              {/* Budget annuo — sempre visibile per intero */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:3}}>
                <span style={{fontSize:11,color:SUB}}>Budget annuo</span>
                <span style={{fontSize:15,fontWeight:700,color:myAv>0?BLK:"#C0392B",fontFamily:"'Space Mono',monospace"}}>{myYU}<span style={{fontSize:10,fontWeight:400,color:SUB}}>/{myUR.days_per_year}</span></span>
              </div>
              <PBar pct={pctAnno}/>
              <div style={{fontSize:9,color:"#AAA",marginTop:2,marginBottom:10}}>{myAv} rimanenti su {myUR.days_per_year} totali/anno</div>

              {/* Limite mensile */}
              <div style={{padding:"7px 9px",background:"#FFF",borderRadius:8,border:"1px solid "+(mLimitOk?BDR:"#F5C0C0"),marginBottom:7}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:10,color:SUB}}>Questo mese</span>
                  <span style={{fontSize:12,fontWeight:700,color:mLimitOk?BLK:"#C0392B"}}>{myMU}/{myUR.days_per_month} usati</span>
                </div>
                <PBar pct={pctMese} col={pctMese>=100?"#C0392B":LYB}/>
                <div style={{fontSize:9,color:mLimitOk?"#AAA":"#C0392B",marginTop:3}}>
                  {mLimitOk?`ancora ${myUR.days_per_month-myMU} disponibili questo mese`:"Limite mensile raggiunto"}
                </div>
              </div>

              {/* Regola settimana */}
              <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:7,
                background:swThisWeek?"#FFF3CD":"#F0FFF4",
                border:"1px solid "+(swThisWeek?"#F0C040":"#C8E6C9"),marginBottom:7}}>
                <span style={{fontSize:11}}>{swThisWeek?"⚠️":"✅"}</span>
                <span style={{fontSize:10,fontWeight:600,color:swThisWeek?"#856404":"#2E7D32"}}>
                  {swThisWeek?"SW già usato questa settimana":"Settimana libera"}
                </span>
              </div>

              {/* Emergenza */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:myExcCount>0?7:0}}>
                <span style={{fontSize:10,color:SUB}}>🚨 Emergenza</span>
                <span style={{fontSize:12,fontWeight:700,color:myEmU<myUR.emergency_days?"#C05A00":"#C0392B"}}>{myEmU}/{myUR.emergency_days}</span>
              </div>

              {/* Eccezioni — solo se ce ne sono */}
              {myExcCount>0&&<div style={{padding:"7px 9px",background:"#FFFBF0",borderRadius:8,border:"1px solid #E0C060",marginTop:4}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:10,color:"#856404",fontWeight:600}}>⚡ Eccezioni</span>
                  <span style={{fontSize:12,fontWeight:700,color:"#856404"}}>{myExcCount} quest'anno</span>
                </div>
                <div style={{fontSize:9,color:"#A07820"}}>Giorni extra autorizzati dall'admin — non scalano dal budget</div>
              </div>}
            </div>
            );
          })()}
          {isAdmin&&<div style={{marginBottom:10}}>
            <Lbl t="Visualizza come"/>
            <select value="" onChange={e=>{const u=team.find(t=>t.id===e.target.value);if(u){setViewAs({...u});setTab("dashboard");setMO(false);}}} style={{fontSize:11,padding:"5px 8px"}}>
              <option value="">— Seleziona utente —</option>
              {team.filter(u=>u.id!==realUser.id).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>}
          <div style={{borderTop:"1px solid "+BDR,paddingTop:12}}>
            {/* Campanella notifiche in sidebar */}
            <div style={{position:"relative",marginBottom:8}}>
              <button onClick={()=>{setNotifOpen(o=>!o); if(!notifOpen) setTimeout(markNotifsRead,300);}} style={{width:"100%",display:"flex",alignItems:"center",gap:9,background:"#F5F5F5",border:"1px solid "+BDR,color:BLK,borderRadius:9,padding:"7px 10px",fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:500}}>
                <span style={{position:"relative",fontSize:16}}>🔔{inAppNotifs.filter(n=>!n.read).length>0&&<span style={{position:"absolute",top:-4,right:-4,background:"#C0392B",color:"white",borderRadius:"50%",width:14,height:14,fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>{inAppNotifs.filter(n=>!n.read).length}</span>}</span>
                <span style={{flex:1,textAlign:"left"}}>Notifiche</span>
                {inAppNotifs.filter(n=>!n.read).length>0&&<span style={{background:"#C0392B",color:"white",borderRadius:10,padding:"1px 7px",fontSize:10,fontWeight:700}}>{inAppNotifs.filter(n=>!n.read).length}</span>}
              </button>
              {notifOpen&&<div ref={notifPopupRef} style={{position:"absolute",bottom:"calc(100% + 6px)",left:0,right:0,background:"white",border:"1px solid "+BDR,borderRadius:12,boxShadow:"0 8px 30px rgba(0,0,0,.15)",zIndex:999,maxHeight:360,overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderBottom:"1px solid #F0F0F0"}}>
                  <span style={{fontSize:12,fontWeight:700}}>Notifiche</span>
                  <button onClick={()=>setNotifOpen(false)} style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:"#AAA"}}>✕</button>
                </div>
                {inAppNotifs.length===0&&<div style={{padding:"20px 14px",textAlign:"center",fontSize:12,color:SUB}}>Nessuna notifica</div>}
                {inAppNotifs.map(n=>{
                  const goTo=n.type==="new_sw_request"||n.type==="cancel_approved"||n.type==="cancel_rejected"?"_admin"
                    :n.type==="sw_approved"||n.type==="sw_rejected"?"request"
                    :n.type==="swap_request"?"swap"
                    :n.type==="desk_deleted"?"desk_booking"
                    :n.type==="birthday"||n.type==="birthday_self"?"dashboard":null;
                  const navigate=goTo?()=>{
                    if(goTo==="_admin"){setTab("admin");setTimeout(()=>setATab("requests"),50);}
                    else setTab(goTo);
                    setNotifOpen(false);setMO(false);
                  }:null;
                  return(
                  <div key={n.id} style={{display:"flex",alignItems:"flex-start",borderBottom:"1px solid #F7F7F7",background:n.read?"white":"#FFFBF0"}}>
                    <button onClick={navigate||undefined} style={{flex:1,background:"none",border:"none",textAlign:"left",padding:"10px 0 10px 14px",cursor:navigate?"pointer":"default",fontFamily:"'DM Sans',sans-serif"}}>
                      <div style={{fontSize:12,fontWeight:n.read?500:700,marginBottom:2}}>{n.title}</div>
                      {n.body&&<div style={{fontSize:11,color:SUB,lineHeight:1.4}}>{n.body}</div>}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:3}}>
                        <span style={{fontSize:10,color:"#CCC"}}>{new Date(n.created_at).toLocaleDateString("it-IT",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</span>
                        {navigate&&<span style={{fontSize:10,color:LYB,fontWeight:600}}>Vai →</span>}
                      </div>
                    </button>
                    <button onClick={()=>deleteNotif(n.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:"#CCC",flexShrink:0,padding:"10px 12px 10px 4px",alignSelf:"flex-start"}}>✕</button>
                  </div>
                  );
                })}
                {inAppNotifs.length>0&&<div style={{padding:"8px 14px",borderTop:"1px solid #F0F0F0"}}>
                  <button onClick={()=>{setInAppNotifs([]);supabase.from("user_notifications").delete().eq("user_id",realUser.id);}} style={{width:"100%",background:"none",border:"none",cursor:"pointer",fontSize:11,color:SUB}}>Cancella tutte</button>
                </div>}
              </div>}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:8}}>
              <div className="av" style={{background:cu?.color||LYB,cursor:"pointer",position:"relative"}} onClick={()=>!simulating&&setProfileModal(true)}>
                {cu?.vacation&&<span style={{position:"absolute",top:-2,right:-2,fontSize:10}}>🌴</span>}
                {cu?.avatar_initials}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cu?.name}</div>
                <div style={{fontSize:10,color:isAdmin?"#C09000":isFullRemote(cu)?"#1A7A3A":myCanSW?LYB:SUB}}>{isAdmin?"Amministratore":isFullRemote(cu)?"Full Remote":myCanSW?"Team fisso":"Collaboratore"}</div>
                {cu?.sw_location&&<div style={{fontSize:9,color:SUB,marginTop:1}}>📍{cu.sw_location}</div>}
              </div>
            </div>
            {!simulating&&<button onClick={()=>setProfileModal(true)} style={{width:"100%",background:"#F5F5F5",border:"1px solid "+BDR,color:BLK,borderRadius:9,padding:7,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",marginBottom:6,fontWeight:500}}>👤 Il mio profilo</button>}
            {!simulating&&myCanSW&&<button onClick={()=>setGcalModal(true)} style={{width:"100%",background:"#F0F5FF",border:"1px solid #C0D0F0",color:"#2040A0",borderRadius:9,padding:7,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",marginBottom:6,fontWeight:500}}>📅 Google Calendar</button>}
            <button onClick={toggleDark} style={{width:"100%",background:darkMode?"#252525":"#F5F5F5",border:"1px solid "+BDR,color:SUB,borderRadius:9,padding:7,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",marginBottom:6}}>{darkMode?"☀️ Modalità chiara":"🌙 Modalità scura"}</button>
            <button onClick={logout} style={{width:"100%",background:"#F5F5F5",border:"1px solid "+BDR,color:SUB,borderRadius:9,padding:7,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Disconnetti</button>
          </div>
        </div>

        {/* ═══ MAIN ═══ */}
        <div className="main-pad main-scroll" style={{flex:1,overflowY:"scroll",overflowX:"hidden",padding:"24px 28px",WebkitOverflowScrolling:"touch",position:"relative",minWidth:0,boxSizing:"border-box"}}>

          {/* ══ DASHBOARD ══ */}
          {tab==="dashboard"&&<div className="page">
            <div style={{marginBottom:16}}><h1 style={{fontSize:21,fontWeight:700}}>{(()=>{const h=new Date().getHours();return h<12?"Buongiorno":h<18?"Buon pomeriggio":"Buonasera";})()}, {cu?.name?.split(" ")[0]}{isAdmin?" 👑":""}</h1><p style={{color:SUB,fontSize:13,marginTop:3}}>{DAY[today.getDay()]} {today.getDate()} {MON[today.getMonth()]} {today.getFullYear()}</p></div>

            {/* Banner countdown compleanno utente loggato */}
            {(()=>{
              if(!cu?.birthday) return null;
              const nowD=new Date(today.getFullYear(),today.getMonth(),today.getDate());
              const [,mm,dd]=cu.birthday.split("-");
              let bday=new Date(today.getFullYear(),+mm-1,+dd);
              if(bday<nowD) bday=new Date(today.getFullYear()+1,+mm-1,+dd);
              const diff=Math.round((bday-nowD)/86400000);
              if(diff===0) return null; // già mostrato dal banner "oggi"
              if(diff>30) return null; // mostra solo entro 30 giorni
              return(
                <div style={{background:"linear-gradient(135deg,#F0F8FF,#DCF0FF)",border:"1px solid #90C8F0",borderRadius:13,padding:"10px 18px",marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:22}}>🎁</span>
                  <div style={{fontSize:13,color:"#1A5F8A"}}>
                    {diff===1
                      ? <><strong>Domani è il tuo compleanno!</strong> 🎉</>
                      : <><strong>Mancano {diff} giorni</strong> al tuo compleanno!</>
                    }
                  </div>
                </div>
              );
            })()}

            {/* Banner compleanno oggi */}
            {(()=>{const bday=team.filter(u=>u.birthday&&u.birthday.slice(5)===todayStr.slice(5));if(!bday.length)return null;return(<div style={{background:"linear-gradient(135deg,#FFF8DC,#FFECAA)",border:"1px solid #F0C840",borderRadius:13,padding:"12px 18px",marginBottom:12,display:"flex",alignItems:"center",gap:12}}><span style={{fontSize:26}}>🎂</span><div><div style={{fontSize:13,fontWeight:700,color:"#856404"}}>Oggi è il compleanno di {bday.map(u=>u.name.split(" ")[0]).join(" e ")}! 🎉</div><div style={{fontSize:11,color:"#A07820"}}>Fai gli auguri!</div></div></div>);})()}

            {/* Banner ferie proprie */}
            {cu?.vacation&&<div style={{background:"#E8F5E9",border:"1px solid #A5D6A7",borderRadius:13,padding:"10px 18px",marginBottom:12,display:"flex",alignItems:"center",gap:10}}><span style={{fontSize:20}}>🌴</span><div style={{fontSize:13,fontWeight:600,color:"#2E7D32"}}>Sei in ferie — buone vacanze!</div></div>}

            <div className="g4" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:13,marginBottom:14}}>
              {SC("In SW oggi",`${smartToday}`,"approvati",LYB)}
              {SC("Postazioni libere",`${freeDesks}`,"non assegnate")}
              {SC(myCanSW?"Gg disponibili":"In attesa",myCanSW?`${myAv}`:`${pendCnt}`,myCanSW?"ordinari":"richieste")}
              {SC("Giorni bloccati",`${blocked.length}`,"non prenotabili","#C0392B")}
            </div>

            {/* Box Dove siamo oggi / domani / venerdì */}
            {(()=>{
              const isWeekendToday=today.getDay()===0||today.getDay()===6;
              if(isWeekendToday) return null;
              const allFixed = team.filter(u=>isFixed(u));
              // Calcola prossimi giorni lavorativi (oggi + 2)
              const nextWorkDays = [];
              let d = new Date(today);
              while(nextWorkDays.length < 3) {
                const dow = d.getDay();
                if(dow!==0 && dow!==6) nextWorkDays.push(new Date(d));
                d.setDate(d.getDate()+1);
                if(nextWorkDays.length===0) break;
                if(nextWorkDays.length===3) break;
                // avoid infinite loop
                if(d - today > 14*86400000) break;
              }
              // Ricalcola correttamente
              const workDays = [];
              const tmp = new Date(today);
              while(workDays.length < 3) {
                const dow = tmp.getDay();
                if(dow!==0 && dow!==6) workDays.push(new Date(tmp));
                tmp.setDate(tmp.getDate()+1);
                if(workDays.length===3) break;
                if(tmp - today > 20*86400000) break;
              }
              const dayLabel = (d, idx) => {
                if(idx===0) return "oggi";
                const dow = d.getDay();
                const names = ["dom","lun","mar","mer","gio","ven","sab"];
                return names[dow]+" "+d.getDate()+"/"+(d.getMonth()+1);
              };
              return(
              <div className="day-slider" style={{marginBottom:14}}>                {workDays.map((wd,idx)=>{
                  const ds=fmtDate(wd);
                  const deskBookersThatDay=new Set(reqs.filter(r=>r.smart_date===ds&&r.type==="desk"&&r.status==="approved").map(r=>r.user_id));
                  // SW quel giorno — ma se uno ha anche prenotato postazione, è in ufficio (desk ha priorità)
                  const inSW=reqs.filter(r=>r.smart_date===ds&&r.status==="approved"&&r.type!=="desk"&&!deskBookersThatDay.has(r.user_id));
                  const inSwIds=new Set(inSW.map(r=>r.user_id));
                  const inUff=[
                    ...allFixed.filter(u=>!isFullRemote(u)&&!inSwIds.has(u.id)&&!u.vacation),
                    ...team.filter(u=>deskBookersThatDay.has(u.id)&&!inSwIds.has(u.id)&&(isFullRemote(u)||!isFixed(u)))
                  ];
                  const inSwFixed=allFixed.filter(u=>!isFullRemote(u)&&inSwIds.has(u.id));
                  return(
                  <div key={ds}>
                    <div className="card" style={{padding:"10px 12px",marginBottom:8}}>
                      <div style={{fontSize:10,color:idx===0?"#1D4ED8":SUB,textTransform:"uppercase",letterSpacing:.8,marginBottom:8,fontWeight:idx===0?700:400}}>🏢 In ufficio {dayLabel(wd,idx)}</div>
                      {inUff.length===0
                        ?<div style={{color:SUB,fontSize:11}}>—</div>
                        :inUff.map(u=>(
                        <div key={u.id} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                          <div className="av" style={{background:u.color||LYB,width:20,height:20,fontSize:7}}>{u.avatar_initials}</div>
                          <span style={{fontSize:11}}>{shortName(u.name)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="card" style={{padding:"10px 12px"}}>
                      <div style={{fontSize:10,color:idx===0?"#1D4ED8":SUB,textTransform:"uppercase",letterSpacing:.8,marginBottom:8,fontWeight:idx===0?700:400}}>🏠 In SW {dayLabel(wd,idx)}</div>
                      {inSwFixed.length===0
                        ?<div style={{color:SUB,fontSize:11}}>—</div>
                        :inSwFixed.map(u=>(
                        <div key={u.id} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                          <div className="av" style={{background:u.color||LYB,width:20,height:20,fontSize:7}}>{u.avatar_initials}</div>
                          <span style={{fontSize:11,fontWeight:600}}>{u.name?.split(" ")[0]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
              );
            })()}

            {/* Postazioni libere prossimi giorni */}
            {(()=>{
              const allDesks = desks.filter(d=>d.active);
              const totalDesks = allDesks.length;
              if(totalDesks===0) return null;
              const todayD = new Date(today);
              const dow0 = todayD.getDay();
              const diffToMon = dow0===0 ? 1 : (dow0===1 ? 0 : -(dow0-1));
              const monBase = new Date(todayD);
              monBase.setDate(todayD.getDate() + diffToMon + deskWkOff*7);
              const days=[];
              const dayNames=["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
              const isOnVac=(u,ds)=>u.vacation&&(!u.vacation_from||!u.vacation_to||(ds>=(u.vacation_from||"").substring(0,10)&&ds<=(u.vacation_to||"").substring(0,10))||(!u.vacation_from&&!u.vacation_to));
              for(let i=0;i<5;i++){
                const d=new Date(monBase); d.setDate(monBase.getDate()+i);
                const ds=fmtDate(d);
                const isHolidayDay=isItalianHoliday(ds);
                const deskReqs=reqs.filter(r=>r.smart_date===ds&&r.type==="desk"&&r.status==="approved");
                const swThatDs=reqs.filter(r=>r.smart_date===ds&&r.status==="approved"&&r.type!=="desk").map(r=>r.user_id);
                const swThatDsSet=new Set(swThatDs);
                const vacPeople=team.filter(u=>isOnVac(u,ds));
                const vacIds=new Set(vacPeople.map(u=>u.id));
                const fixedInOff=team.filter(u=>isFixed(u)&&!isFullRemote(u)&&!swThatDsSet.has(u.id)&&!vacIds.has(u.id));
                const fixedInOffIds=new Set(fixedInOff.map(u=>u.id));
                // FR/collab che hanno prenotato postazione (vengono in ufficio quel giorno)
                const deskBookerIds=new Set(deskReqs.filter(r=>!fixedInOffIds.has(r.user_id)&&!swThatDsSet.has(r.user_id)).map(r=>r.user_id));
                const bookedNonFixed=deskBookerIds.size;
                // Persone totali in ufficio = fissi + chi ha prenotato postazione
                const peopleInOffice=fixedInOff.length+bookedNonFixed;
                const free=Math.max(0,totalDesks-peopleInOffice);
                const halfAvail=deskReqs.some(r=>r.half_day)&&free>=0;
                const isToday=ds===todayStr;
                const isPast=d<todayD&&!isToday;
                days.push({ds,free,halfAvail,label:dayNames[d.getDay()]+" "+d.getDate()+"/"+(d.getMonth()+1),isToday,isPast,isHolidayDay,vacPeople});
              }
              const weekLabel=deskWkOff===0?"Questa settimana":deskWkOff===1?"Prossima settimana":"+"+deskWkOff+" settimane";
              return(
                <div className="card" style={{padding:"12px 18px",marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                    <div style={{fontSize:10,color:SUB,textTransform:"uppercase",letterSpacing:1}}>🪑 Postazioni libere</div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <button onClick={()=>setDeskWkOff(w=>Math.max(0,w-1))} disabled={deskWkOff===0} style={{width:24,height:24,borderRadius:6,border:"1px solid #DDD",background:deskWkOff===0?"#F5F5F5":"white",cursor:deskWkOff===0?"default":"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",color:deskWkOff===0?"#CCC":"#333"}}>‹</button>
                      <span style={{fontSize:11,color:SUB,minWidth:110,textAlign:"center"}}>{weekLabel}</span>
                      <button onClick={()=>setDeskWkOff(w=>w+1)} style={{width:24,height:24,borderRadius:6,border:"1px solid #DDD",background:"white",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
                    {days.map(({ds,free,halfAvail,label,isToday,isPast,isHolidayDay,vacPeople})=>{
                      const pct=isHolidayDay?0:totalDesks>0?free/totalDesks:0;
                      const col=isHolidayDay?"#C0392B":isPast?"#BBB":pct>0.5?"#1A7A3A":pct>0?"#856404":"#C0392B";
                      const bg=isHolidayDay?"#FFF0F0":isPast?"#F5F5F5":pct>0.5?"#F1F8F2":pct>0?"#FFFBEC":"#FEE8E8";
                      const border=isHolidayDay?"#F5C0C0":isToday?"#93C5FD":isPast?"#E0E0E0":pct>0.5?"#A5D6A7":pct>0?"#F0E090":"#F5C0C0";
                      return(
                        <div key={ds} onClick={()=>!isPast&&!isHolidayDay&&setDeskDayModal({ds,label:isToday?"Oggi":label})} style={{textAlign:"center",background:bg,border:"2px solid "+border,borderRadius:10,padding:"8px 4px",cursor:(isPast||isHolidayDay)?"default":"pointer",transition:"transform .1s"}}>
                          <div style={{fontSize:10,color:isHolidayDay?"#C0392B":isToday?"#1D4ED8":SUB,fontWeight:isToday?700:400,marginBottom:4}}>{isToday?"Oggi":label}</div>
                          {isHolidayDay
                            ?<div style={{fontSize:13}}>🎉</div>
                            :<><div style={{fontSize:18,fontWeight:700,color:col}}>{isPast?"—":free}</div>
                            <div style={{fontSize:9,color:SUB}}>/ {totalDesks}</div>
                            {!isPast&&halfAvail&&<div style={{fontSize:9,color:"#7C3AED",marginTop:2}}>½ libera</div>}
                            {!isPast&&<div style={{fontSize:8,color:"#CCC",marginTop:3}}>tocca per dettagli</div>}</>
                          }
                          {vacPeople.length>0&&<div style={{marginTop:4,display:"flex",flexWrap:"wrap",justifyContent:"center",gap:2}}>
                            {vacPeople.map(u=><div key={u.id} title={shortName(u.name)+" (ferie)"} className="av" style={{background:u.color||LYB,width:14,height:14,fontSize:5}}>🌴</div>)}
                          </div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Prossimi compleanni (30gg) */}
            {(()=>{const now=new Date();const nowD=new Date(now.getFullYear(),now.getMonth(),now.getDate());const yr=nowD.getFullYear();const up=team.filter(u=>u.birthday).map(u=>{const[,mm,dd]=u.birthday.split("-");let d=new Date(yr,+mm-1,+dd);if(d<nowD)d=new Date(yr+1,+mm-1,+dd);const diff=Math.round((d-nowD)/86400000);return{...u,diff};}).filter(u=>u.diff<=90).sort((a,b)=>a.diff-b.diff);if(!up.length)return null;return(<div className="card" style={{padding:"12px 18px",marginBottom:14}}><div style={{fontSize:10,color:SUB,textTransform:"uppercase",letterSpacing:1,marginBottom:9}}>🎂 Prossimi compleanni</div><div style={{display:"flex",flexWrap:"wrap",gap:8}}>{up.map(u=>(<div key={u.id} style={{display:"flex",alignItems:"center",gap:7,background:"#FFFBEC",border:"1px solid #F0E090",borderRadius:9,padding:"4px 10px"}}><div className="av" style={{background:u.color||LYB,width:24,height:24,fontSize:9}}>{u.avatar_initials}</div><div><div style={{fontSize:11,fontWeight:600}}>{shortName(u.name)}</div><div style={{fontSize:10,color:"#856404"}}>{u.diff===0?"Oggi! 🎉":u.diff===1?"Domani":u.diff+"gg"}</div></div></div>))}</div></div>);})()}

            {/* Chi è in ferie */}
            {(()=>{const vacToday=team.filter(u=>{if(!u.vacation||u.id===cu?.id)return false;const f=(u.vacation_from||"").substring(0,10);const t=(u.vacation_to||"").substring(0,10);return(!f&&!t)||(todayStr>=f&&todayStr<=t);});return vacToday.length>0&&<div className="card" style={{padding:"10px 18px",marginBottom:14,borderColor:"#A5D6A7"}}><div style={{fontSize:10,color:SUB,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>🌴 In ferie oggi</div><div style={{display:"flex",flexWrap:"wrap",gap:7}}>{vacToday.map(u=>(<div key={u.id} style={{display:"flex",alignItems:"center",gap:6,background:"#F1F8F2",border:"1px solid #C8E6C9",borderRadius:8,padding:"3px 9px"}}><div className="av" style={{background:u.color||LYB,width:22,height:22,fontSize:8}}>{u.avatar_initials}</div><span style={{fontSize:11}}>{shortName(u.name)}</span></div>))}</div></div>;})()}

            <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <div className="card"><div className="st">In smart working oggi</div>
                {reqs.filter(r=>r.smart_date===todayStr&&r.status==="approved"&&!(r.is_exception&&r.forced_by_admin)).length===0
                  ?<div style={{color:SUB,fontSize:13}}>Nessuno in smart working oggi</div>
                  :reqs.filter(r=>r.smart_date===todayStr&&r.status==="approved"&&!(r.is_exception&&r.forced_by_admin)).map(r=>{const u=team.find(t=>t.id===r.user_id);if(!u)return null;return(<div key={r.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}><div className="av" style={{background:u.color||LYB}}>{u.avatar_initials}</div><div><div style={{fontSize:13,fontWeight:600}}>{u.name}</div>{r.is_emergency&&<span className="tem tag" style={{fontSize:10}}>emergenza</span>}</div></div>);})}
              </div>
              <div className="card"><div className="st">Ultime richieste</div>
                {(isAdmin?reqs:reqs.filter(r=>r.user_id===cu.id)).slice(0,6).map(r=>{const u=team.find(t=>t.id===r.user_id);if(!u&&isAdmin)return null;return(<div key={r.id} style={{display:"flex",alignItems:"center",gap:9,marginBottom:10}}><div className="av" style={{background:u?.color||"#AAA",width:26,height:26,fontSize:9}}>{u?.avatar_initials}</div><div style={{flex:1}}>{isAdmin&&<div style={{fontSize:11,fontWeight:600}}>{u?.name}</div>}<div style={{fontSize:11,color:SUB,fontFamily:"'Space Mono',monospace"}}>{r.smart_date}</div></div><span className={tagCls(r.cancel_requested?"p":r.status)} style={{fontSize:10}}>{r.cancel_requested?"annull.":r.status==="pending"?"attesa":r.status==="approved"?"✓":"✗"}</span></div>);})}
              </div>
            </div>
          </div>}

          {/* ══ GUIDA & REGOLE ══ */}
          {tab==="guide"&&<div className="page">
            <h1 style={{fontSize:21,fontWeight:700,marginBottom:4}}>📖 Guida e Regole</h1>
            <p style={{color:SUB,fontSize:13,marginBottom:20}}>Tutto quello che devi sapere sul portale Smart Working LikeYou.</p>

            <div style={{display:"flex",flexDirection:"column",gap:14}}>

              {/* Regole base */}
              <div className="card">
                <div style={{fontSize:14,fontWeight:700,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>📋</span> Regole del Smart Working
                </div>
                {[
                  ["📅","Budget annuo",`Hai a disposizione ${myUR.days_per_year} giorni di smart working all'anno. I giorni non usati in un mese rimangono nel tuo budget — non si perdono.`],
                  ["📆","Limite mensile",`Puoi usare al massimo ${myUR.days_per_month} giorni di SW al mese. Superato il limite mensile, dovrai aspettare il mese successivo.`],
                  ["📅","Regola settimanale","Puoi fare smart working massimo 1 volta a settimana (lunedì–domenica). Non è possibile prenotare due SW nella stessa settimana."],
                  ["⏰","Anticipo minimo",`Le richieste ordinarie devono essere inviate con almeno ${rules.advance_days} giorni di anticipo. Le emergenze richiedono minimo 2 giorni.`],
                  ["🏠","Sede obbligatoria","Ogni richiesta deve indicare dove lavorerai (es. Casa, Coworking Bologna...). La sede è visibile solo all'amministratore, non ai colleghi."],
                  ["👥","Limite giornaliero",`Al massimo ${rules.max_per_day} persone possono fare SW lo stesso giorno. Se il giorno è pieno, non sarà selezionabile.`],
                ].map(([ic,title,desc])=>(
                  <div key={title} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:"1px solid #F5F5F5"}}>
                    <span style={{fontSize:20,flexShrink:0}}>{ic}</span>
                    <div><div style={{fontSize:13,fontWeight:700,marginBottom:3}}>{title}</div><div style={{fontSize:12,color:SUB,lineHeight:1.6}}>{desc}</div></div>
                  </div>
                ))}
              </div>

              {/* Periodi speciali */}
              {rules.consec_enabled && (()=>{
                const allPeriods = [
                  ...(rules.consec_from && rules.consec_to ? [{from:(rules.consec_from||"").substring(0,10), to:(rules.consec_to||"").substring(0,10), max:rules.consec_max||5}] : []),
                  ...(rules.extra_periods||[]).filter(p=>p.from&&p.to).map(p=>({from:(p.from||"").substring(0,10),to:(p.to||"").substring(0,10),max:p.max||5}))
                ];
                if(allPeriods.length===0) return null;
                return <div className="card" style={{borderColor:"#A5D6A7",background:"#F0FFF4"}}>
                  <div style={{fontSize:14,fontWeight:700,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:18}}>🗓️</span> Periodi speciali: più giorni consecutivi
                  </div>
                  <div style={{fontSize:12,color:"#1A7A3A",lineHeight:1.8}}>
                    {allPeriods.map((p,i)=>(
                      <div key={i} style={{marginBottom:i<allPeriods.length-1?10:0}}>
                        <strong>Periodo {i+1}:</strong> dal <strong>{p.from}</strong> al <strong>{p.to}</strong> —
                        puoi prenotare fino a <strong>{p.max} giorni consecutivi</strong> per volta, senza il limite di 1 a settimana.
                      </div>
                    ))}
                    <br/>Le richieste seguono comunque le regole di budget mensile e annuale e vanno approvate dall'amministratore.
                  </div>
                </div>;
              })()}

              {/* Come prenotare */}
              <div className="card">
                <div style={{fontSize:14,fontWeight:700,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>🖱️</span> Come prenotare uno Smart Working
                </div>
                {[
                  ["1","Vai su «Prenota Smart Working»","dal menu a sinistra"],
                  ["2","Seleziona una data","Le date disponibili sono evidenziate. Viola = settimana già occupata. Giallo = limite giornaliero. Grigio = bloccata."],
                  ["3","Inserisci la sede","Indica dove lavorerai (es. «Casa», «Coworking Bologna»). È obbligatorio."],
                  ["4","Invia la richiesta","L'admin riceve la notifica e approva o rifiuta. Riceverai conferma nella sezione «Le mie richieste»."],
                ].map(([n,title,desc])=>(
                  <div key={n} style={{display:"flex",gap:12,padding:"8px 0",borderBottom:"1px solid #F5F5F5"}}>
                    <div style={{width:24,height:24,borderRadius:"50%",background:BLK,color:"white",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>{n}</div>
                    <div><div style={{fontSize:13,fontWeight:700,marginBottom:2}}>{title}</div><div style={{fontSize:12,color:SUB}}>{desc}</div></div>
                  </div>
                ))}
              </div>

              {/* Emergenze */}
              <div className="card" style={{borderColor:"#F5C0A0"}}>
                <div style={{fontSize:14,fontWeight:700,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>🚨</span> Giorni di emergenza
                </div>
                <div style={{fontSize:12,color:SUB,lineHeight:1.8}}>
                  Hai a disposizione <strong>{myUR.emergency_days} giorni di emergenza</strong> all'anno per situazioni impreviste.
                  Questi giorni richiedono solo <strong>2 giorni di anticipo</strong> invece di {rules.advance_days}.
                  I giorni di emergenza scalano dal tuo budget annuo totale ({myUR.days_per_year} giorni).
                  Per attivarla, usa il toggle «🚨 Emergenza» nella schermata di prenotazione.
                </div>
              </div>

              {/* Scambio SW */}
              <div className="card">
                <div style={{fontSize:14,fontWeight:700,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>🔄</span> Come funziona lo scambio
                </div>
                <div style={{fontSize:12,color:SUB,lineHeight:1.8}}>
                  Puoi scambiare uno dei tuoi smart working prenotati con quello di un collega.
                  <br/><br/>
                  <strong>Come fare:</strong> Vai su «Scambia SW» → scegli il tuo SW da cedere (devi averne uno prenotato) → scegli il collega → scegli il suo SW che vuoi.
                  <br/><br/>
                  La proposta viene inviata e l'admin approverà lo scambio. Entrambi i giorni vengono aggiornati automaticamente.
                </div>
              </div>

              {/* Budget esaurito */}
              <div className="card" style={{borderColor:"#F5A0A0"}}>
                <div style={{fontSize:14,fontWeight:700,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>⚠️</span> Hai finito i giorni?
                </div>
                <div style={{fontSize:12,color:SUB,lineHeight:1.8}}>
                  Se hai esaurito il budget annuo di <strong>{myUR.days_per_year} giorni</strong>, puoi comunque provare a inviare una richiesta speciale:
                  <br/><br/>
                  <strong>1.</strong> Vai su «Prenota Smart Working» e seleziona la data desiderata.<br/>
                  <strong>2.</strong> Il sistema ti avviserà che il budget è esaurito e ti chiederà se vuoi inviare una richiesta speciale.<br/>
                  <strong>3.</strong> La richiesta resterà <em>in attesa</em> — l'amministratore deciderà se approvarla.<br/><br/>
                  In alternativa, puoi <strong>scrivere un messaggio</strong> nella sezione <strong>«💬 Comunicazioni»</strong> per spiegare la situazione e richiedere giorni aggiuntivi.
                </div>
              </div>

              {/* Profilo e calendario */}
              <div className="card">
                <div style={{fontSize:14,fontWeight:700,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>👤</span> Il tuo profilo
                </div>
                {[
                  ["🎂","Compleanno","Imposta la tua data di nascita nel profilo — i colleghi vedranno il tuo compleanno in dashboard (solo giorno e mese)."],
                  ["🌴","Ferie","Quando sei in ferie, attiva lo stato «Sono in ferie» nel profilo. I colleghi lo vedranno in dashboard."],
                  ["📍","Sede SW","Imposta la tua sede preferita di smart working nel profilo — viene pre-compilata nelle richieste."],
                  ["📅","Google Calendar","Usa il pulsante «📅 Google Calendar» nella sidebar per aggiungere i tuoi SW approvati al tuo calendario Google."],
                ].map(([ic,title,desc])=>(
                  <div key={title} style={{display:"flex",gap:12,padding:"8px 0",borderBottom:"1px solid #F5F5F5"}}>
                    <span style={{fontSize:18,flexShrink:0}}>{ic}</span>
                    <div><div style={{fontSize:13,fontWeight:700,marginBottom:2}}>{title}</div><div style={{fontSize:12,color:SUB}}>{desc}</div></div>
                  </div>
                ))}
              </div>

              {/* Il tuo stato attuale */}
              {myCanSW&&<div className="card" style={{background:"#F0F5F8",borderColor:"#C8DCE8"}}>
                <div style={{fontSize:14,fontWeight:700,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>📊</span> Il tuo stato attuale ({today.getFullYear()})
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                  {[
                    ["Budget annuo",`${Math.max(0,myAv)} / ${myUR.days_per_year}`,"rimasti"],
                    ["Questo mese",`${myMU} / ${myUR.days_per_month}`,"usati"],
                    ["Emergenza",`${myEmU} / ${myUR.emergency_days}`,"usati"],
                  ].map(([l,v,s])=>(
                    <div key={l} style={{background:"#FFF",borderRadius:9,padding:"10px 12px",border:"1px solid #E0E8F0",textAlign:"center"}}>
                      <div style={{fontSize:10,color:SUB,marginBottom:4}}>{l}</div>
                      <div style={{fontSize:16,fontWeight:700,fontFamily:"'Space Mono',monospace"}}>{v}</div>
                      <div style={{fontSize:10,color:SUB}}>{s}</div>
                    </div>
                  ))}
                </div>
              </div>}

            </div>
          </div>}

          {/* ══ PRENOTA ══ */}
          {tab==="request"&&<div className="page">
            <h1 style={{fontSize:21,fontWeight:700,marginBottom:4}}>Prenota Smart Working</h1>
            <p style={{color:SUB,fontSize:13,marginBottom:18}}>Seleziona una data e invia la richiesta. Riceverai conferma dall'amministratore.</p>
            {myActiveExcPeriod&&<div style={{background:"#FFFBF0",border:"1px solid #E0C060",borderRadius:11,padding:"10px 16px",marginBottom:16,display:"flex",alignItems:"flex-start",gap:10}}>
              <span style={{fontSize:18}}>⚡</span>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#856404"}}>Periodo eccezione attivo</div>
                <div style={{fontSize:12,color:"#A07820"}}>Dal {myActiveExcPeriod.date_from} al {myActiveExcPeriod.date_to} — puoi prenotare più giorni nella stessa settimana. Il 1° giorno/settimana scala dal budget, gli altri sono eccezioni.</div>
                {myActiveExcPeriod.note&&<div style={{fontSize:11,color:"#888",marginTop:2}}>{myActiveExcPeriod.note}</div>}
              </div>
            </div>}
            {!myCanSW?<div className="card" style={{color:"#C0392B"}}>Lo smart working è riservato al team fisso. Contatta Giovanni Rago per modificare i permessi.</div>:
            <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:20}}>
              <div className="card">
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18,padding:"10px 13px",background:isEm?"#FFF8EE":"#F8F8F8",borderRadius:10,border:`1px solid ${isEm?"#F0C080":BDR}`}}>
                  <Tog on={isEm} set={()=>{setIsEm(e=>!e);setNrDate("");}}/>
                  <div><div style={{fontSize:13,fontWeight:700,color:isEm?"#C05A00":BLK}}>Modalità emergenza</div><div style={{fontSize:11,color:SUB}}>Anticipo min. 2 gg · {myEmU}/{myUR.emergency_days} usati</div></div>
                </div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <span style={{fontSize:14,fontWeight:700}}>{MON[tab_selM]} {tab_selY}</span>
                  <div style={{display:"flex",gap:7}}>
                    <button onClick={()=>{if(tab_selM===0){setSelM(11);setSelY(y=>y-1);}else setSelM(m=>m-1);}} style={{background:"#F5F5F5",border:"1px solid "+BDR,borderRadius:7,padding:"4px 11px",cursor:"pointer"}}>←</button>
                    <button onClick={()=>{if(tab_selM===11){setSelM(0);setSelY(y=>y+1);}else setSelM(m=>m+1);}} style={{background:"#F5F5F5",border:"1px solid "+BDR,borderRadius:7,padding:"4px 11px",cursor:"pointer"}}>→</button>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:6}}>
                  {["L","M","M","G","V","S","D"].map((d,i)=><div key={i} style={{textAlign:"center",fontSize:11,color:SUB,fontWeight:700,lineHeight:"28px"}}>{d}</div>)}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,justifyItems:"center"}}
                  onTouchStart={e=>{const t=e.touches[0];e.currentTarget._tsx=t.clientX;e.currentTarget._tsy=t.clientY;}}
                  onTouchEnd={e=>{const dx=e.changedTouches[0].clientX-(e.currentTarget._tsx||0);const dy=Math.abs(e.changedTouches[0].clientY-(e.currentTarget._tsy||0));if(Math.abs(dx)>50&&dy<60){if(dx<0){if(tab_selM===11){setSelM(0);setSelY(y=>y+1);}else setSelM(m=>m+1);}else{if(tab_selM===0){setSelM(11);setSelY(y=>y-1);}else setSelM(m=>m-1);}}}}>
                  {Array.from({length:(fd(tab_selY,tab_selM)+6)%7}).map((_,i)=><div key={"x"+i}/>)}
                  {Array.from({length:dim(tab_selY,tab_selM)},(_,i)=>{
                    const day=i+1;
                    const ds=`${tab_selY}-${String(tab_selM+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                    const isPast=ds<todayStr;
                    const dsInExc=isInExcPeriod(cu.id,ds);
                    const cf=(rules.consec_from||"").substring(0,10);const ct=(rules.consec_to||"").substring(0,10);const dsInConsec=rules.consec_enabled&&cf&&ct&&ds>=cf&&ds<=ct;
                    const isHoliday=isItalianHoliday(ds);
                    const isTooSoon=!dsInExc&&!dsInConsec&&!isHoliday&&ds<fmtDate(addD(today,isEm?2:rules.advance_days));
                    const isBlk=isBl(ds); const cnt=scnt(ds); const isFull=cnt>=rules.max_per_day;
                    const hasReq=reqs.some(r=>r.user_id===cu.id&&r.smart_date===ds&&r.status!=="rejected");
                    const weekUsed=!isEm&&!isPast&&!hasReq&&!dsInExc&&!dsInConsec&&hasSwInWeek(cu.id,ds);
                    const weekHasReq=!isEm&&!isPast&&!dsInExc&&!dsInConsec&&hasSwInWeek(cu.id,ds);
                    let cls="cd";
                    if(isPast)cls+=" pa";
                    else if(hasReq)cls+=" hs"; // già prenotata — sempre visibile
                    else if(isHoliday)cls+=" ho"; // festivo italiano
                    else if(isBlk)cls+=" bl";
                    else if(isFull)cls+=" fu";
                    else if(isTooSoon)cls+=" ts";
                    else if(weekUsed)cls+=" wu"; // altra data prenotata questa settimana
                    if(isEm&&!isPast&&!isBlk)cls+=" em";
                    if(nrDate===ds)cls+=" sel";
                    if(dsInConsec&&nrDate&&nrDateTo&&ds>nrDate&&ds<nrDateTo)cls+=" rng";
                    if(dsInConsec&&nrDateTo&&ds===nrDateTo)cls+=" sel";
                    return(<div key={day} className={cls}
                      title={hasReq?"Già prenotato":weekUsed?"Hai già uno SW questa settimana":isHoliday?"🎉 Festivo":isBlk?"Bloccato":isFull?"Limite giornaliero":dsInConsec?"🗓️ Periodo speciale":""}
                      onClick={()=>{
                        if(isPast||isBlk||isTooSoon)return;
                        if(hasReq){showToast("Hai già una prenotazione in questa data",false);return;}
                        if(isHoliday){showToast("Giorno festivo",false);return;}
                        if(isFull){showToast("Limite raggiunto",false);return;}
                        if(dsInConsec&&!isEm){
                          if(!nrDate||nrDateTo||(ds<nrDate)){setNrDate(ds);setNrDateTo("");}
                          else if(ds===nrDate){setNrDate("");setNrDateTo("");}
                          else{setNrDateTo(ds);}
                          return;
                        }
                        if(weekHasReq&&!isEm){showToast("Hai già uno SW questa settimana (max 1 a settimana)",false);return;}
                        setNrDate(ds); setNrDateTo("");
                      }}>
                      {day}{cnt>0&&!isPast&&isAdmin&&<div className="dot" style={{background:isFull?"#856404":LYB}}/>}
                    </div>);
                  })}
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div className="card">
                  <div className="st">Nuova richiesta</div>
                  {(!!getConsecPeriod(nrDate)&&!isEm)?(
                    <div style={{marginBottom:13}}>
                      <label className="lb">🗓️ Periodo speciale — Dal…Al</label>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <div style={{flex:1,background:"#F5F5F5",border:"1px solid "+BDR,borderRadius:9,padding:"9px 12px",fontSize:13,color:nrDate?BLK:SUB,fontFamily:nrDate?"'Space Mono',monospace":"inherit",textAlign:"center"}}>{nrDate||"Dal…"}</div>
                        <span style={{color:SUB}}>→</span>
                        <div style={{flex:1,background:"#F5F5F5",border:"1px solid "+BDR,borderRadius:9,padding:"9px 12px",fontSize:13,color:nrDateTo?BLK:SUB,fontFamily:nrDateTo?"'Space Mono',monospace":"inherit",textAlign:"center"}}>{nrDateTo||"Al…"}</div>
                      </div>
                      {nrDate&&!nrDateTo&&<div style={{fontSize:11,color:"#856404",marginTop:5}}>Seleziona la data finale sul calendario</div>}
                      {nrDate&&nrDateTo&&(()=>{
  let gg=0;
  const cur=new Date(nrDate+"T12:00:00");
  const end=new Date(nrDateTo+"T12:00:00");
  while(cur<=end){const dow=cur.getDay();if(dow>=1&&dow<=5&&!isBl(cur.toISOString().split("T")[0]))gg++;cur.setDate(cur.getDate()+1);}
  return <div style={{fontSize:11,color:"#1A7A3A",marginTop:5}}>✓ {gg} giorni lavorativi selezionati</div>;
})()}
                    </div>
                  ):(
                    <><Lbl t="Data"/><div style={{background:"#F5F5F5",border:"1px solid "+BDR,borderRadius:9,padding:"9px 12px",fontSize:13,color:nrDate?BLK:SUB,fontFamily:nrDate?"'Space Mono',monospace":"inherit",marginBottom:13}}>{nrDate||"Seleziona dal calendario"}</div></>
                  )}
                  {/* Sede SW: usa quella del profilo, opzione cambio temporaneo */}
                  {cu?.sw_location ? (
                    <div style={{marginBottom:12}}>
                      <label className="lb">Sede smart working</label>
                      <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 12px",background:"#F5F5F5",border:"1px solid "+BDR,borderRadius:9,marginBottom:4}}>
                        <span style={{fontSize:13,flex:1}}>📍 {nrNote||cu.sw_location}</span>
                        <button onClick={()=>{
                          const nuova = window.prompt("Nuova sede temporanea per questa richiesta (non modifica la sede nel profilo):","");
                          if(nuova&&nuova.trim()) setNrNote(nuova.trim());
                          else setNrNote("");
                        }} style={{fontSize:10,color:SUB,background:"none",border:"1px solid "+BDR,borderRadius:6,padding:"2px 7px",cursor:"pointer"}}>
                          cambia
                        </button>
                        {nrNote&&<button onClick={()=>setNrNote("")} style={{fontSize:10,color:"#C0392B",background:"none",border:"none",cursor:"pointer"}}>✕ ripristina</button>}
                      </div>
                      {nrNote&&nrNote!==cu.sw_location&&<div style={{background:"#FFF8E0",border:"1px solid #F0C040",borderRadius:8,padding:"8px 12px",fontSize:11,color:"#856404",lineHeight:1.6}}>
                        ⚠️ <strong>Attenzione:</strong> lavorerai da una sede diversa da quella dichiarata nel tuo contratto. Se cambi sede in modo permanente, aggiorna il tuo profilo — la sede contrattuale influisce sulla busta paga.
                      </div>}
                    </div>
                  ):(
                    <div style={{background:"#FFF3CD",border:"1px solid #F0C040",borderRadius:9,padding:12,marginBottom:12}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#856404",marginBottom:4}}>📍 Sede non impostata</div>
                      <div style={{fontSize:11,color:"#7A5A00",lineHeight:1.6,marginBottom:8}}>
                        Prima di prenotare lo SW devi dichiarare la sede dove lavorerai.<br/>
                        <strong>Questa sede verrà usata per tutte le tue richieste future</strong> e corrisponde al domicilio in busta paga.
                      </div>
                      <input type="text" value={nrNote} onChange={e=>setNrNote(e.target.value)}
                        placeholder="Es. Via Roma 10, Bologna"
                        style={{width:"100%",marginBottom:8,borderColor:!nrNote?"#F5C0C0":undefined}}/>
                      {nrNote&&<button onClick={async()=>{
                        const {error}=await supabase.from("profiles").update({sw_location:nrNote.trim()}).eq("id",cu.id);
                        if(error){showToast("Errore: "+error.message,false);return;}
                        const u={...cu,sw_location:nrNote.trim()};
                        try{safeStore.set('sw_user',JSON.stringify(u));}catch{}
                        setRealUser(u);
                        setTeam(prev=>prev.map(t=>t.id===cu.id?u:t));
                        showToast("Sede salvata ✓");
                      }} style={{width:"100%",padding:"8px 0",background:"#856404",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                        💾 Salva sede
                      </button>}
                    </div>
                  )}
                  {/* Mezza giornata — visibile solo se admin l'ha abilitata per questo utente */}
                  {cu?.half_day_enabled&&<div style={{marginBottom:13}}>
                    <label className="lb">Durata</label>
                    <div style={{display:"flex",gap:7}}>
                      {[["full","Intera giornata","☀️"],["morning","Mattina","🌅"],["afternoon","Pomeriggio","🌇"]].map(([v,l,ic])=>(
                        <button key={v} onClick={()=>setNrHalf(v)} style={{flex:1,padding:"7px 4px",borderRadius:9,
                          border:"2px solid "+(nrHalf===v?BLK:BDR),
                          background:nrHalf===v?BLK:"#FAFAFA",
                          color:nrHalf===v?WHT:BLK,fontSize:11,fontWeight:nrHalf===v?700:400,
                          cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                          <div>{ic}</div><div>{l}</div>
                          {v!=="full"&&<div style={{fontSize:9,opacity:.7}}>−0.5 gg</div>}
                        </button>
                      ))}
                    </div>
                  </div>}
                  <button className="bp" onClick={simulating?()=>showToast("Modalità visualizzazione",false):submitReq} style={{width:"100%"}} disabled={!nrDate||(!!getConsecPeriod(nrDate)&&!isEm&&!nrDateTo)}>
                    Invia {nrHalf!=="full"&&cu?.half_day_enabled?"(mezza giornata)":""} →
                  </button>
                </div>
                <div className="card" style={{fontSize:12}}>
                  <div style={{fontWeight:700,marginBottom:8,fontSize:13}}>Stato</div>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}><span style={{color:SUB}}>Disponibili ord.</span><strong style={{fontFamily:"'Space Mono',monospace",color:myAv>0?BLK:"#C0392B"}}>{myAv}</strong></div>
                  <PBar pct={(myYU/myUR.days_per_year)*100}/>
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:10,marginBottom:5}}><span style={{color:SUB}}>Emergenza</span><strong style={{fontFamily:"'Space Mono',monospace",color:myEmU<myUR.emergency_days?"#C05A00":"#C0392B"}}>{myEmU}/{myUR.emergency_days}</strong></div>
                  <PBar pct={(myEmU/myUR.emergency_days)*100} col="#C05A00"/>
                  {myExtra!==0&&<div style={{marginTop:10,padding:"7px 10px",borderRadius:8,background:myExtra>0?"#D4EDDA":"#F8D7DA",color:myExtra>0?"#1A7A3A":"#721C24",fontSize:12,fontWeight:600}}>{myExtra>0?`+${myExtra} bonus`:`${Math.abs(myExtra)} riserva`}</div>}
                </div>
              </div>
            </div>}
          </div>}

          {/* ══ SCAMBIO SW ══ */}
          {tab==="swap"&&<div className="page">
            <h1 style={{fontSize:21,fontWeight:700,marginBottom:4}}>Scambia Smart Working</h1>
            <p style={{color:SUB,fontSize:13,marginBottom:16}}>Puoi proporre di scambiare uno dei tuoi smart working prenotati con quello di un collega. L'admin approverà lo scambio.</p>

            {/* Avviso se non hai SW prenotati */}
            {(()=>{
              const myBooked = reqs.filter(r=>r.user_id===cu.id&&r.status!=="rejected"&&r.type!=="desk"&&r.smart_date>=todayStr);
              if(myBooked.length===0) return(
                <div style={{background:"#FFF8E0",border:"1px solid #F0C040",borderRadius:11,padding:"14px 18px",marginBottom:16,display:"flex",gap:12,alignItems:"flex-start"}}>
                  <span style={{fontSize:20}}>ℹ️</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:"#856404"}}>Non hai smart working prenotati</div>
                    <div style={{fontSize:12,color:"#A07820",marginTop:3}}>Per proporre uno scambio devi avere almeno uno SW approvato o in attesa. Vai su "Prenota Smart Working" per prenotarne uno.</div>
                  </div>
                </div>
              );
              return null;
            })()}

            <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
              <div className="card">
                <div className="st" style={{color:"#1A7A3A"}}>+ Proponi uno scambio</div>

                {/* Step 1: scegli il TUO giorno da un select dei tuoi SW prenotati */}
                <Row><Lbl t="1. Il mio giorno SW da cedere"/>
                  <select value={swapMyDate} onChange={e=>setSwapMyDate(e.target.value)}>
                    <option value="">Seleziona il tuo SW...</option>
                    {reqs.filter(r=>r.user_id===cu.id&&r.status!=="rejected"&&r.type!=="desk"&&r.smart_date>=todayStr)
                      .sort((a,b)=>a.smart_date.localeCompare(b.smart_date))
                      .map(r=>(
                        <option key={r.id} value={r.smart_date}>
                          {r.smart_date} {r.status==="approved"?"✓ approvato":"⏳ in attesa"} — {r.note||""}
                        </option>
                    ))}
                  </select>
                </Row>

                {/* Step 2: scegli il collega */}
                <Row><Lbl t="2. Con chi vuoi scambiare?"/>
                  <select value={swapTgtUser} onChange={e=>{setSwapTgtUser(e.target.value);setSwapTgtDate("");}}>
                    <option value="">Seleziona collega...</option>
                    {team.filter(u=>u.id!==cu.id&&isFixed(u)&&
                      reqs.some(r=>r.user_id===u.id&&r.status!=="rejected"&&r.type!=="desk"&&r.smart_date>=todayStr)
                    ).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </Row>

                {/* Step 3: scegli il giorno del collega — solo tra i suoi SW prenotati */}
                <Row><Lbl t="3. Il suo giorno SW che vuoi"/>
                  <select value={swapTgtDate} onChange={e=>setSwapTgtDate(e.target.value)} disabled={!swapTgtUser}>
                    <option value="">{swapTgtUser?"Seleziona il suo SW...":"Prima scegli il collega"}</option>
                    {swapTgtUser&&reqs.filter(r=>r.user_id===swapTgtUser&&r.status!=="rejected"&&r.type!=="desk"&&r.smart_date>=todayStr)
                      .sort((a,b)=>a.smart_date.localeCompare(b.smart_date))
                      .map(r=>(
                        <option key={r.id} value={r.smart_date}>
                          {r.smart_date} {r.status==="approved"?"✓":"⏳"} — {r.note||""}
                        </option>
                    ))}
                  </select>
                </Row>

                <Row><Lbl t="Nota (opzionale)"/>
                  <textarea rows={2} defaultValue="" ref={el=>{if(el)el.value=swapNoteVal;}} onChange={e=>{swapNoteRef.current=e.target.value;setSwapNoteVal(e.target.value);}} style={{resize:"none"}} placeholder="Motivo dello scambio..."/>
                </Row>
                {swapMyDate&&swapTgtDate&&swapTgtUser&&<div style={{background:"#F0F8FF",border:"1px solid #C0DCF0",borderRadius:9,padding:"10px 12px",marginBottom:12,fontSize:12}}>
                  <strong>Riepilogo:</strong> cedi <strong>{swapMyDate}</strong> e prendi <strong>{swapTgtDate}</strong> di {team.find(t=>t.id===swapTgtUser)?.name?.split(" ")[0]}
                </div>}
                <button className="bp" onClick={simulating?()=>showToast("Solo visualizzazione",false):submitSwap} style={{width:"100%"}} disabled={!swapMyDate||!swapTgtUser||!swapTgtDate}>
                  Proponi scambio →
                </button>
              </div>
              <div>
                <div className="card" style={{marginBottom:14}}>
                  <div className="st">Richieste ricevute</div>
                  {swaps.filter(s=>s.target_id===cu.id&&s.status==="pending").length===0
                    ?<div style={{color:SUB,fontSize:13}}>Nessuna proposta di scambio</div>
                    :swaps.filter(s=>s.target_id===cu.id&&s.status==="pending").map(s=>{
                      const req=team.find(t=>t.id===s.requester_id);
                      return(<div key={s.id} style={{padding:"10px 0",borderBottom:"1px solid #F2F2F2"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                          <div className="av" style={{background:req?.color||LYB,width:28,height:28,fontSize:10}}>{req?.avatar_initials}</div>
                          <div><div style={{fontSize:13,fontWeight:600}}>{req?.name}</div><div style={{fontSize:11,color:SUB}}>Cede: <strong>{s.requester_date}</strong> · Vuole: <strong>{s.target_date}</strong></div></div>
                        </div>
                        {s.note&&<div style={{fontSize:12,color:SUB,marginBottom:8}}>"{s.note}"</div>}
                        <div style={{display:"flex",gap:8}}>
                          <button className="bs ba" onClick={()=>showToast("Solo l'admin può approvare gli scambi",false)}>✓ Accetta (admin approva)</button>
                        </div>
                      </div>);
                    })}
                </div>
                <div className="card">
                  <div className="st">Le mie proposte</div>
                  {swaps.filter(s=>s.requester_id===cu.id).length===0
                    ?<div style={{color:SUB,fontSize:13}}>Nessuna proposta inviata</div>
                    :swaps.filter(s=>s.requester_id===cu.id).slice(0,8).map(s=>{
                      const tgt=team.find(t=>t.id===s.target_id);
                      return(<div key={s.id} className="ri">
                        <div className="av" style={{background:tgt?.color||"#AAA",width:26,height:26,fontSize:9}}>{tgt?.avatar_initials}</div>
                        <div style={{flex:1,fontSize:12}}><div style={{fontWeight:600}}>{tgt?.name}</div><div style={{color:SUB}}>{s.requester_date} ↔ {s.target_date}</div></div>
                        <span className={`tag t${(s.status||"p")[0]}`} style={{fontSize:10}}>{s.status==="pending"?"in attesa":s.status==="approved"?"approvato":"rifiutato"}</span>
                      </div>);
                    })}
                </div>
              </div>
            </div>
          </div>}

          {/* ══ PRENOTA POSTAZIONE (full remote) ══ */}
          {tab==="desk_booking"&&<div className="page">
            <h1 style={{fontSize:21,fontWeight:700,marginBottom:4}}>Prenota una Postazione</h1>
            <p style={{color:SUB,fontSize:13,marginBottom:12}}>Scegli il giorno in cui vieni in ufficio e una postazione libera.</p>
            {isFullRemote(cu)&&<div style={{background:"#E8F5E9",border:"1px solid #A5D6A7",borderRadius:11,padding:"10px 16px",marginBottom:16,fontSize:12,color:"#2E7D32",lineHeight:1.7}}>
              🏠 <strong>Sei Full Remote</strong> — la tua sede contrattuale è <strong>{cu?.sw_location||"non impostata"}</strong>. Quando vieni in ufficio prenota qui una postazione.
            </div>}
            <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:20}}>
              <div className="card">
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <span style={{fontSize:14,fontWeight:700}}>{MON[tab_selM]} {tab_selY}</span>
                  <div style={{display:"flex",gap:7}}>
                    <button onClick={()=>{if(tab_selM===0){setSelM(11);setSelY(y=>y-1);}else setSelM(m=>m-1);}} style={{background:"#F5F5F5",border:"1px solid "+BDR,borderRadius:7,padding:"4px 11px",cursor:"pointer"}}>←</button>
                    <button onClick={()=>{if(tab_selM===11){setSelM(0);setSelY(y=>y+1);}else setSelM(m=>m+1);}} style={{background:"#F5F5F5",border:"1px solid "+BDR,borderRadius:7,padding:"4px 11px",cursor:"pointer"}}>→</button>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:6}}>
                  {["L","M","M","G","V","S","D"].map((d,i)=><div key={i} style={{textAlign:"center",fontSize:11,color:SUB,fontWeight:700,lineHeight:"28px"}}>{d}</div>)}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,justifyItems:"center"}}
                  onTouchStart={e=>{const t=e.touches[0];e.currentTarget._tsx=t.clientX;e.currentTarget._tsy=t.clientY;}}
                  onTouchEnd={e=>{const dx=e.changedTouches[0].clientX-(e.currentTarget._tsx||0);const dy=Math.abs(e.changedTouches[0].clientY-(e.currentTarget._tsy||0));if(Math.abs(dx)>50&&dy<60){if(dx<0){if(tab_selM===11){setSelM(0);setSelY(y=>y+1);}else setSelM(m=>m+1);}else{if(tab_selM===0){setSelM(11);setSelY(y=>y-1);}else setSelM(m=>m-1);}}}}>
                  {Array.from({length:(fd(tab_selY,tab_selM)+6)%7}).map((_,i)=><div key={"x"+i}/>)}
                  {Array.from({length:dim(tab_selY,tab_selM)},(_,i)=>{
                    const day=i+1;
                    const ds=`${tab_selY}-${String(tab_selM+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                    const isPast=ds<todayStr;
                    const isBlk=isBl(ds);
                    const hasBook=reqs.some(r=>r.user_id===cu.id&&r.smart_date===ds&&r.type==="desk"&&r.status!=="rejected");
                    let cls="cd";
                    if(isPast)cls+=" pa"; else if(isBlk)cls+=" bl"; else if(hasBook)cls+=" hs";
                    if(nrDate===ds)cls+=" sel";
                    return(<div key={day} className={cls} onClick={()=>{if(isPast||isBlk)return; setNrDate(ds);}}>{day}</div>);
                  })}
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div className="card">
                  <div className="st">Prenota postazione</div>
                  <label className="lb">Data</label>
                  <div style={{background:"#F5F5F5",border:"1px solid "+BDR,borderRadius:9,padding:"9px 12px",fontSize:13,color:nrDate?BLK:SUB,fontFamily:nrDate?"'Space Mono',monospace":"inherit",marginBottom:13}}>{nrDate||"Seleziona dal calendario"}</div>
                  <label className="lb">Postazione preferita</label>
                  <select value={nrNote} onChange={e=>setNrNote(e.target.value)} style={{marginBottom:14}}>
                    <option value="">Qualsiasi disponibile</option>
                    {desks.filter(d=>d.active&&!d.assigned_to).map(d=><option key={d.id} value={d.name}>{d.name} — {d.room}</option>)}
                  </select>
                  <button className="bp" onClick={async()=>{
                    if(!nrDate){showToast("Seleziona una data",false);return;}
                    if(isBl(nrDate)){showToast("Data non disponibile — giorno bloccato",false);return;}
                    if(simulating){showToast("Solo visualizzazione",false);return;}
                    // Già prenotata questa data?
                    const alreadyBooked=reqs.some(r=>r.user_id===cu.id&&r.smart_date===nrDate&&r.type==="desk"&&r.status!=="rejected");
                    if(alreadyBooked){showToast("Hai già una postazione prenotata per questo giorno",false);return;}
                    // Postazioni disponibili?
                    const deskReqsDay=reqs.filter(r=>r.smart_date===nrDate&&r.type==="desk"&&r.status==="approved");
                    const swThatDay=reqs.filter(r=>r.smart_date===nrDate&&r.status==="approved"&&r.type!=="desk").map(r=>r.user_id);
                    const fixedInOffice=team.filter(u=>isFixed(u)&&!isFullRemote(u)&&!swThatDay.includes(u.id)&&!u.vacation);
                    const fixedIdsSet=new Set(fixedInOffice.map(u=>u.id));
                    const bookedEff=deskReqsDay.filter(r=>!fixedIdsSet.has(r.user_id)).length;
                    const freeCount=Math.max(0,desks.filter(d=>d.active).length-fixedInOffice.length-bookedEff);
                    if(freeCount<=0){showToast("Nessuna postazione disponibile per questo giorno",false);return;}
                    const{error}=await supabase.from("requests").insert({user_id:cu.id,type:"desk",date_from:nrDate,date_to:nrDate,smart_date:nrDate,note:nrNote||"Qualsiasi postazione",status:"approved",advance_days:0,forced_by_admin:false,is_emergency:false,cancel_requested:false,company_id:cu.company_id||null});
                    if(error){showToast("Errore: "+error.message,false);return;}
                    showToast("Postazione prenotata ✓"); setNrDate(""); setNrNote(""); load();
                  }} style={{width:"100%"}} disabled={!nrDate}>Prenota →</button>
                </div>
                <div className="card">
                  <div className="st">Postazioni libere</div>
                  {desks.filter(d=>d.active&&!d.assigned_to).length===0
                    ?<div style={{color:SUB,fontSize:13}}>Nessuna postazione libera</div>
                    :Object.entries((() => { const m={}; desks.filter(d=>d.active&&!d.assigned_to).forEach(d=>{const r=d.room||"Altro";if(!m[r])m[r]=[];m[r].push(d);});return m; })()).map(([room,ds])=>(
                      <div key={room} style={{marginBottom:12}}>
                        <div style={{fontSize:10,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{room}</div>
                        {ds.map(d=><div key={d.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"1px solid #F5F5F5"}}><span>🪑</span><span style={{fontSize:12}}>{d.name}</span></div>)}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>}

          {/* ══ PIANO SETTIMANALE ══ */}
          {tab==="piano"&&<div className="page">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:10}}>
              <h1 style={{fontSize:21,fontWeight:700}}>Piano Settimanale</h1>
              <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
                {/* Filtri */}
                {[["all","Tutti"],["office","🏢 Ufficio"],["sw","🏠 SW"],["fr","🌐 FR"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setWkFilter(v)} style={{padding:"5px 11px",borderRadius:7,border:"1px solid "+(wkFilter===v?BLK:BDR),background:wkFilter===v?BLK:"white",color:wkFilter===v?"white":SUB,fontSize:12,fontFamily:"'DM Sans',sans-serif",fontWeight:600,cursor:"pointer"}}>{l}</button>
                ))}
                <div style={{width:1,height:20,background:BDR,margin:"0 4px"}}/>
                <button onClick={()=>setWkOff(o=>o-7)} style={{background:WHT,border:"1px solid "+BDR,borderRadius:8,padding:"6px 13px",cursor:"pointer"}}>←</button>
                <button onClick={()=>setWkOff(0)} style={{background:WHT,border:"1px solid "+BDR,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontFamily:"'DM Sans',sans-serif"}}>Oggi</button>
                <button onClick={()=>setWkOff(o=>o+7)} style={{background:WHT,border:"1px solid "+BDR,borderRadius:8,padding:"6px 13px",cursor:"pointer"}}>→</button>
              </div>
            </div>
            {(()=>{
              // Calcola postazioni libere per ogni giorno della settimana
              const wkFreeDesks=(ds)=>{
                const allD=desks.filter(d=>d.active);
                const swIds=new Set(reqs.filter(r=>r.smart_date===ds&&r.status==="approved"&&r.type!=="desk").map(r=>r.user_id));
                const fixedInOff=team.filter(u=>isFixed(u)&&!isFullRemote(u)&&!swIds.has(u.id)&&!u.vacation);
                const fixedIdsSet=new Set(fixedInOff.map(u=>u.id));
                const assignedOcc=allD.filter(d=>{
                  if(!d.assigned_to) return false;
                  const owner=team.find(u=>u.name===d.assigned_to||u.email===d.assigned_to||u.id===d.assigned_to);
                  return owner && fixedIdsSet.has(owner.id);
                }).length;
                const deskBook=reqs.filter(r=>r.smart_date===ds&&r.type==="desk"&&r.status==="approved"&&!fixedIdsSet.has(r.user_id)&&!swIds.has(r.user_id)).length;
                return Math.max(0,allD.length-assignedOcc-deskBook);
              };
              // Filtra e ordina team
              const teamFiltered=team.filter(u=>u.id).filter(u=>{
                if(wkFilter==="fr") return isFullRemote(u);
                if(wkFilter==="sw") return wkDays.some(d=>{ const ds=fmtDate(d); return reqs.some(r=>r.user_id===u.id&&r.smart_date===ds&&r.status==="approved"&&r.type!=="desk"); });
                if(wkFilter==="office") return !isFullRemote(u)&&wkDays.some(d=>{ const ds=fmtDate(d); const hasSwThatDay=reqs.some(r=>r.user_id===u.id&&r.smart_date===ds&&r.status==="approved"&&r.type!=="desk"); const hasDeskBooking=reqs.some(r=>r.user_id===u.id&&r.smart_date===ds&&r.status==="approved"&&r.type==="desk"); return hasDeskBooking||(isFixed(u)&&!hasSwThatDay); });
                return true;
              }).sort((a,b)=>a.name.localeCompare(b.name));
              return(
            <div className="gwk" style={{overflowX:"auto"}}>
            <div className="card" style={{padding:0,overflow:"hidden",minWidth:580}}>
              <div style={{display:"grid",gridTemplateColumns:"140px repeat(7,1fr)",borderBottom:"1px solid "+BDR}}>
                <div style={{padding:"12px 14px",fontSize:10,color:SUB,textTransform:"uppercase",letterSpacing:1}}>Membro</div>
                {wkDays.map(d=>{const ds=fmtDate(d);const isT=ds===todayStr;const isB=isBl(ds);const cnt=scnt(ds);const freeDk=wkFreeDesks(ds);const isWe=d.getDay()===0||d.getDay()===6;return(<div key={ds} style={{padding:"8px 5px",textAlign:"center",background:isB?"#FFF0F0":isT?"#EEF4FB":isWe?"#F8F8F8":"transparent",borderLeft:"1px solid "+BDR}}>
                  <div style={{fontSize:9,color:isB?"#C0392B":isT?LYB:isWe?"#BBB":SUB,textTransform:"uppercase"}}>{DAY[d.getDay()]}</div>
                  <div style={{fontSize:15,fontWeight:700,fontFamily:"'Space Mono',monospace",color:isB?"#C0392B":isT?LYB:isWe?"#CCC":BLK}}>{d.getDate()}</div>
                  {isWe?<div style={{fontSize:8,color:"#CCC"}}>—</div>:isB?<div style={{fontSize:8,color:"#C0392B"}}>stop</div>:<>
                    <div style={{fontSize:8,color:cnt>=rules.max_per_day?"#856404":SUB}}>{cnt}/{rules.max_per_day} SW</div>
                    <div style={{fontSize:8,color:freeDk>0?"#1A7A3A":"#C0392B",fontWeight:600,marginTop:1}}>🪑{freeDk}</div>
                  </>}
                </div>);})}
              </div>
              {teamFiltered.map(u=>(
                <div key={u.id} style={{display:"grid",gridTemplateColumns:"140px repeat(7,1fr)",borderBottom:"1px solid #F5F5F5"}}>
                  <div style={{padding:"8px 14px",display:"flex",alignItems:"center",gap:7}}><div className="av" style={{background:u.color||LYB,width:26,height:26,fontSize:9}}>{u.avatar_initials}</div><span style={{fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{shortName(u.name)}</span>{isFullRemote(u)&&<span style={{fontSize:8,background:"#D4EDDA",color:"#1A7A3A",borderRadius:3,padding:"0 3px",flexShrink:0}}>FR</span>}</div>
                  {wkDays.map(d=>{
                    const ds=fmtDate(d);
                    const isWeekend=d.getDay()===0||d.getDay()===6;
                    const isBlocked=isBl(ds);
                    const req=reqs.find(r=>r.user_id===u.id&&r.smart_date===ds&&r.type!=="desk");
                    const deskReq=reqs.find(r=>r.user_id===u.id&&r.smart_date===ds&&r.type==="desk"&&r.status==="approved");
                    const isFR=isFullRemote(u);
                    const hasSW=req&&req.status==="approved";
                    const hasPending=req&&req.status==="pending";
                    const rawHl=req?.half_day;
                    const hl=(rawHl==="morning"||rawHl==="afternoon")?rawHl:null;
                    if(isWeekend||isBlocked) return(<div key={ds} style={{display:"flex",alignItems:"center",justifyContent:"center",borderLeft:"1px solid #F5F5F5",background:"#F8F8F8"}}></div>);
                    if(hasSW){return(<div key={ds} style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderLeft:"1px solid #F5F5F5",background:"#FFFBF0",gap:1}}>
                        <span title="Smart Working" style={{fontSize:14}}>🏠</span>
                        {hl&&<span style={{fontSize:7,background:"#F3E8FF",color:"#7C3AED",borderRadius:2,padding:"0 3px",fontWeight:700}}>{hl==="morning"?"mat":"pom"}</span>}
                      </div>);}
                    if(hasPending) return(<div key={ds} style={{display:"flex",alignItems:"center",justifyContent:"center",borderLeft:"1px solid #F5F5F5",background:"#FFFBF0"}}><span title="In attesa" style={{fontSize:13}}>⏳</span></div>);
                    if(deskReq) return(<div key={ds} style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderLeft:"1px solid #F5F5F5",background:"#EAF5EA",gap:1}}><span title="In ufficio" style={{fontSize:14}}>🏢</span>{deskReq.half_day&&<span style={{fontSize:7,background:"#D1FAE5",color:"#065F46",borderRadius:2,padding:"0 3px",fontWeight:700}}>{deskReq.half_day==="morning"?"mat":"pom"}</span>}{isFR&&!deskReq.half_day&&<span style={{fontSize:7,color:"#1A7A3A",fontWeight:600}}>FR</span>}</div>);
                    if(isFR) return(<div key={ds} style={{display:"flex",alignItems:"center",justifyContent:"center",borderLeft:"1px solid #F5F5F5",background:"#EAF5EA"}}><span title="Full Remote" style={{fontSize:10,color:"#1A7A3A",fontWeight:600}}>FR</span></div>);
                    const vacActive = u.vacation && (!u.vacation_from || !u.vacation_to || (fmtDate(ds) >= (u.vacation_from||"").substring(0,10) && fmtDate(ds) <= (u.vacation_to||"").substring(0,10)) || (!u.vacation_from && !u.vacation_to));
                    if(vacActive) return(<div key={ds} style={{display:"flex",alignItems:"center",justifyContent:"center",borderLeft:"1px solid #F5F5F5",background:"#FFF3E0"}}><span title="In ferie" style={{fontSize:14}}>🌴</span></div>);
                    return(<div key={ds} style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderLeft:"1px solid #F5F5F5",background:isWeekend?"#F8F8F8":"#F0F5F8"}}>
                      {!isWeekend&&<span title="In ufficio" style={{fontSize:10,color:LYB}}>🏢</span>}
                      {u.half_day_enabled&&!isWeekend&&<span style={{fontSize:7,background:"#F3E8FF",color:"#7C3AED",borderRadius:2,padding:"0 2px"}}>½</span>}
                    </div>);
                  })}
                </div>
              ))}
            </div>
            </div>
              );})()}
          </div>}

          {/* ══ RIEPILOGO ══ */}
          {tab==="summary"&&<div className="page">
            <h1 style={{fontSize:21,fontWeight:700,marginBottom:4}}>Il mio riepilogo</h1>
            <p style={{color:SUB,fontSize:13,marginBottom:16}}>{today.getFullYear()} — max {myUR.days_per_month} SW/mese · {myUR.days_per_year} giorni/anno · max 1 a settimana</p>

            {/* Box esplicativo budget */}
            <div style={{background:"#F0F5F8",border:"1px solid #C8DCE8",borderRadius:11,padding:"12px 16px",marginBottom:18}}>
              <div style={{fontSize:12,fontWeight:700,color:LYB,marginBottom:8}}>📊 Come funziona il tuo budget</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div style={{fontSize:12,lineHeight:1.8}}>
                  <div>Budget annuo: <strong>{myUR.days_per_year} giorni</strong></div>
                  <div>Già usati: <strong>{myYU}</strong></div>
                  <div>Rimasti: <strong style={{color:myAv>0?BLK:"#C0392B"}}>{Math.max(0,myAv)}</strong></div>
                  {myExtra!==0&&<div style={{color:myExtra>0?"#1A7A3A":"#C0392B"}}><strong>{myExtra>0?`+${myExtra} bonus`:`${myExtra} riserva`}</strong></div>}
                </div>
                <div style={{fontSize:12,lineHeight:1.8}}>
                  <div>Limite mensile: <strong>max {myUR.days_per_month}/mese</strong></div>
                  <div>Questo mese: <strong>{myMU}</strong></div>
                  <div style={{color:myMU<myUR.days_per_month?"#2E7D32":"#C0392B"}}>
                    {myMU<myUR.days_per_month?`ancora ${myUR.days_per_month-myMU} questo mese`:"Limite mensile raggiunto"}
                  </div>
                </div>
              </div>
              <div style={{marginTop:8,fontSize:11,color:SUB}}>
                I giorni non usati in un mese <strong>non si perdono</strong> — rimangono nel budget annuo.
              </div>
            </div>

            <div className="g4" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:13,marginBottom:18}}>
              {SC("Disponibili",`${Math.max(0,myAv)}`,`su ${myUR.days_per_year}/anno`,myAv>0?BLK:"#C0392B")}
              {SC("Usati/anno",`${myYU}`,`${myUR.days_per_year-myYU} rimasti`)}
              {SC("Questo mese",`${myMU}`,`max ${myUR.days_per_month}`,myMU<myUR.days_per_month?LYB:"#C0392B")}
              {(()=>{
                const excCount=reqs.filter(r=>r.user_id===cu.id&&r.is_exception&&r.status==="approved"&&r.smart_date&&new Date(r.smart_date).getFullYear()===today.getFullYear()).length;
                return SC("Eccezioni",`${excCount}`,"quest'anno",excCount>0?"#856404":SUB);
              })()}
            </div>

            <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
              <div className="card">
                <div className="st">Cumulo mese per mese {today.getFullYear()}</div>
                {Array.from({length:12},(_,m)=>{
                  const used=mUsed(cu.id,today.getFullYear(),m);
                  const maturati=Math.min((m+1)*myUR.days_per_month, myUR.days_per_year);
                  const usatiFino=reqs.filter(r=>r.user_id===cu.id&&r.status!=="rejected"&&r.smart_date&&new Date(r.smart_date).getFullYear()===today.getFullYear()&&new Date(r.smart_date).getMonth()<=m).length;
                  const avM=maturati-usatiFino+(myExtra||0);
                  const isFut=m>today.getMonth();
                  return(<div key={m} style={{marginBottom:10,opacity:isFut?.5:1}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                      <span style={{fontSize:12,color:m===today.getMonth()?BLK:SUB,fontWeight:m===today.getMonth()?700:400}}>{MON[m].slice(0,3)}</span>
                      <div style={{display:"flex",gap:10,fontSize:10,fontFamily:"'Space Mono',monospace"}}>
                        <span style={{color:SUB}}>mat. {maturati}</span>
                        <span style={{color:used>0?BLK:"#CCC"}}>usati {used}</span>
                        <span style={{fontWeight:700,color:avM>0?"#1A7A3A":"#C0392B"}}>→ {avM}</span>
                      </div>
                    </div>
                    <div style={{height:5,borderRadius:3,background:"#EAEAEA",overflow:"hidden"}}>
                      <div style={{height:"100%",borderRadius:3,background:used>=myUR.days_per_month?"#C0392B":BLK,width:`${Math.min((used/myUR.days_per_month)*100,100)}%`}}/>
                    </div>
                  </div>);
                })}
              </div>
              <div className="card">
                <div className="st">Storico richieste</div>
                {reqs.filter(r=>r.user_id===cu.id&&r.type!=="desk").length===0
                  ?<div style={{color:SUB,fontSize:13}}>Nessuna richiesta</div>
                  :reqs.filter(r=>r.user_id===cu.id&&r.type!=="desk").slice(0,20).map(r=>(<div key={r.id} style={{display:"flex",alignItems:"center",gap:9,marginBottom:10}}>
                    <span>{r.status==="approved"?"🏠":r.status==="pending"?"⏳":"❌"}</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:600,fontFamily:"'Space Mono',monospace"}}>{r.smart_date}</div>
                      {r.note&&<div style={{fontSize:11,color:SUB}}>📍 {r.note}</div>}
                    </div>
                    {r.is_emergency&&<span className="tem tag" style={{fontSize:9}}>em.</span>}
                    <span className={tagCls(r.status)} style={{fontSize:10}}>{tagTxt(r.status)}</span>
                  </div>))}
              </div>
            </div>
          </div>}

          {/* ══ LE MIE RICHIESTE ══ */}
          {tab==="mystatus"&&<div className="page">
            <h1 style={{fontSize:21,fontWeight:700,marginBottom:4}}>Le mie richieste</h1>
            <p style={{color:SUB,fontSize:13,marginBottom:18}}>Gestisci le tue richieste di smart working</p>
            {(()=>{
              const myReqs = reqs.filter(r=>r.user_id===cu.id&&r.type!=="desk")
                .sort((a,b)=>a.smart_date.localeCompare(b.smart_date));
              if(myReqs.length===0) return <div className="card" style={{color:SUB}}>Nessuna richiesta</div>;
              // Raggruppa date consecutive
              const makeGroups = (list) => {
                const groups=[];
                for(const r of list){
                  const last=groups[groups.length-1];
                  const lastR=last?.reqs[last.reqs.length-1];
                  const isConsec=last&&lastR&&lastR.status===r.status&&lastR.cancel_requested===r.cancel_requested&&(()=>{
                    const diff=Math.round((new Date(r.smart_date+"T12:00:00")-new Date(lastR.smart_date+"T12:00:00"))/86400000);
                    return diff>=1&&diff<=3;
                  })();
                  if(isConsec) last.reqs.push(r);
                  else groups.push({reqs:[r]});
                }
                return groups;
              };
              const futureReqs = myReqs.filter(r=>r.smart_date>=todayStr);
              const pastReqs = myReqs.filter(r=>r.smart_date<todayStr).reverse();
              const futureGroups = makeGroups(futureReqs);
              const pastGroups = makeGroups(pastReqs.slice().reverse()).reverse();
              const ReqCard = ({g})=>{
                const first=g.reqs[0]; const last=g.reqs[g.reqs.length-1];
                const isRange=g.reqs.length>1;
                return(<div className="card" style={{padding:"12px 16px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                    <span style={{fontSize:20}}>{first.cancel_requested?"🗑":first.status==="approved"?"🏠":first.status==="pending"?"⏳":"❌"}</span>
                    <div style={{flex:1,minWidth:160}}>
                      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:3,flexWrap:"wrap"}}>
                        {isRange
                          ? <span style={{fontFamily:"'Space Mono',monospace",fontSize:13,fontWeight:700}}>{first.smart_date} → {last.smart_date} <span style={{fontSize:11,fontWeight:400,color:SUB}}>({g.reqs.length} gg)</span></span>
                          : <span style={{fontFamily:"'Space Mono',monospace",fontSize:13,fontWeight:700}}>{first.smart_date}</span>}
                        {first.is_emergency&&<span className="tem tag" style={{fontSize:10}}>emergenza</span>}
                        {first.cancel_requested
                          ? <span style={{background:"#F3E8FF",color:"#6B21A8",borderRadius:20,padding:"2px 9px",fontSize:11,fontWeight:600}}>Cancellazione in attesa</span>
                          : <span className={tagCls(first.status)} style={{fontSize:10}}>{tagTxt(first.status)}</span>}
                      </div>
                      {first.note&&<div style={{fontSize:11,color:SUB}}>Nota: {first.note}</div>}
                      <div style={{fontSize:10,color:"#BBB",marginTop:2}}>{first.created_at?.split("T")[0]}</div>
                    </div>
                    {!first.cancel_requested&&(first.status==="approved"||first.status==="pending")&&
                      <button className="bsec" onClick={()=>setCancelTarget(first)}>{isRange?"Cancella gruppo":"Cancella"}</button>}
                  </div>
                </div>);
              };
              return <div style={{display:"flex",flexDirection:"column",gap:16}}>
                {/* Prossimi SW */}
                {futureGroups.length>0&&<div>
                  <div style={{fontSize:12,fontWeight:700,color:SUB,marginBottom:8,textTransform:"uppercase",letterSpacing:1}}>📅 Prossimi SW ({futureReqs.length})</div>
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {futureGroups.map((g,i)=><ReqCard key={i} g={g}/>)}
                  </div>
                </div>}
                {/* SW passati — accordion */}
                {pastGroups.length>0&&<details style={{cursor:"pointer"}}>
                  <summary style={{fontSize:12,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:1,listStyle:"none",display:"flex",alignItems:"center",gap:6,marginBottom:8,userSelect:"none"}}>
                    <span>▶</span> SW passati ({pastReqs.length})
                  </summary>
                  <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:8}}>
                    {pastGroups.map((g,i)=><ReqCard key={i} g={g}/>)}
                  </div>
                </details>}
              </div>;
            })()}
          </div>}

          {/* ══ COMUNICAZIONI ══ */}
          {tab==="messages"&&<div className="page">
            <h1 style={{fontSize:21,fontWeight:700,marginBottom:4}}>Comunicazioni</h1>
            <p style={{color:SUB,fontSize:13,marginBottom:18}}>{isAdmin?"Bacheca messaggi, assistenza e richieste del team":"Scrivi all'amministrazione o apri una richiesta di assistenza"}</p>

            {/* Tab switcher */}
            <div style={{display:"flex",gap:8,marginBottom:20,borderBottom:"2px solid #F0F0F0",paddingBottom:0}}>
              {[["msg","💬 Messaggi"],["assistenza","🎫 Assistenza"],...(isAdmin?[["storico","👤 Per utente"]]:[])].map(([id,label])=>(
                <button key={id} onClick={()=>setMsgTab(id)} style={{background:"none",border:"none",borderBottom:msgTab===id?"2px solid #1A1A1A":"2px solid transparent",marginBottom:-2,padding:"6px 14px",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:msgTab===id?700:400,cursor:"pointer",color:msgTab===id?"#1A1A1A":"#888",transition:"all .15s"}}>{label}</button>
              ))}
            </div>

            {/* ── MESSAGGI LIBERI ── */}
            {msgTab==="msg"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}} className="g2">
              <div className="card">
                <div className="st">{isAdmin?"Tutti i messaggi":"Invia un messaggio"}</div>
                {!isAdmin&&<div style={{marginBottom:18}}>
                  <Lbl t="Messaggio libero"/>
                  <textarea rows={4} defaultValue="" onChange={e=>{msgRef.current=e.target.value;setMsgVal(e.target.value);}} style={{resize:"none",marginBottom:10}} placeholder="Scrivi una domanda, un commento, o una richiesta all'amministrazione..."/>
                  <button className="bp" onClick={simulating?()=>showToast("Solo visualizzazione",false):sendMessage} style={{width:"100%"}}>Invia messaggio →</button>
                </div>}
                {messages.filter(m=>isAdmin?(!m.msg_type||m.msg_type==="msg"):m.user_id===cu.id&&(!m.msg_type||m.msg_type==="msg")).map(m=>{
                  const u=team.find(t=>t.id===m.user_id);
                  return(<MessageItem key={m.id} m={m} u={u} isAdmin={isAdmin} onDelete={()=>delMessage(m.id)} onReply={txt=>replyMessage(m.id,txt)}/>);
                })}
              </div>
              <div className="card">
                <div className="st">Come comunicare</div>
                <div style={{color:SUB,fontSize:13,lineHeight:1.7}}>
                  <div style={{marginBottom:8}}>💬 <strong>Messaggi liberi</strong> — domande generali, commenti rapidi.</div>
                  <div style={{marginBottom:8}}>🎫 <strong>Assistenza</strong> — problemi, richieste formali, suggerimenti. {isAdmin?"Gestisci le richieste del team dalla tab Assistenza.":"Riceverai una risposta dedicata."}</div>
                  <div style={{fontSize:11,color:"#AAA",marginTop:12}}>Le risposte dell'amministrazione appariranno sotto il tuo messaggio.</div>
                </div>
              </div>
            </div>}

            {/* ── ASSISTENZA ── */}
            {msgTab==="assistenza"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}} className="g2">
              <div className="card">
                <div className="st">{isAdmin?"🎫 Richieste ricevute":"Apri una richiesta"}</div>
                {/* ADMIN: lista richieste */}
                {isAdmin&&<>{messages.filter(m=>m.msg_type&&m.msg_type!=="msg").length===0&&<div style={{color:SUB,fontSize:13,textAlign:"center",padding:"20px 0"}}>Nessuna richiesta aperta</div>}
                {messages.filter(m=>m.msg_type&&m.msg_type!=="msg").map(m=>{
                  const u=team.find(t=>t.id===m.user_id);
                  const catLabel={domanda:"❓ Domanda",problema:"🔧 Problema",richiesta:"📋 Richiesta",suggerimento:"💡 Suggerimento"}[m.msg_type]||m.msg_type;
                  return(
                    <div key={m.id} style={{padding:"12px 0",borderBottom:"1px solid #F2F2F2"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                        <div style={{width:30,height:30,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#FFF",background:u?.color||"#7A9BAD",flexShrink:0}}>{u?.avatar_initials}</div>
                        <div style={{flex:1}}><div style={{fontSize:12,fontWeight:700}}>{u?.name}</div><div style={{fontSize:10,color:SUB}}>{m.created_at?.split("T")[0]}</div></div>
                        <span style={{background:m.admin_reply?"#D4EDDA":"#FFF3CD",color:m.admin_reply?"#1A7A3A":"#856404",borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:700}}>{m.admin_reply?"✓ Risposto":"In attesa"}</span>
                        <span style={{background:"#F0F0F0",borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:600,color:"#555"}}>{catLabel}</span>
                        <button onClick={()=>delMessage(m.id)} style={{background:"#FEE8E8",color:"#C0392B",border:"none",borderRadius:7,padding:"4px 9px",fontSize:11,cursor:"pointer"}}>🗑</button>
                      </div>
                      <div style={{fontSize:13,color:"#1A1A1A",lineHeight:1.5,paddingLeft:38}}>{m.content}</div>
                      {m.admin_reply&&<div style={{marginTop:8,marginLeft:38,padding:"8px 11px",background:"#F0F5F8",borderRadius:8,borderLeft:"3px solid #7A9BAD"}}><div style={{fontSize:10,color:"#7A9BAD",fontWeight:700,marginBottom:3}}>RISPOSTA ADMIN</div><div style={{fontSize:12}}>{m.admin_reply}</div></div>}
                      {!m.admin_reply&&<div style={{paddingLeft:38,marginTop:8}}><ReplyBox onSend={txt=>replyMessage(m.id,txt)}/></div>}
                    </div>
                  );
                })}</>}
                {/* UTENTE: form invio */}
                {!isAdmin&&<><div style={{marginBottom:14}}>
                  <Lbl t="Tipo di richiesta"/>
                  <select value={suppCat} onChange={e=>setSuppCat(e.target.value)} style={{marginBottom:0}}>
                    <option value="domanda">❓ Domanda</option>
                    <option value="problema">🔧 Problema tecnico</option>
                    <option value="richiesta">📋 Richiesta formale</option>
                    <option value="suggerimento">💡 Suggerimento</option>
                  </select>
                </div>
                <div style={{marginBottom:14}}>
                  <Lbl t="Descrivi la tua richiesta"/>
                  <textarea rows={5} value={suppText} onChange={e=>setSuppText(e.target.value)} style={{resize:"none",marginBottom:0}} placeholder="Descrivi nel dettaglio la tua richiesta o il problema riscontrato..."/>
                </div>
                <button className="bp" onClick={simulating?()=>showToast("Solo visualizzazione",false):sendSupport} disabled={suppBusy||!suppText.trim()} style={{width:"100%",opacity:suppBusy||!suppText.trim()?.5:1}}>
                  {suppBusy?"Invio in corso...":"Invia richiesta →"}
                </button>
                {/* Storico richieste proprie */}
                {messages.filter(m=>m.user_id===cu.id&&m.msg_type&&m.msg_type!=="msg").length>0&&<>
                  <div style={{fontSize:12,fontWeight:700,marginTop:20,marginBottom:10,color:"#666"}}>Le mie richieste precedenti</div>
                  {messages.filter(m=>m.user_id===cu.id&&m.msg_type&&m.msg_type!=="msg").map(m=>{
                    const catLabel={domanda:"❓",problema:"🔧",richiesta:"📋",suggerimento:"💡"}[m.msg_type]||"";
                    return(
                      <div key={m.id} style={{padding:"10px 0",borderBottom:"1px solid #F5F5F5"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <span style={{fontSize:13}}>{catLabel}</span>
                          <span style={{fontSize:11,fontWeight:600,flex:1,color:"#333"}}>{m.content.slice(0,60)}{m.content.length>60?"…":""}</span>
                          <span style={{background:m.admin_reply?"#D4EDDA":"#FFF3CD",color:m.admin_reply?"#1A7A3A":"#856404",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:700}}>{m.admin_reply?"Risposto":"In attesa"}</span>
                        </div>
                        {m.admin_reply&&<div style={{padding:"7px 10px",background:"#F0F5F8",borderRadius:7,borderLeft:"3px solid #7A9BAD",fontSize:12}}>💬 {m.admin_reply}</div>}
                      </div>
                    );
                  })}
                </>}
                </>}
              </div>
              <div className="card">
                <div className="st">Quando usare l'assistenza</div>
                {[
                  ["❓","Domanda","Hai dubbi su come funziona il portale, le regole SW, o come prenotare."],
                  ["🔧","Problema tecnico","Qualcosa non funziona correttamente nell'app."],
                  ["📋","Richiesta formale","Vuoi richiedere giorni extra, una modifica al tuo profilo, o una situazione speciale."],
                  ["💡","Suggerimento","Hai un'idea per migliorare il portale o il modo in cui gestiamo lo smart working."],
                ].map(([ic,title,desc])=>(
                  <div key={title} style={{display:"flex",gap:10,padding:"10px 0",borderBottom:"1px solid #F5F5F5"}}>
                    <span style={{fontSize:18,flexShrink:0}}>{ic}</span>
                    <div><div style={{fontSize:13,fontWeight:700,marginBottom:2}}>{title}</div><div style={{fontSize:11,color:SUB,lineHeight:1.5}}>{desc}</div></div>
                  </div>
                ))}
              </div>
            </div>}

            {/* ── STORICO PER UTENTE (solo admin) ── */}
            {msgTab==="storico"&&isAdmin&&<div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div className="card">
                <div className="st">Seleziona utente</div>
                <select value={storicoUser} onChange={e=>setStoricoUser(e.target.value)} style={{width:"100%",padding:"8px 12px",borderRadius:8,border:"1px solid "+BDR,fontSize:13,fontFamily:"'DM Sans',sans-serif"}}>
                  <option value="">— Scegli un utente —</option>
                  {team.filter(u=>!u.is_admin).sort((a,b)=>a.name.localeCompare(b.name)).map(u=>(
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              {storicoUser&&(()=>{
                const u = team.find(t=>t.id===storicoUser);
                const userMsgs = messages.filter(m=>m.user_id===storicoUser).sort((a,b)=>new Date(a.created_at)-new Date(b.created_at));
                const catLabel = {msg:"💬 Messaggio",domanda:"❓ Domanda",problema:"🔧 Problema",richiesta:"📋 Richiesta",suggerimento:"💡 Suggerimento"};
                return (
                  <div className="card">
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,paddingBottom:12,borderBottom:"1px solid "+BDR}}>
                      <div className="av" style={{background:u?.color||LYB,width:38,height:38,fontSize:13}}>{u?.avatar_initials}</div>
                      <div>
                        <div style={{fontSize:15,fontWeight:700}}>{u?.name}</div>
                        <div style={{fontSize:11,color:SUB}}>{userMsgs.length} comunicazioni totali · {userMsgs.filter(m=>!m.admin_reply).length} senza risposta</div>
                      </div>
                    </div>
                    {userMsgs.length===0&&<div style={{textAlign:"center",color:SUB,fontSize:13,padding:"20px 0"}}>Nessuna comunicazione</div>}
                    <div style={{display:"flex",flexDirection:"column",gap:12}}>
                      {userMsgs.map(m=>(
                        <div key={m.id} style={{padding:"12px 14px",borderRadius:10,border:"1px solid "+BDR,background:m.admin_reply?"#F8FAF8":"#FFFDF5"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                            <span style={{background:"#F0F0F0",borderRadius:5,padding:"2px 8px",fontSize:11,fontWeight:600,color:"#555"}}>{catLabel[m.msg_type||"msg"]||m.msg_type}</span>
                            <span style={{fontSize:11,color:SUB,fontFamily:"'Space Mono',monospace"}}>{new Date(m.created_at).toLocaleDateString("it-IT",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
                            <span style={{marginLeft:"auto",background:m.admin_reply?"#D4EDDA":"#FFF3CD",color:m.admin_reply?"#1A7A3A":"#856404",borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:700}}>{m.admin_reply?"✓ Risposto":"In attesa"}</span>
                            <button onClick={()=>delMessage(m.id)} style={{background:"#FEE8E8",color:"#C0392B",border:"none",borderRadius:6,padding:"3px 8px",fontSize:11,cursor:"pointer"}}>🗑</button>
                          </div>
                          <div style={{fontSize:13,color:"#1A1A1A",lineHeight:1.5,marginBottom:m.admin_reply?8:0}}>{m.content}</div>
                          {m.admin_reply&&(
                            <div style={{padding:"8px 11px",background:"#F0F5F8",borderRadius:8,borderLeft:"3px solid #7A9BAD"}}>
                              <div style={{fontSize:10,color:"#7A9BAD",fontWeight:700,marginBottom:3}}>RISPOSTA ADMIN</div>
                              <div style={{fontSize:12}}>{m.admin_reply}</div>
                            </div>
                          )}
                          {!m.admin_reply&&<div style={{marginTop:8}}><ReplyBox onSend={txt=>replyMessage(m.id,txt)}/></div>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>}
          </div>}

          {/* ══ PANNELLO ADMIN ══ */}
          {tab==="admin"&&isAdmin&&<AdminPanel
            reqs={reqs} swaps={swaps} blocked={blocked} desks={desks} team={team}
            rules={rules} editRules={editRules} setEditRules={setEditRules} uSet={uSet}
            aTab={aTab} setATab={setATab} mvM={mvM} mvY={mvY} setMvM={setMvM} setMvY={setMvY}
            fUser={fUser} fDate={fDate} fNote={fNote} setFUser={setFUser} setFDate={setFDate} setFNote={setFNote}
            blDate={blDate} blDateTo={blDateTo} blReas={blReas} setBlDate={setBlDate} setBlDateTo={setBlDateTo} setBlReas={setBlReas}
            ndName={ndName} ndRoom={ndRoom} setNdName={setNdName} setNdRoom={setNdRoom}
            ndAssigned={ndAssigned} setNdAssigned={setNdAssigned}
            ndExpires={ndExpires} setNdExpires={setNdExpires}
            nuName={nuName} nuEmail={nuEmail} nuRole={nuRole} setNuName={setNuName} setNuEmail={setNuEmail} setNuRole={setNuRole}
            exUid={exUid} exDays={exDays} exNote={exNote} setExUid={setExUid} setExDays={setExDays} setExNote={setExNote}
            usTgt={usTgt} usDm={usDm} usDy={usDy} usEm={usEm} usNote={usNote}
            setUsTgt={setUsTgt} setUsDm={setUsDm} setUsDy={setUsDy} setUsEm={setUsEm} setUsNote={setUsNote}
            editU={editU} setEditU={setEditU}
            hReq={hReq} delReq={delReq} hCancel={hCancel} hSwap={hSwap}
            forceSW={forceSW} blDay={blDay} unblDay={unblDay} togDesk={togDesk} delDesk={delDesk} addDesk={addDesk}
            saveRules={saveRules} saveEx={saveEx} saveUS={saveUS} resetUS={resetUS} updUser={updUser} delUser={delUser}
            editBooking={editBooking} setEditBooking={setEditBooking} saveEditBooking={saveEditBooking} delBooking={delBooking}
            getUR={getUR} yUsed={yUsed} mUsed={mUsed} emUsed={emUsed} isBl={isBl} scnt={scnt} dim={dim} fd={fd}
            isFixed={isFixed} isFullRemote={isFullRemote} pendCnt={pendCnt} cnCnt={cnCnt} mvData={mvData} deskRooms={deskRooms}
            recUser={recUser} recDay={recDay} recFrom={recFrom} recTo={recTo} recNote={recNote}
            setRecUser={setRecUser} setRecDay={setRecDay} setRecFrom={setRecFrom} setRecTo={setRecTo} setRecNote={setRecNote}
            forceRecurring={forceRecurring}
            realUser={realUser} setViewAs={setViewAs} setTab={setTab} load={load} setTeam={setTeam}
            logoUrl={logoUrl}
            notifSettings={notifSettings} setNotifSettings={setNotifSettings}
            showToast={showToast} today={today} todayStr={todayStr}
            reqSearch={reqSearch} setReqSearch={setReqSearch}
            debugLogs={debugLogs} setDebugLogs={setDebugLogs}
            excPeriods={excPeriods}
            vacTgt={vacTgt} setVacTgt={setVacTgt} vacFrom={vacFrom} setVacFrom={setVacFrom} vacTo={vacTo} setVacTo={setVacTo}
          />}

        </div>{/* end main */}
      </div>{/* end flex row */}

      {/* ══ FLOATING GUIDA BUTTON ══ */}
      <button onClick={()=>setGuideOpen(true)} title="Guida rapida" style={{position:"fixed",bottom:24,right:24,width:44,height:44,borderRadius:"50%",background:"#1A1A1A",color:"white",border:"none",fontSize:20,cursor:"pointer",boxShadow:"0 4px 16px rgba(0,0,0,.25)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontFamily:"'Space Mono',monospace"}}>?</button>

      {/* ══ MODAL GUIDA RAPIDA ══ */}
      {guideOpen&&<div className="mo" onClick={()=>setGuideOpen(false)}>
        <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:480,maxHeight:"85vh",overflowY:"auto"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
            <div><div style={{fontSize:17,fontWeight:700}}>📖 Guida rapida</div><div style={{fontSize:12,color:"#888"}}>Tutto quello che ti serve sapere</div></div>
            <button onClick={()=>setGuideOpen(false)} style={{background:"#F0F0F0",border:"none",borderRadius:8,width:32,height:32,cursor:"pointer",fontSize:16}}>✕</button>
          </div>
          {[
            {ic:"📅",title:"Prenotare uno Smart Working",items:["Vai su «Prenota Smart Working» nel menu","Seleziona una data disponibile (non grigia)","Inserisci la sede da cui lavorerai","Invia — l'admin approverà la richiesta"]},
            {ic:"⏰",title:"Regole principali",items:[`Anticipo minimo: ${rules.advance_days} giorni (emergenze: 2 gg)`,`Max ${rules.days_per_month} SW al mese, ${rules.days_per_year} all'anno`,`Max ${rules.max_per_day} persone in SW lo stesso giorno`,"1 SW a settimana (lun–dom)"]},
            {ic:"🚨",title:"Modalità emergenza",items:["Solo 2 giorni di anticipo","Richiede motivazione valida","Scala dal budget annuo","Attivabile con il toggle nella schermata prenota"]},
            {ic:"🔄",title:"Scambio SW",items:["Vai su «Scambia SW»","Scegli il tuo SW da cedere","Scegli il collega e il suo SW","La proposta viene inviata all'admin"]},
            {ic:"🪑",title:"Postazioni ufficio",items:["Vai su «Postazioni» per prenotare una scrivania","Scegli mattina o pomeriggio","Vedi chi è in ufficio e dove"]},
            {ic:"💬",title:"Assistenza",items:["Sezione «Comunicazioni» → tab Assistenza","Scegli il tipo: domanda, problema, richiesta, suggerimento","L'admin risponderà direttamente nel portale"]},
          ].map(({ic,title,items})=>(
            <div key={title} style={{marginBottom:16,padding:"14px",background:"#FAFAFA",borderRadius:12,border:"1px solid #F0F0F0"}}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:10,display:"flex",alignItems:"center",gap:7}}><span style={{fontSize:17}}>{ic}</span>{title}</div>
              {items.map((it,i)=>(
                <div key={i} style={{display:"flex",gap:8,marginBottom:5,fontSize:12,color:"#555",lineHeight:1.5}}>
                  <span style={{color:"#7A9BAD",fontWeight:700,flexShrink:0}}>→</span>{it}
                </div>
              ))}
            </div>
          ))}
          <button onClick={()=>{setGuideOpen(false);setTab("guide");}} style={{width:"100%",background:"#F5F5F5",border:"1px solid #E2E2E2",borderRadius:10,padding:10,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",color:"#333",marginTop:4}}>
            📖 Vai alla guida completa →
          </button>
          <button onClick={()=>{setGuideOpen(false);setTab("messages");setMsgTab("assistenza");}} style={{width:"100%",background:"#1A1A1A",border:"none",borderRadius:10,padding:10,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",color:"white",marginTop:8}}>
            🎫 Apri una richiesta di assistenza →
          </button>
        </div>
      </div>}

      {/* ══ MODAL GOOGLE CALENDAR UTENTE ══ */}
      {gcalModal&&!simulating&&<div className="mo" onClick={()=>setGcalModal(false)}>
        <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:460}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
            <span style={{fontSize:32}}>📅</span>
            <div><div style={{fontSize:16,fontWeight:700}}>Sincronizza Google Calendar</div>
              <div style={{fontSize:12,color:SUB}}>Aggiungi i tuoi smart working al tuo calendario</div>
            </div>
          </div>

          <div style={{background:"#EEF2FF",border:"1px solid #C0CCF0",borderRadius:10,padding:14,marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:"#2040A0",marginBottom:8}}>Come funziona</div>
            <ol style={{fontSize:12,color:"#304080",paddingLeft:18,lineHeight:2.1}}>
              <li>Clicca il pulsante qui sotto — si apre Google Calendar</li>
              <li>Accedi con il tuo account Google</li>
              <li>Clicca <strong>"+ Aggiungi"</strong> per importare i tuoi SW approvati</li>
            </ol>
          </div>

          {/* Lista completa SW approvati futuri con link GCal individuale */}
          {(()=>{
            const myApproved = reqs
              .filter(r=>r.user_id===cu?.id&&r.status==="approved"&&r.smart_date&&r.type!=="desk"&&r.smart_date>=todayStr)
              .sort((a,b)=>a.smart_date.localeCompare(b.smart_date));
            if(!myApproved.length) return(
              <div style={{textAlign:"center",padding:20,color:SUB,fontSize:13}}>
                Nessuno smart working approvato futuro da sincronizzare.
              </div>
            );
            const makeGcalUrl = (r) => {
              const ds = r.smart_date.replace(/-/g,"");
              const title = encodeURIComponent(`Smart Working — ${r.note||"Casa"}`);
              const dtStart = r.half_day==="afternoon" ? ds+"T130000" : ds+"T090000";
              const dtEnd   = r.half_day==="morning"   ? ds+"T130000" : ds+"T180000";
              const details = encodeURIComponent("Smart Working LikeYou Srl" + (r.note ? "\n📍 "+r.note : ""));
              return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dtStart}/${dtEnd}&details=${details}`;
            };
            return(
              <div style={{marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div style={{fontSize:11,color:SUB,textTransform:"uppercase",letterSpacing:.8}}>
                    {myApproved.length} SW approvati futuri
                  </div>
                  <a href={myApproved.slice(0,1).map(makeGcalUrl)[0]} target="_blank" rel="noopener noreferrer"
                    style={{fontSize:11,color:"white",fontWeight:700,textDecoration:"none",
                      background:"#1A73E8",borderRadius:7,padding:"4px 10px"}}>
                    Apri prossimo in GCal
                  </a>
                </div>
                <div style={{maxHeight:260,overflowY:"auto",borderRadius:9,border:"1px solid #F0F0F0"}}>
                  {myApproved.map((r,i)=>(
                    <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                      borderBottom:"1px solid #F5F5F5",background:i%2===0?"white":"#FAFAFA"}}>
                      <span style={{fontFamily:"'Space Mono',monospace",fontSize:12,minWidth:90}}>{r.smart_date}</span>
                      <span style={{fontSize:11,color:SUB,flex:1}}>
                        {r.half_day==="morning"?"🌅 Mattina":r.half_day==="afternoon"?"🌇 Pomeriggio":"☀️ Giornata intera"}
                        {r.note&&<span style={{marginLeft:6}}>· 📍 {r.note}</span>}
                      </span>
                      <a href={makeGcalUrl(r)} target="_blank" rel="noopener noreferrer"
                        style={{fontSize:11,color:"#1A73E8",fontWeight:600,textDecoration:"none",
                          background:"#EEF2FF",border:"1px solid #C0CCF0",borderRadius:6,padding:"3px 9px",flexShrink:0}}>
                        + GCal
                      </a>
                    </div>
                  ))}
                </div>
                <div style={{fontSize:10,color:SUB,marginTop:8,lineHeight:1.6}}>
                  Clicca <strong>+ GCal</strong> su ogni riga per aggiungere quell'evento al tuo Google Calendar.
                </div>
              </div>
            );
          })()}

          <div style={{background:"#F8F8F8",borderRadius:9,padding:12,fontSize:11,color:SUB,lineHeight:1.7,marginBottom:16}}>
            <strong>Suggerimento:</strong> Per una sincronizzazione automatica completa, chiedi all'amministratore di attivare l'integrazione Google Calendar nelle impostazioni dell'app.
          </div>
          <button onClick={()=>setGcalModal(false)} className="bp" style={{width:"100%"}}>Chiudi</button>
        </div>
      </div>}

      {/* ══ MODAL PROFILO UTENTE ══ */}
      {profileModal&&!simulating&&<div className="mo" onClick={()=>setProfileModal(false)}>
        <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:400}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div className="av" style={{background:cu?.color||LYB,width:46,height:46,fontSize:16}}>{cu?.avatar_initials}</div>
              <div><div style={{fontSize:16,fontWeight:700}}>{cu?.name}</div><div style={{fontSize:12,color:SUB}}>{cu?.email}</div></div>
            </div>
            <button onClick={()=>setProfileModal(false)} style={{background:"#F0F0F0",border:"none",borderRadius:8,width:32,height:32,cursor:"pointer",fontSize:16,flexShrink:0}}>✕</button>
          </div>
          <div style={{marginBottom:13}}>
            <label className="lb">Data di compleanno</label>
            <input type="date" value={profileBday||cu?.birthday||""} onChange={e=>setProfileBday(e.target.value)}/>
            <div style={{fontSize:10,color:SUB,marginTop:4}}>Tutti i colleghi vedranno il tuo compleanno nella dashboard 🎂</div>
          </div>
          <div style={{marginBottom:13}}>
            <label className="lb">Sede contrattuale smart working</label>
            <input type="text" value={profileSwLoc||(cu?.sw_location||"")} onChange={e=>setProfileSwLoc(e.target.value)} placeholder="Es. Via Roma 10, Bologna"/>
            <div style={{fontSize:10,color:SUB,marginTop:4}}>
              Indirizzo da cui lavori in smart working — corrisponde al domicilio in busta paga.
              Verrà usata automaticamente per tutte le tue richieste SW.
            </div>
            {profileSwLoc && profileSwLoc !== cu?.sw_location && (
              <div style={{background:"#FFF3CD",border:"1px solid #F0C040",borderRadius:8,padding:"8px 10px",marginTop:6,fontSize:11,color:"#856404",lineHeight:1.6}}>
                ⚠️ <strong>Attenzione:</strong> modificare la sede aggiorna il domicilio contrattuale e può influire sulla busta paga. Procedi solo se hai cambiato residenza.
              </div>
            )}
          </div>
            {(()=>{
              // vacOn = valore attuale da mostrare: se l'utente ha già toccato il toggle usa quello, altrimenti usa il valore dal DB
              const vacOn = profileVacation !== null ? profileVacation : !!cu?.vacation;
              return(
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderTop:"1px solid "+BDR,marginBottom:16}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>Sono in ferie 🌴</div>
                  <div style={{fontSize:11,color:SUB}}>Il tuo stato sarà visibile a tutti</div>
                </div>
                <button className="tgl" style={{background:vacOn?BLK:"#DDD"}}
                  onClick={()=>setProfileVacation(!vacOn)}>
                  <div className="tglk" style={{left:vacOn?23:3}}/>
                </button>
              </div>
              );
            })()}
          {/* Cambio password */}
          <div style={{borderTop:"1px solid "+BDR,paddingTop:14,marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:10}}>🔐 Cambia password</div>
            {(()=>{
              return null; // handled by profilePwSection below
            })()}
            <ProfilePwSection supabase={supabase} cu={cu} showToast={showToast}/>
          </div>

          <div style={{display:"flex",gap:10}}>
            <button className="bp" onClick={async()=>{
              const vacVal = profileVacation !== null ? profileVacation : !!cu?.vacation;
              const locVal = profileSwLoc.trim() || cu?.sw_location || "";
              const bdVal  = profileBday || cu?.birthday || null;
              const updates = {birthday:bdVal, vacation:vacVal, sw_location:locVal};
              const{data:upData,error}=await supabase.from("profiles").update(updates).eq("id",cu.id).select();
              console.log("[Profile save]", {updates, upData, error, userId: cu.id});
              if(error){showToast("Errore: "+error.message,false);return;}
              if(!upData||upData.length===0){showToast("Errore: nessuna riga aggiornata — contatta l'admin",false);return;}
              setRealUser(prev => { const u=prev?{...prev,...updates}:prev; try{if(u)safeStore.set('sw_user',JSON.stringify(u));}catch{} return u; });
              showToast("Profilo aggiornato ✓");
              setProfileModal(false);setProfileBday("");setProfileSwLoc("");setProfileVacation(null);
              load();
            }} style={{flex:1}}>Salva</button>
            <button onClick={()=>{setProfileModal(false);setProfileBday("");setProfileSwLoc("");setProfileVacation(null);}} className="bsec" style={{flex:1,borderRadius:10,padding:10}}>Annulla</button>
          </div>
        </div>
      </div>}

      {/* ══ MODAL CANCELLAZIONE ══ */}
      {cancelTarget&&<div className="mo" onClick={()=>setCancelTarget(null)}>
        <div className="md" onClick={e=>e.stopPropagation()}>
          <div className="st">Richiesta di cancellazione</div>
          <p style={{color:SUB,fontSize:13,marginBottom:18,lineHeight:1.6}}>
            Stai richiedendo la cancellazione dello smart working del <strong style={{color:LYB}}>{cancelTarget.smart_date}</strong>.<br/>
            L'amministratore dovrà approvare la richiesta prima che diventi effettiva.
          </p>
          <Lbl t="Motivazione (opzionale)"/>
          {/* FIX BUG TEXTAREA: usa input non controllato con ref */}
          <textarea
            rows={3}
            defaultValue=""
            onChange={e=>{ cnNoteRef.current=e.target.value; setCnNoteVal(e.target.value); }}
            style={{resize:"none",marginBottom:18,width:"100%"}}
            placeholder="Indica il motivo..."
          />
          <div style={{display:"flex",gap:10}}>
            <button className="bp" onClick={()=>reqCancel(cancelTarget.id)} style={{flex:1}}>Invia richiesta</button>
            <button onClick={()=>{setCancelTarget(null);cnNoteRef.current="";setCnNoteVal("");}} className="bsec" style={{flex:1,borderRadius:10,padding:10}}>Annulla</button>
          </div>
        </div>
      </div>}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ReplyBox — componente separato per evitare il bug del focus
// ─────────────────────────────────────────────────────────────
function ReplyBox({ onSend }) {
  const [val, setVal] = useState("");
  return (
    <div style={{marginTop:10,display:"flex",gap:8}}>
      <textarea
        rows={2}
        value={val}
        onChange={e=>setVal(e.target.value)}
        placeholder="Scrivi una risposta..."
        style={{flex:1,resize:"none",fontSize:12,padding:"7px 10px",borderRadius:8,border:"1px solid #E2E2E2",fontFamily:"'DM Sans',sans-serif",outline:"none",background:"#FAFAFA"}}
      />
      <button
        onClick={()=>{if(val.trim()){onSend(val.trim());setVal("");}}}
        style={{background:"#1A1A1A",color:"#FFF",border:"none",borderRadius:8,padding:"7px 13px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}
      >↵</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MessageItem — componente separato per evitare hook-in-map
// ─────────────────────────────────────────────────────────────
function MessageItem({ m, u, isAdmin, onDelete, onReply }) {
  const LYB="#7A9BAD", BLK="#1A1A1A", SUB="#888";
  return (
    <div style={{padding:"12px 0",borderBottom:"1px solid #F2F2F2"}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:9}}>
        <div style={{width:34,height:34,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#FFF",background:u?.color||LYB,flexShrink:0}}>{u?.avatar_initials}</div>
        <div style={{flex:1}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
            <span style={{fontSize:12,fontWeight:700}}>{u?.name}</span>
            <span style={{fontSize:10,color:SUB}}>{m.created_at?.split("T")[0]}</span>
          </div>
          <div style={{fontSize:13,color:BLK,lineHeight:1.5}}>{m.content}</div>
          {m.admin_reply&&<div style={{marginTop:8,padding:"8px 11px",background:"#F0F5F8",borderRadius:8,borderLeft:"3px solid "+LYB}}>
            <div style={{fontSize:10,color:LYB,fontWeight:700,marginBottom:3}}>RISPOSTA ADMIN</div>
            <div style={{fontSize:12,color:BLK}}>{m.admin_reply}</div>
          </div>}
        </div>
        {isAdmin&&<button onClick={onDelete} style={{background:"#FEE8E8",color:"#C0392B",border:"none",borderRadius:7,padding:"5px 11px",fontSize:12,cursor:"pointer",flexShrink:0}}>🗑</button>}
      </div>
      {isAdmin&&!m.admin_reply&&<ReplyBox onSend={onReply}/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Row / Lbl components (helpers per form)
// ─────────────────────────────────────────────────────────────
function Lbl({ t }) { return <label className="lb">{t}</label>; }
function Row({ children, mb=14 }) {
  return <div style={{marginBottom:mb}}>{children}</div>;
}

// ─────────────────────────────────────────────────────────────
// ADMIN PANEL — componente separato (riduce re-render)
// ─────────────────────────────────────────────────────────────
function AdminPanel(props) {
  const [storicoFiltUser, setStoricoFiltUser] = useState("");
  const [storicoFiltStatus, setStoricoFiltStatus] = useState("");
  const [storicoSearch, setStoricoSearch] = useState("");
  const [pwResetTgt, setPwResetTgt] = useState(null);
  const [pwResetVal, setPwResetVal] = useState("");
  const [pwResetBusy, setPwResetBusy] = useState(false);
  const {
    reqs, swaps, blocked, desks, team, rules, editRules, setEditRules, uSet,
    aTab, setATab, mvM, mvY, setMvM, setMvY,
    fUser, fDate, fNote, setFUser, setFDate, setFNote,
    blDate, blDateTo, blReas, setBlDate, setBlDateTo, setBlReas,
    ndName, ndRoom, setNdName, setNdRoom,
    ndAssigned, setNdAssigned, ndExpires, setNdExpires,
    nuName, nuEmail, nuRole, setNuName, setNuEmail, setNuRole,
    exUid, exDays, exNote, setExUid, setExDays, setExNote,
    usTgt, usDm, usDy, usEm, usNote, setUsTgt, setUsDm, setUsDy, setUsEm, setUsNote,
    editU, setEditU,
    hReq, delReq, hCancel, hSwap,
    forceSW, blDay, unblDay, togDesk, delDesk, addDesk,
    saveRules, saveEx, saveUS, resetUS, updUser, delUser,
    editBooking, setEditBooking, saveEditBooking, delBooking,
    getUR, yUsed, mUsed, emUsed, isBl, scnt, dim, fd, isFixed, isFullRemote,
    pendCnt, cnCnt, mvData, deskRooms,
    recUser, recDay, recFrom, recTo, recNote,
    setRecUser, setRecDay, setRecFrom, setRecTo, setRecNote,
    forceRecurring,
    realUser, setViewAs, setTab, load, setTeam,
    logoUrl,
    notifSettings, setNotifSettings,
    showToast, today, todayStr,
    reqSearch, setReqSearch,
    debugLogs, setDebugLogs,
    excPeriods,
    vacTgt, setVacTgt, vacFrom, setVacFrom, vacTo, setVacTo,
  } = props;

  const [uEditModal, setUEditModal] = useState(null);
  const [usModal, setUsModal]       = useState(null);
  const [excModal, setExcModal]     = useState(null); // { userId, name }
  const [excDateFrom, setExcDateFrom] = useState("");
  const [excDateTo, setExcDateTo]   = useState("");
  const [excNote, setExcNote]       = useState("");
  const [excSaving, setExcSaving]   = useState(false);
  const [welcomeSel, setWelcomeSel] = useState(new Set()); // selected user ids
  const [welcomePsw, setWelcomePsw] = useState(""); // temporary password
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [replyTargets, setRT]       = useState({});

  const LYB="#7A9BAD",BLK="#1A1A1A",WHT="#FFFFFF",BDR="#E2E2E2",SUB="#888",MON=["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
  const DAY=["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
  const AVCOLS=["#7A9BAD","#8A7F9D","#6A9B8F","#A08060","#9A7080","#7A8FA8","#6A8A7A","#A0806A"];

  const addUser = async () => {
    if(!nuName||!nuEmail){showToast("Nome e email obbligatori",false);return;}
    const initials=nuName.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
    // Hash della password temporanea
    const defaultPw = "Likeyou2024!";
    const pwHash = await hashPw(defaultPw);
    const color=AVCOLS[Math.floor(Math.random()*AVCOLS.length)];
    try{
      // Genera UUID lato client e inserisce direttamente in profiles (senza account auth)
      const newId = crypto.randomUUID();
      const{error}=await supabase.from("profiles").insert({id:newId,name:nuName,role:nuRole,avatar_initials:initials,color,email:nuEmail.toLowerCase().trim(),password:pwHash,must_change_password:true});
      if(error)throw error;
      showToast(nuName+" aggiunto ✓");setNuName("");setNuEmail("");setNuRole("employee");
      load();
    }catch(e){showToast("Errore: "+e.message,false);}
  };

  const tagCls = st => `tag t${(st||"p")[0]}`;

  const adTabGroups = [
    { label:"📋 Richieste", tabs:[
      {id:"requests",  l:`Richieste${pendCnt>0?` (${pendCnt})`:""}`},
      {id:"cancels",   l:`Cancellaz.${cnCnt>0?` (${cnCnt})`:""}`},
      {id:"swaps",     l:`Scambi${swaps.filter(s=>s.status==="pending").length>0?` (${swaps.filter(s=>s.status==="pending").length})`:""}`},
    ]},
    { label:"📅 Calendario", tabs:[
      {id:"monthly",   l:"Vista Mensile"},
      {id:"storico",   l:"Storico"},
      {id:"stats",     l:"Statistiche"},
    ]},
    { label:"👥 Team", tabs:[
      {id:"users",     l:"Utenti"},
      {id:"usetsgs",   l:"Impost. Utenti"},
      {id:"force",     l:"Assegna SW"},
      {id:"recurring", l:"Ricorrente"},
      {id:"exceptions",l:`Eccezioni${excPeriods.length>0?` (${excPeriods.length})`:""}` },
    ]},
    { label:"🏢 Ufficio", tabs:[
      {id:"desks",     l:"Postazioni"},
      {id:"calendar",  l:"Gg Bloccati"},
      {id:"closures",  l:"Chiusure"},
    ]},
    { label:"⚙️ Config", tabs:[
      {id:"rules",     l:"Regole SW"},
      {id:"notifs",    l:"Notifiche"},
      {id:"logo",      l:"Logo & App"},
    ]},
    { label:"🧪 Debug", tabs:[
      {id:"debug",     l:"Log attività"},
    ]},
  ];
  const adTabs = adTabGroups.flatMap(g=>g.tabs);
  const activeGroup = adTabGroups.find(g=>g.tabs.some(t=>t.id===aTab));

  return (
    <div className="page">
      {/* MODAL modifica utente */}
      {uEditModal&&<div className="mo" onClick={()=>setUEditModal(null)}>
        <div className="md" onClick={e=>e.stopPropagation()}>
          <div className="st">Modifica utente — {uEditModal.name}</div>
          <div style={{marginBottom:13}}><label className="lb">Nome</label><input type="text" value={uEditModal.name} onChange={e=>setUEditModal({...uEditModal,name:e.target.value})}/></div>
          <div style={{marginBottom:13}}><label className="lb">Ruolo</label><select value={uEditModal.role} onChange={e=>setUEditModal({...uEditModal,role:e.target.value})}><option value="employee">Dipendente</option><option value="collaborator">Collaboratore</option><option value="admin">Amministratore</option></select></div>
          <div style={{marginBottom:10,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderTop:"1px solid "+BDR}}>
            <div><div style={{fontSize:13,fontWeight:600}}>Può fare smart working?</div><div style={{fontSize:11,color:SUB}}>Se null, usa la lista predefinita</div></div>
            <select value={uEditModal.sw_permission==null?"null":uEditModal.sw_permission?"yes":"no"} onChange={e=>setUEditModal({...uEditModal,sw_permission:e.target.value==="null"?null:e.target.value==="yes"})} style={{width:"auto",fontSize:12,padding:"5px 8px"}}>
              <option value="null">Default</option>
              <option value="yes">Sì</option>
              <option value="no">No</option>
            </select>
          </div>
          <div style={{marginBottom:18,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderTop:"1px solid "+BDR}}>
            <div><div style={{fontSize:13,fontWeight:600}}>Full Remote?</div><div style={{fontSize:11,color:SUB}}>Può prenotare postazioni ma non SW</div></div>
            <select value={uEditModal.is_full_remote==null?"null":uEditModal.is_full_remote?"yes":"no"} onChange={e=>setUEditModal({...uEditModal,is_full_remote:e.target.value==="null"?null:e.target.value==="yes"})} style={{width:"auto",fontSize:12,padding:"5px 8px"}}>
              <option value="null">Default</option>
              <option value="yes">Sì — Full Remote</option>
              <option value="no">No</option>
            </select>
          </div>
          <div style={{marginBottom:18,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderTop:"1px solid "+BDR}}>
            <div><div style={{fontSize:13,fontWeight:600}}>Fascia postazione (fissi)</div><div style={{fontSize:11,color:SUB}}>Quando è in ufficio di default</div></div>
            <select value={uEditModal.desk_slot||"full"} onChange={e=>setUEditModal({...uEditModal,desk_slot:e.target.value})} style={{width:"auto",fontSize:12,padding:"5px 8px"}}>
              <option value="full">☀️ Intera giornata</option>
              <option value="morning">🌅 Solo mattina</option>
              <option value="afternoon">🌇 Solo pomeriggio</option>
            </select>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button className="bp" onClick={async()=>{await supabase.from("profiles").update({name:uEditModal.name,role:uEditModal.role,sw_permission:uEditModal.sw_permission,is_full_remote:uEditModal.is_full_remote,desk_slot:uEditModal.desk_slot||"full"}).eq("id",uEditModal.id);showToast("Aggiornato ✓");setUEditModal(null);props.load&&props.load();}} style={{flex:1}}>Salva</button>
            <button onClick={()=>setUEditModal(null)} className="bsec" style={{flex:1,borderRadius:10,padding:10}}>Annulla</button>
          </div>
        </div>
      </div>}

      {/* MODAL impostazioni singolo utente */}
      {usModal&&<div className="mo" onClick={()=>setUsModal(null)}>
        <div className="md" onClick={e=>e.stopPropagation()}>
          <div className="st">Impostazioni — {team.find(t=>t.id===usModal)?.name}</div>
          {[["Giorni SW/mese",usDm,setUsDm],["Giorni SW/anno",usDy,setUsDy],["Giorni emergenza/anno",usEm,setUsEm]].map(([l,v,s])=>(
            <div key={l} style={{marginBottom:13}}><label className="lb">{l}</label><input type="number" value={v} onChange={e=>s(parseInt(e.target.value)||0)}/></div>
          ))}
          <div style={{marginBottom:14}}><label className="lb">Nota interna</label><input type="text" value={usNote} onChange={e=>setUsNote(e.target.value)} placeholder="Motivazione..."/></div>
          {/* Toggle mezza giornata — abilitata solo dall'admin */}
          {(()=>{
            const u=team.find(t=>t.id===usModal);
            const hdOn=!!u?.half_day_enabled;
            return(
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",borderRadius:9,background:"#FAFAFA",border:"1px solid "+BDR,marginBottom:16}}>
              <div>
                <div style={{fontSize:13,fontWeight:600}}>Prenotazione mezza giornata</div>
                <div style={{fontSize:11,color:SUB}}>L'utente può scegliere mattina o pomeriggio (−0.5 gg)</div>
              </div>
              <button className="tgl" style={{background:hdOn?BLK:"#DDD",flexShrink:0}} onClick={async()=>{
                const newVal = !hdOn;
                await supabase.from("profiles").update({half_day_enabled:newVal}).eq("id",usModal);
                // Aggiorna team in memoria per riflettere subito il cambio
                if(props.load) props.load();
                if(props.setTeam) props.setTeam(prev => prev.map(t => t.id===usModal ? {...t, half_day_enabled:newVal} : t));
              }}>
                <div className="tglk" style={{left:hdOn?23:3}}/>
              </button>
            </div>
            );
          })()}
          <div style={{display:"flex",gap:10}}>
            <button className="bp" onClick={()=>{saveUS();setUsModal(null);}} style={{flex:1}}>Salva</button>
            <button onClick={()=>setUsModal(null)} className="bsec" style={{flex:1,borderRadius:10,padding:10}}>Annulla</button>
          </div>
        </div>
      </div>}

      {/* MODAL reset password admin */}
      {pwResetTgt&&<div onClick={()=>setPwResetTgt(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <div onClick={e=>e.stopPropagation()} style={{background:"white",borderRadius:16,padding:26,width:360,maxWidth:"100%",boxShadow:"0 14px 50px rgba(0,0,0,.15)"}}>
          <div style={{fontSize:15,fontWeight:800,marginBottom:4}}>🔑 Reset password</div>
          <div style={{fontSize:12,color:"#888",marginBottom:18}}>{pwResetTgt.name} — l'utente dovrà cambiarla al primo accesso</div>
          <label style={{fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:.8,display:"block",marginBottom:6}}>Nuova password temporanea</label>
          <input
            type="text"
            value={pwResetVal}
            onChange={e=>setPwResetVal(e.target.value)}
            placeholder="es. Likeyou2024!"
            style={{width:"100%",border:"1px solid #E2E2E2",borderRadius:9,padding:"10px 13px",fontSize:14,fontFamily:"inherit",outline:"none",marginBottom:16}}
            autoFocus
          />
          <div style={{display:"flex",gap:8}}>
            <button onClick={async()=>{
              if(!pwResetVal.trim()||pwResetVal.length<6){alert("Minimo 6 caratteri");return;}
              setPwResetBusy(true);
              const hash=await hashPw(pwResetVal.trim());
              await supabase.from("profiles").update({password:hash,must_change_password:true}).eq("id",pwResetTgt.id);
              setPwResetBusy(false);
              setPwResetTgt(null);
              showToast(`Password di ${pwResetTgt.name} aggiornata ✓`);
              load();
            }} disabled={pwResetBusy} style={{flex:1,background:"#1A1A1A",color:"white",border:"none",borderRadius:9,padding:"10px 0",fontSize:13,fontWeight:700,cursor:"pointer"}}>
              {pwResetBusy?"Salvataggio...":"Imposta password →"}
            </button>
            <button onClick={()=>setPwResetTgt(null)} style={{background:"#F0F0F0",border:"none",borderRadius:9,padding:"10px 14px",fontSize:13,cursor:"pointer"}}>✕</button>
          </div>
        </div>
      </div>}

      {/* MODAL ferie admin */}
      {vacTgt&&<div className="mo" onClick={()=>setVacTgt(null)}>
        <div className="md" onClick={e=>e.stopPropagation()}>
          <div className="st">Ferie — {team.find(t=>t.id===vacTgt)?.name}</div>
          {/* Toggle ferie immediate */}
          {(()=>{
            const u=team.find(t=>t.id===vacTgt);
            const vacOn=!!u?.vacation;
            return(
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",borderRadius:9,background:"#FAFAFA",border:"1px solid "+BDR,marginBottom:16}}>
              <div>
                <div style={{fontSize:13,fontWeight:600}}>In ferie adesso 🌴</div>
                <div style={{fontSize:11,color:SUB}}>Appare come "in ferie" in dashboard fino a disattivazione</div>
              </div>
              <button className="tgl" style={{background:vacOn?BLK:"#DDD",flexShrink:0}} onClick={async()=>{
                await supabase.from("profiles").update({vacation:!vacOn}).eq("id",vacTgt);
                setTeam(prev=>prev.map(t=>t.id===vacTgt?{...t,vacation:!vacOn}:t));
                showToast(vacOn?"Ferie disattivate":"Ferie attivate ✓");
              }}><div className="tglk" style={{left:vacOn?23:3}}/></button>
            </div>);
          })()}
          {/* Periodo ferie */}
          <div style={{marginBottom:8}}><label className="lb">Periodo ferie (opzionale)</label></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
            <div><label className="lb">Dal</label><input type="date" value={vacFrom} onChange={e=>setVacFrom(e.target.value)}/></div>
            <div><label className="lb">Al</label><input type="date" value={vacTo} onChange={e=>setVacTo(e.target.value)}/></div>
          </div>
          {vacFrom&&vacTo&&<button className="bp" style={{width:"100%",marginBottom:10}} onClick={async()=>{
            // Salva periodo ferie in profiles come vacation_from/vacation_to
            await supabase.from("profiles").update({vacation:true, vacation_from:vacFrom, vacation_to:vacTo}).eq("id",vacTgt);
            setTeam(prev=>prev.map(t=>t.id===vacTgt?{...t,vacation:true,vacation_from:vacFrom,vacation_to:vacTo}:t));
            showToast("Periodo ferie salvato ✓");
            setVacTgt(null);
          }}>Salva periodo ferie →</button>}
          <button onClick={()=>setVacTgt(null)} className="bsec" style={{width:"100%",borderRadius:10,padding:10}}>Chiudi</button>
        </div>
      </div>}

      {/* MODAL periodo eccezione SW */}
      {excModal&&<div className="mo" onClick={()=>setExcModal(null)}>
        <div className="md" onClick={e=>e.stopPropagation()}>
          <div className="st">⚡ Periodo eccezione — {excModal.name}</div>
          <div style={{background:"#FFF8E0",border:"1px solid #E0C060",borderRadius:9,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#856404",lineHeight:1.6}}>
            Durante questo periodo l'utente può prenotare <strong>più giorni nella stessa settimana</strong>.<br/>
            Il <strong>1° giorno/settimana</strong> scala dal budget ordinario, gli altri vengono conteggiati come eccezioni.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:13}}>
            <div><label className="lb">Data inizio</label><input type="date" value={excDateFrom} onChange={e=>{setExcDateFrom(e.target.value);if(!excDateTo)setExcDateTo(e.target.value);}}/></div>
            <div><label className="lb">Data fine</label><input type="date" value={excDateTo} onChange={e=>setExcDateTo(e.target.value)} min={excDateFrom}/></div>
          </div>
          <div style={{marginBottom:16}}><label className="lb">Motivo / nota interna</label><input type="text" value={excNote} onChange={e=>setExcNote(e.target.value)} placeholder="Es. emergenza familiare, assistenza familiare..."/></div>
          {/* Periodi attivi per questo utente */}
          {excPeriods.filter(p=>p.user_id===excModal.userId).length>0&&<div style={{marginBottom:14}}>
            <label className="lb">Periodi attivi</label>
            {excPeriods.filter(p=>p.user_id===excModal.userId).map(p=>(
              <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 10px",background:"#FFFBF0",border:"1px solid #E0C060",borderRadius:8,marginBottom:6,fontSize:12}}>
                <span>⚡ {p.date_from} → {p.date_to}</span>
                {p.note&&<span style={{color:"#888",fontSize:11}}>{p.note}</span>}
                <button onClick={async()=>{await supabase.from("user_exception_periods").delete().eq("id",p.id);props.load&&props.load();}} style={{background:"none",border:"none",cursor:"pointer",color:"#C0392B",fontSize:14}}>✕</button>
              </div>
            ))}
          </div>}
          <div style={{display:"flex",gap:10}}>
            <button className="bp" disabled={!excDateFrom||!excDateTo||excSaving} style={{flex:1}} onClick={async()=>{
              setExcSaving(true);
              const {error}=await supabase.from("user_exception_periods").insert({
                user_id:excModal.userId, date_from:excDateFrom, date_to:excDateTo, note:excNote||null
              });
              setExcSaving(false);
              if(error){showToast("Errore: "+error.message,false);return;}
              showToast("✓ Periodo eccezione attivato");
              setExcModal(null);setExcDateFrom("");setExcDateTo("");setExcNote("");
              props.load&&props.load();
            }}>{excSaving?"Salvataggio...":"Attiva periodo →"}</button>
            <button onClick={()=>setExcModal(null)} className="bsec" style={{flex:1,borderRadius:10,padding:10}}>Chiudi</button>
          </div>
        </div>
      </div>}

      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
        <h1 style={{fontSize:21,fontWeight:700}}>Pannello Admin</h1>
        <span style={{background:BLK,color:"white",borderRadius:4,padding:"2px 7px",fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>Amministratore</span>
      </div>

      {/* Shortcut rapidi sempre visibili */}
      {aTab!=="force"&&<div style={{display:"flex",gap:9,marginBottom:14,flexWrap:"wrap"}}>
        <button onClick={()=>setATab("force")} style={{background:"#FFF8E0",border:"1px solid #E0C060",color:"#856404",borderRadius:9,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:7}}>
          ✍️ Assegna / Inserisci Smart Working
        </button>
        <button onClick={()=>setATab("usetsgs")} style={{background:"#F0F5F8",border:"1px solid "+BDR,color:LYB,borderRadius:9,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
          ⚙️ Impostazioni per utente
        </button>
      </div>}

      {/* Menu admin a due livelli */}
      <div style={{marginBottom:20}}>
        {/* Livello 1 — gruppi */}
        <div style={{display:"flex",gap:4,marginBottom:6,overflowX:"auto",scrollbarWidth:"none",WebkitOverflowScrolling:"touch",paddingBottom:2}}>
          {adTabGroups.map(g=>{
            const isActive=g===activeGroup;
            return(
            <button key={g.label} onClick={()=>setATab(g.tabs[0].id)}
              style={{flexShrink:0,background:isActive?BLK:"#F0F0F0",color:isActive?"white":"#555",border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",transition:"all .15s"}}>
              {g.label}
            </button>);
          })}
        </div>
        {/* Livello 2 — tab del gruppo attivo */}
        {activeGroup&&<div style={{display:"flex",gap:5,overflowX:"auto",scrollbarWidth:"none",WebkitOverflowScrolling:"touch",paddingBottom:2,borderBottom:"2px solid #F0F0F0",paddingTop:2}}>
          {activeGroup.tabs.map(t=>(
            <button key={t.id} onClick={()=>setATab(t.id)}
              style={{flexShrink:0,background:"none",border:"none",borderBottom:aTab===t.id?"2px solid "+BLK:"2px solid transparent",marginBottom:-2,padding:"5px 12px",fontSize:12,fontWeight:aTab===t.id?700:400,cursor:"pointer",color:aTab===t.id?BLK:"#777",transition:"all .15s"}}>
              {t.l}
            </button>
          ))}
        </div>}
      </div>

      {/* ─ RICHIESTE ─ */}
      {aTab==="requests"&&(()=>{
        const allReqs=reqs.filter(r=>!r.cancel_requested);
        const filtered=reqSearch.trim()
          ? allReqs.filter(r=>{ const u=team.find(t=>t.id===r.user_id); return u?.name.toLowerCase().includes(reqSearch.toLowerCase())||r.smart_date?.includes(reqSearch); })
          : allReqs;
        // Raggruppa per utente
        const byUser={};
        filtered.forEach(r=>{ if(!byUser[r.user_id])byUser[r.user_id]=[]; byUser[r.user_id].push(r); });
        const userIds=Object.keys(byUser).sort((a,b)=>{
          const ua=team.find(t=>t.id===a)?.name||""; const ub=team.find(t=>t.id===b)?.name||"";
          // Pending first
          const pa=byUser[a].some(r=>r.status==="pending")?0:1;
          const pb=byUser[b].some(r=>r.status==="pending")?0:1;
          return pa-pb||ua.localeCompare(ub);
        });
        return(
        <div>
          {/* Barra ricerca */}
          <div style={{position:"relative",marginBottom:14}}>
            <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:14,color:SUB}}>🔍</span>
            <input value={reqSearch} onChange={e=>setReqSearch(e.target.value)} placeholder="Cerca per nome o data..." style={{paddingLeft:36,background:WHT}}/>
            {reqSearch&&<button onClick={()=>setReqSearch("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:14,color:SUB}}>✕</button>}
          </div>
          {userIds.length===0
            ? <div className="card" style={{color:SUB}}>Nessuna richiesta</div>
            : userIds.map(uid=>{
                const u=team.find(t=>t.id===uid);
                const urqs=byUser[uid].sort((a,b)=>b.smart_date?.localeCompare(a.smart_date||"")||0);
                const hasPending=urqs.some(r=>r.status==="pending");
                return(
                <div key={uid} className="card" style={{padding:0,overflow:"hidden",marginBottom:10,border:hasPending?"1px solid #FCD34D":"1px solid "+BDR}}>
                  {/* Header utente */}
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:hasPending?"#FFFBF0":WHT,borderBottom:"1px solid "+BDR}}>
                    <div className="av" style={{background:u?.color||"#AAA",width:30,height:30,fontSize:10}}>{u?.avatar_initials}</div>
                    <div style={{flex:1}}>
                      <span style={{fontSize:13,fontWeight:700}}>{u?.name}</span>
                      {hasPending&&<span style={{marginLeft:8,fontSize:10,background:"#FEF3C7",color:"#92400E",borderRadius:4,padding:"1px 6px",fontWeight:700}}>⏳ in attesa</span>}
                    </div>
                    <span style={{fontSize:11,color:SUB}}>{urqs.length} {urqs.length===1?"richiesta":"richieste"}</span>
                  </div>
                  {/* Lista date */}
                  {urqs.map((r,i)=>(
                    <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:i<urqs.length-1?"1px solid #F5F5F5":"none",background:r.status==="pending"?"#FFFDF5":"transparent"}}>
                      <span style={{fontFamily:"'Space Mono',monospace",fontSize:12,color:LYB,minWidth:90}}>{r.smart_date}</span>
                      <span style={{fontSize:10,background:r.type==="desk"?"#E8F0F5":"#FFF8E0",color:r.type==="desk"?LYB:"#856404",borderRadius:4,padding:"1px 5px",fontWeight:600}}>{r.type==="desk"?"🪑 Postazione":"🏠 Smart W."}</span>
                      {r.half_day&&<span style={{fontSize:10,background:"#F3E8FF",color:"#7C3AED",borderRadius:4,padding:"1px 5px"}}>{r.half_day==="morning"?"🌅 mat":"🌇 pom"}</span>}
                      {r.is_emergency&&<span className="tem tag" style={{fontSize:10}}>em.</span>}
                      <span className={tagCls(r.status)} style={{fontSize:10}}>{r.status==="pending"?"in attesa":r.status==="approved"?"approvata":"rifiutata"}</span>
                      {r.forced_by_admin&&<span style={{fontSize:10,color:"#856404",background:"#FFF3CD",borderRadius:4,padding:"1px 5px"}}>admin</span>}
                      {r.note&&<span style={{fontSize:11,color:SUB,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.note}</span>}
                      <div style={{display:"flex",gap:5,marginLeft:"auto",flexShrink:0}}>
                        {r.status==="pending"&&<><button className="bs ba" onClick={()=>hReq(r.id,"approved")}>✓</button><button className="bs br" onClick={()=>hReq(r.id,"rejected")}>✗</button></>}
                        <button className="bd" onClick={()=>delReq(r.id)}>🗑</button>
                      </div>
                    </div>
                  ))}
                </div>);
              })
          }
        </div>);
      })()}

      {/* ─ CANCELLAZIONI ─ */}
      {aTab==="cancels"&&<div style={{display:"flex",flexDirection:"column",gap:10}}>
        {reqs.filter(r=>r.cancel_requested).length===0?<div className="card" style={{color:SUB}}>Nessuna richiesta di cancellazione</div>
        :reqs.filter(r=>r.cancel_requested).map(r=>{const u=team.find(t=>t.id===r.user_id);return(<div key={r.id} className="card" style={{padding:"12px 16px",borderColor:"#D8B4FE"}}>
          <div style={{display:"flex",alignItems:"center",gap:11}}>
            <div className="av" style={{background:u?.color||"#AAA"}}>{u?.avatar_initials}</div>
            <div style={{flex:1}}>
              <div style={{display:"flex",gap:7,alignItems:"center",marginBottom:3}}><span style={{fontSize:13,fontWeight:700}}>{u?.name}</span><span style={{fontFamily:"'Space Mono',monospace",fontSize:12,color:LYB}}>{r.smart_date}</span></div>
              {r.cancel_note&&<div style={{fontSize:12,color:"#6B21A8"}}>Motivo: {r.cancel_note}</div>}
            </div>
            <div style={{display:"flex",gap:6}}><button className="bs ba" onClick={()=>hCancel(r.id,true)}>✓ Approva</button><button className="bs br" onClick={()=>hCancel(r.id,false)}>✗ Nega</button></div>
          </div>
        </div>);})}
      </div>}

      {/* ─ SCAMBI ─ */}
      {aTab==="swaps"&&<div style={{display:"flex",flexDirection:"column",gap:10}}>
        {swaps.length===0?<div className="card" style={{color:SUB}}>Nessuna proposta di scambio</div>
        :swaps.map(s=>{const req=team.find(t=>t.id===s.requester_id);const tgt=team.find(t=>t.id===s.target_id);return(<div key={s.id} className="card" style={{padding:"12px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:11,flexWrap:"wrap"}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                <div className="av" style={{background:req?.color||"#AAA",width:28,height:28,fontSize:9}}>{req?.avatar_initials}</div>
                <span style={{fontSize:13,fontWeight:600}}>{req?.name}</span>
                <span style={{color:SUB}}>↔</span>
                <div className="av" style={{background:tgt?.color||"#AAA",width:28,height:28,fontSize:9}}>{tgt?.avatar_initials}</div>
                <span style={{fontSize:13,fontWeight:600}}>{tgt?.name}</span>
                <span className={tagCls(s.status)} style={{fontSize:10}}>{s.status==="pending"?"in attesa":s.status==="approved"?"approvato":"rifiutato"}</span>
              </div>
              <div style={{fontSize:12,color:SUB}}>{req?.name?.split(" ")[0]} cede <strong>{s.requester_date}</strong> e prende <strong>{s.target_date}</strong></div>
              {s.note&&<div style={{fontSize:11,color:SUB,marginTop:2}}>"{s.note}"</div>}
            </div>
            {s.status==="pending"&&<div style={{display:"flex",gap:6,flexShrink:0}}>
              <button className="bs ba" onClick={()=>hSwap(s.id,"approved")}>✓ Approva</button>
              <button className="bs br" onClick={()=>hSwap(s.id,"rejected")}>✗ Rifiuta</button>
            </div>}
          </div>
        </div>);})}
      </div>}

      {/* ─ VISTA MENSILE ─ */}
      {aTab==="monthly"&&<div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
          <span style={{fontSize:15,fontWeight:700}}>{MON[mvM]} {mvY}</span>
          <div style={{display:"flex",gap:7}}>
            <button onClick={()=>{if(mvM===0){setMvM(11);setMvY(y=>y-1);}else setMvM(m=>m-1);}} style={{background:WHT,border:"1px solid "+BDR,borderRadius:7,padding:"4px 11px",cursor:"pointer"}}>←</button>
            <button onClick={()=>{if(mvM===11){setMvM(0);setMvY(y=>y+1);}else setMvM(m=>m+1);}} style={{background:WHT,border:"1px solid "+BDR,borderRadius:7,padding:"4px 11px",cursor:"pointer"}}>→</button>
          </div>
        </div>
        {mvData().filter(d=>d.rqs.length>0||d.bl).length===0?<div className="card" style={{color:SUB}}>Nessuna prenotazione questo mese</div>
        :mvData().filter(d=>d.rqs.length>0||d.bl).map(d=>{
          const swRqs=d.rqs.filter(r=>r.type!=="desk");
          const dkRqs=d.rqs.filter(r=>r.type==="desk");
          return(<div key={d.ds} className="card" style={{padding:"12px 16px",marginBottom:8,borderColor:d.bl?"#F8D7DA":swRqs.length>=rules.max_per_day?"#FFF3CD":BDR}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:14}}>
            <div style={{textAlign:"center",minWidth:50}}>
              <div style={{fontSize:9,color:SUB,textTransform:"uppercase"}}>{DAY[new Date(d.ds+"T12:00:00").getDay()]}</div>
              <div style={{fontSize:22,fontWeight:700,fontFamily:"'Space Mono',monospace",color:d.bl?"#C0392B":swRqs.length>=rules.max_per_day?"#856404":BLK}}>{d.day}</div>
            </div>
            {d.bl?<div style={{color:"#C0392B",fontSize:13}}>Bloccato — {blocked.find(b=>b.date===d.ds)?.reason||"nessun motivo"}</div>
            :<div style={{flex:1}}>
              {swRqs.length>0&&<div style={{marginBottom:dkRqs.length>0?10:0}}>
                <div style={{fontSize:11,color:SUB,marginBottom:6}}>{swRqs.length} SW{swRqs.length>=rules.max_per_day?" — limite":""}</div>
                <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                  {swRqs.map(r=>{const u=team.find(t=>t.id===r.user_id);if(!u)return null;return(<div key={r.id} style={{display:"flex",alignItems:"center",gap:6,background:"#F5F5F5",borderRadius:8,padding:"3px 9px"}}>
                    <div className="av" style={{background:u.color||LYB,width:20,height:20,fontSize:8}}>{u.avatar_initials}</div>
                    <span style={{fontSize:11}}>{u.name?.split(" ")[0]}</span>
                    {r.is_emergency&&<span className="tem tag" style={{fontSize:8,padding:"0 4px"}}>em</span>}
                    <span className={tagCls(r.status)} style={{fontSize:9,padding:"0 5px"}}>{r.status==="pending"?"⏳":"✓"}</span>
                    <button className="bd" style={{padding:"1px 5px",fontSize:9}} onClick={()=>delReq(r.id)}>🗑</button>
                  </div>);})}
                </div>
              </div>}
              {dkRqs.length>0&&<div>
                <div style={{fontSize:11,color:"#1A5A8A",marginBottom:6}}>🪑 {dkRqs.length} postazion{dkRqs.length===1?"e":"i"} prenotate</div>
                <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                  {dkRqs.map(r=>{const u=team.find(t=>t.id===r.user_id);if(!u)return null;const isEd=editBooking?.id===r.id;return(<div key={r.id} style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,padding:"3px 9px"}}>
                    {!isEd&&<div style={{display:"flex",alignItems:"center",gap:5}}>
                      <div className="av" style={{background:u.color||"#64B5F6",width:20,height:20,fontSize:8}}>{u.avatar_initials}</div>
                      <span style={{fontSize:11,fontWeight:600}}>{u.name?.split(" ")[0]}</span>
                      {r.half_day&&<span style={{fontSize:9,background:"#F3E8FF",color:"#7C3AED",borderRadius:3,padding:"0 4px"}}>{r.half_day==="morning"?"🌅":"🌇"}</span>}
                      <button onClick={()=>setEditBooking({id:r.id,half_day:r.half_day||"",name:u?.name})} style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:"#4F46E5"}}>✏️</button>
                      <button onClick={()=>delBooking(r.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:"#C0392B"}}>✕</button>
                    </div>}
                    {isEd&&<div style={{display:"flex",gap:5,alignItems:"center"}}>
                      <span style={{fontSize:11,fontWeight:600}}>{u?.name?.split(" ")[0]}</span>
                      <select value={editBooking.half_day} onChange={e=>setEditBooking(b=>({...b,half_day:e.target.value}))} style={{fontSize:11,padding:"2px 5px",borderRadius:5,border:"1px solid #DDD"}}>
                        <option value="">Tutto il giorno</option>
                        <option value="morning">🌅 Mattina</option>
                        <option value="afternoon">🌇 Pomeriggio</option>
                      </select>
                      <button onClick={()=>saveEditBooking(editBooking.id,editBooking.half_day)} style={{background:"#1A7A3A",color:"white",border:"none",borderRadius:5,padding:"2px 8px",fontSize:11,fontWeight:700,cursor:"pointer"}}>✓</button>
                      <button onClick={()=>setEditBooking(null)} style={{background:"#EEE",border:"none",borderRadius:5,padding:"2px 6px",fontSize:11,cursor:"pointer"}}>✕</button>
                    </div>}
                  </div>);})}
                </div>
              </div>}
            </div>}
          </div>
        </div>);})}
      </div>}

      {/* ─ ASSEGNA SW ─ */}
      {aTab==="force"&&<ForceSwTab
        team={team} reqs={reqs} isFixed={isFixed} isFullRemote={isFullRemote}
        forceSW={forceSW} delReq={delReq}
        fUser={fUser} fDate={fDate} fNote={fNote}
        setFUser={setFUser} setFDate={setFDate} setFNote={setFNote}
        BLK={BLK} LYB={LYB} BDR={BDR} SUB={SUB} WHT={WHT}
        supabase={supabase} load={load} showToast={showToast}
      />}

      {/* ─ SW RICORRENTE ─ */}
      {aTab==="recurring"&&<div style={{maxWidth:520}}>
        <div className="card" style={{marginBottom:16,padding:"14px 18px",background:"#F0F8FF",borderColor:"#B3D4F0"}}>
          <div style={{fontSize:12,color:"#1A5A8A",lineHeight:1.7}}>
            <strong>🔁 SW Ricorrente</strong> — imposta che un dipendente lavora sempre in smart working in un determinato giorno della settimana per un certo periodo. Tutte le date vengono inserite direttamente come <strong>approvate</strong>, escludendo automaticamente giorni bloccati e date già prenotate.
          </div>
        </div>
        <div className="card">
          <div className="st">Configura regola ricorrente</div>
          <div style={{marginBottom:13}}>
            <label className="lb">Dipendente</label>
            <select value={recUser} onChange={e=>setRecUser(e.target.value)}>
              <option value="">Seleziona...</option>
              {team.filter(u=>u.role!=="admin").map(u=>(
                <option key={u.id} value={u.id}>{u.name}{isFullRemote(u)?" (FR)":""}</option>
              ))}
            </select>
          </div>
          <div style={{marginBottom:13}}>
            <label className="lb">Giorno della settimana</label>
            <select value={recDay} onChange={e=>setRecDay(e.target.value)}>
              {[["1","Lunedì"],["2","Martedì"],["3","Mercoledì"],["4","Giovedì"],["5","Venerdì"]].map(([v,l])=>(
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:13}}>
            <div><label className="lb">Dal</label><input type="date" value={recFrom} onChange={e=>setRecFrom(e.target.value)}/></div>
            <div><label className="lb">Al</label><input type="date" value={recTo} onChange={e=>setRecTo(e.target.value)} min={recFrom}/></div>
          </div>
          <div style={{marginBottom:14}}>
            <label className="lb">Sede (lascia vuoto = usa sede profilo)</label>
            <input type="text" value={recNote} onChange={e=>setRecNote(e.target.value)} placeholder="Es. Casa, Coworking Roma..."/>
          </div>
          {recUser&&recFrom&&recTo&&(()=>{
            // Preview date che verranno inserite
            const DOW = parseInt(recDay);
            let cur = new Date(recFrom+"T12:00:00");
            while(((cur.getDay()||7))!==DOW) cur.setDate(cur.getDate()+1);
            const preview=[];
            while(cur<=new Date(recTo+"T12:00:00")){
              const ds=cur.toISOString().slice(0,10);
              const bl=blocked.some(b=>b.date===ds);
              const ex=reqs.some(r=>r.user_id===recUser&&r.smart_date===ds&&r.status!=="rejected");
              preview.push({ds,bl,ex});
              cur.setDate(cur.getDate()+7);
            }
            const valid=preview.filter(p=>!p.bl&&!p.ex);
            return(
            <div style={{background:"#F8F8F8",borderRadius:9,padding:"10px 14px",marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:SUB,marginBottom:8}}>ANTEPRIMA ({preview.length} settimane totali)</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
                {preview.slice(0,20).map(p=>(
                  <span key={p.ds} style={{fontSize:10,padding:"2px 6px",borderRadius:5,
                    background:p.bl?"#FFDDD0":p.ex?"#FFF3CD":"#D4EDDA",
                    color:p.bl?"#C05A00":p.ex?"#856404":"#1A7A3A",
                    textDecoration:(p.bl||p.ex)?"line-through":"none"
                  }}>{p.ds}</span>
                ))}
                {preview.length>20&&<span style={{fontSize:10,color:SUB}}>+{preview.length-20} altre...</span>}
              </div>
              <div style={{fontSize:11,color:SUB}}>
                ✅ <strong>{valid.length}</strong> verranno inserite
                {preview.filter(p=>p.bl).length>0&&<> · 🚫 <strong>{preview.filter(p=>p.bl).length}</strong> bloccate</>}
                {preview.filter(p=>p.ex).length>0&&<> · ⚠️ <strong>{preview.filter(p=>p.ex).length}</strong> già esistenti</>}
              </div>
            </div>
            );
          })()}
          <button className="bp" onClick={forceRecurring} style={{width:"100%"}}>
            🔁 Inserisci SW ricorrenti →
          </button>
        </div>
        {/* Lista SW ricorrenti già inseriti per utente */}
        {recUser&&(()=>{
          const dayNames=["","Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
          const uName=team.find(t=>t.id===recUser)?.name;
          const forced=reqs.filter(r=>r.user_id===recUser&&r.forced_by_admin&&r.status!=="rejected"&&r.smart_date>=todayStr).sort((a,b)=>a.smart_date.localeCompare(b.smart_date));
          if(forced.length===0) return null;
          return(
          <>
          <div className="card" style={{marginTop:14}}>
            <div className="st">SW ricorrenti futuri di {uName} ({forced.length})</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
              {forced.map(r=>(
                <div key={r.id} style={{display:"flex",alignItems:"center",gap:6,background:"#F0FFF4",border:"1px solid #A5D6A7",borderRadius:8,padding:"4px 10px"}}>
                  <span style={{fontSize:12,fontFamily:"monospace"}}>{r.smart_date}</span>
                  <button onClick={async()=>{await supabase.from("requests").delete().eq("id",r.id);load();}} style={{background:"none",border:"none",color:"#C0392B",cursor:"pointer",fontSize:12,padding:0}}>✕</button>
                </div>
              ))}
            </div>
          </div>
          <div style={{marginTop:8}}>
            <button onClick={async()=>{
              const toDelete = forced.filter(r => blocked.some(b=>b.date===r.smart_date));
              if(toDelete.length===0){showToast("✅ Tutto allineato — nessun conflitto trovato");return;}
              for(const r of toDelete) await supabase.from("requests").delete().eq("id",r.id);
              showToast("🔁 Allineati: rimossi "+toDelete.length+" SW su giorni bloccati/ferie");
              load();
            }} style={{background:"#1A1A1A",color:"white",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,cursor:"pointer",fontWeight:600}}>
              🔄 Allinea con chiusure aziendali
            </button>
          </div>
          </>
          );
        })()}
      </div>}

      {/* ─ STORICO RICHIESTE ─ */}
      {aTab==="storico"&&(()=>{
        const filtered = reqs.filter(r => {
          if(storicoFiltUser && r.user_id !== storicoFiltUser) return false;
          if(storicoFiltStatus && r.status !== storicoFiltStatus) return false;
          if(storicoSearch) {
            const u = team.find(t=>t.id===r.user_id);
            const hay = ((u?.name||"")+(r.smart_date||"")+(r.note||"")).toLowerCase();
            if(!hay.includes(storicoSearch.toLowerCase())) return false;
          }
          return true;
        }).sort((a,b)=>b.smart_date?.localeCompare(a.smart_date||"")||0);
        return(
        <div>
          <div className="card" style={{marginBottom:12,padding:"12px 16px"}}>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
              <input placeholder="🔍 Cerca nome o data..." value={storicoSearch} onChange={e=>setStoricoSearch(e.target.value)}
                style={{flex:1,minWidth:150,padding:"7px 10px",borderRadius:7,border:"1px solid #E2E2E2",fontSize:12,fontFamily:"'DM Sans',sans-serif"}}/>
              <select value={storicoFiltUser} onChange={e=>setStoricoFiltUser(e.target.value)}
                style={{padding:"7px 10px",borderRadius:7,border:"1px solid #E2E2E2",fontSize:12,fontFamily:"'DM Sans',sans-serif"}}>
                <option value="">Tutti i dipendenti</option>
                {team.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <select value={storicoFiltStatus} onChange={e=>setStoricoFiltStatus(e.target.value)}
                style={{padding:"7px 10px",borderRadius:7,border:"1px solid #E2E2E2",fontSize:12,fontFamily:"'DM Sans',sans-serif"}}>
                <option value="">Tutti gli stati</option>
                <option value="approved">Approvati</option>
                <option value="pending">In attesa</option>
                <option value="rejected">Rifiutati</option>
              </select>
            </div>
            <div style={{marginTop:8,fontSize:11,color:SUB}}>{filtered.length} richieste trovate</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {filtered.length===0?<div className="card" style={{color:SUB}}>Nessuna richiesta trovata</div>
            :filtered.map(r=>{
              const u=team.find(t=>t.id===r.user_id);
              return(
              <div key={r.id} className="card" style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                <div className="av" style={{background:u?.color||"#AAA",flexShrink:0}}>{u?.avatar_initials}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
                    <span style={{fontSize:13,fontWeight:700}}>{u?.name}</span>
                    <span style={{fontFamily:"'Space Mono',monospace",fontSize:12,color:LYB}}>{r.smart_date}</span>
                    <span className={tagCls(r.status)} style={{fontSize:10}}>{r.status==="pending"?"in attesa":r.status==="approved"?"approvata":"rifiutata"}</span>
                    {r.forced_by_admin&&<span style={{fontSize:10,color:"#856404",background:"#FFF3CD",borderRadius:4,padding:"1px 6px"}}>admin</span>}
                    {r.is_emergency&&<span className="tem tag" style={{fontSize:10}}>em.</span>}
                  </div>
                  {r.note&&<div style={{fontSize:11,color:SUB,marginTop:2}}>📍 {r.note}</div>}
                  <div style={{fontSize:10,color:"#CCC",marginTop:1}}>{r.created_at?.split("T")[0]}</div>
                </div>
                <button onClick={async()=>{
                  if(!window.confirm("Cancellare questa richiesta?")) return;
                  await supabase.from("requests").delete().eq("id",r.id);
                  showToast("🗑 Richiesta eliminata");
                  load();
                }} style={{background:"#FEE8E8",color:"#C0392B",border:"none",borderRadius:7,padding:"5px 10px",fontSize:12,cursor:"pointer",flexShrink:0}}>🗑</button>
              </div>);
            })}
          </div>
        </div>
        );
      })()}

      {/* ─ GESTIONE UTENTI ─ */}
      {aTab==="users"&&<div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:20}}>
        <div className="card">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div className="st" style={{marginBottom:0}}>Team ({team.length})</div>
            {welcomeSel.size>0&&<span style={{fontSize:11,background:"#E8F0F5",color:LYB,borderRadius:6,padding:"3px 9px",fontWeight:600}}>{welcomeSel.size} selezionati</span>}
          </div>
          {team.map(u=>(<div key={u.id} className="ri">
            <input type="checkbox" checked={welcomeSel.has(u.id)} onChange={e=>{const s=new Set(welcomeSel);e.target.checked?s.add(u.id):s.delete(u.id);setWelcomeSel(s);}} style={{marginRight:4,cursor:"pointer",width:15,height:15}}/>
            <div className="av" style={{background:u.color||LYB}}>{u.avatar_initials}</div>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600}}>{u.name}</div><div style={{fontSize:11,color:SUB}}>{u.email||"—"}{u.must_change_password&&<span style={{marginLeft:6,background:"#FEF3C7",color:"#92400E",borderRadius:3,padding:"0 4px",fontSize:9,fontWeight:700}}>PSW TEMP</span>}</div></div>
            <span style={{fontSize:10,padding:"2px 7px",borderRadius:4,fontWeight:600,background:u.role==="admin"?"#FFF3CD":isFullRemote(u)?"#D4EDDA":isFixed(u)?"#E8F0F5":"#F5F5F5",color:u.role==="admin"?"#856404":isFullRemote(u)?"#1A7A3A":isFixed(u)?LYB:SUB}}>{u.role==="admin"?"Admin":isFullRemote(u)?"Full Remote":isFixed(u)?"Fisso":"Collab."}</span>
            <button className="bsec" onClick={()=>setUEditModal({...u})}>✏</button>
            <button title="Aggiungi eccezione SW" style={{background:"#FFF8E0",border:"1px solid #E0C060",borderRadius:7,padding:"4px 8px",fontSize:10,cursor:"pointer",color:"#856404"}} onClick={()=>{setExcModal({userId:u.id,name:u.name,companyId:u.company_id});setExcDateFrom("");setExcDateTo("");setExcNote("");}}>⚡</button>
            <button title="Reset password" style={{background:"#FFF8E0",border:"1px solid #F0C040",borderRadius:7,padding:"4px 8px",fontSize:10,cursor:"pointer",color:"#856404"}} onClick={()=>{setPwResetTgt({id:u.id,name:u.name});setPwResetVal("");}}>🔑</button>
            {u.id!==realUser.id&&<button className="bd" onClick={()=>delUser(u.id)}>🗑</button>}
            <button style={{background:"#F0F0F0",border:"none",borderRadius:7,padding:"4px 8px",fontSize:10,cursor:"pointer"}} onClick={()=>{setViewAs({...u});setTab("dashboard");}}>👁</button>
          </div>))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div className="card">
            <div className="st" style={{color:"#1A7A3A"}}>+ Aggiungi</div>
            <p style={{fontSize:11,color:SUB,marginBottom:12,lineHeight:1.6}}>Il nuovo utente riceverà la password temporanea <strong>Likeyou2024!</strong> — dovrà cambiarla al primo accesso.</p>
            <div style={{marginBottom:12}}><label className="lb">Nome e Cognome</label><input type="text" value={nuName} onChange={e=>setNuName(e.target.value)} placeholder="Marco Rossi"/></div>
            <div style={{marginBottom:12}}><label className="lb">Email</label><input type="email" value={nuEmail} onChange={e=>setNuEmail(e.target.value)} placeholder="marco@likeyousrl.com"/></div>
            <div style={{marginBottom:14}}><label className="lb">Ruolo</label><select value={nuRole} onChange={e=>setNuRole(e.target.value)}><option value="employee">Dipendente</option><option value="collaborator">Collaboratore</option><option value="admin">Admin</option></select></div>
            <button className="bp" onClick={addUser} style={{width:"100%"}}>Aggiungi →</button>
          </div>

          {/* Welcome email panel */}
          <div className="card" style={{border:"1px solid #C8E6C9"}}>
            <div className="st" style={{color:"#1A7A3A",marginBottom:6}}>📧 Email di benvenuto</div>
            <p style={{fontSize:11,color:SUB,marginBottom:12,lineHeight:1.6}}>Seleziona gli utenti nella lista e inserisci la password temporanea da comunicare.</p>
            <div style={{marginBottom:10}}>
              <label className="lb">Password temporanea</label>
              <input type="text" value={welcomePsw} onChange={e=>setWelcomePsw(e.target.value)} placeholder="Es. Likeyou2024!"/>
            </div>
            <div style={{display:"flex",gap:7,marginBottom:12,flexWrap:"wrap"}}>
              <button onClick={()=>setWelcomeSel(new Set(team.filter(u=>u.role!=="admin").map(u=>u.id)))} style={{fontSize:11,background:"#F0F0F0",border:"none",borderRadius:6,padding:"4px 9px",cursor:"pointer"}}>Seleziona tutti</button>
              <button onClick={()=>setWelcomeSel(new Set())} style={{fontSize:11,background:"#F0F0F0",border:"none",borderRadius:6,padding:"4px 9px",cursor:"pointer"}}>Deseleziona</button>
            </div>
            {welcomeSel.size===0
              ?<div style={{fontSize:11,color:SUB,textAlign:"center",padding:"8px 0"}}>Nessun utente selezionato</div>
              :<div style={{fontSize:11,color:"#1A7A3A",marginBottom:10,fontWeight:600}}>✓ {welcomeSel.size} utent{welcomeSel.size===1?"e":"i"} selezionat{welcomeSel.size===1?"o":"i"}</div>
            }
            <button className="bp" disabled={welcomeSel.size===0||!welcomePsw.trim()||welcomeSending}
              style={{width:"100%",background:"#1A7A3A",opacity:welcomeSel.size===0||!welcomePsw.trim()?0.5:1}}
              onClick={async()=>{
                if(!window.confirm(`Inviare email di benvenuto a ${welcomeSel.size} utent${welcomeSel.size===1?"e":"i"}?`)) return;
                setWelcomeSending(true);
                const savedTexts = (() => { try { const s=localStorage.getItem("sw_notif_texts"); return s?JSON.parse(s):{}; } catch(e){return{};} })();
                const tpl = { subject:"👋 Benvenuto/a su LikeYou SW — le tue credenziali", body:"Ciao {{nome}},\n\nsei stato/a aggiunto/a alla piattaforma Smart Working di LikeYou!\n\n🔑 Le tue credenziali di accesso:\nEmail: {{email}}\nPassword temporanea: {{password}}\n\n🔗 Accedi su: sw.likeyousrl.com\n\nTi consigliamo di cambiare la password al primo accesso.\n\nBenvenuto/a nel team! 🚀\n\nLikeYou Team", ...(savedTexts["welcome"]||{}) };
                const savedBrand = (() => { try { const s=localStorage.getItem("sw_notif_brand"); return s?JSON.parse(s):{}; } catch(e){return{};} })();
                let ok=0, fail=0;
                for(const uid of welcomeSel){
                  const u = team.find(t=>t.id===uid);
                  if(!u?.email) { fail++; continue; }
                  const replace = s => s.replace(/{{nome}}/g,u.name).replace(/{{email}}/g,u.email||"").replace(/{{password}}/g,welcomePsw);
                  try {
                    const res = await fetch("https://kmesrtgnkohmflyoestm.supabase.co/functions/v1/send-notification", {
                      method:"POST",
                      headers:{"Content-Type":"application/json","Authorization":`Bearer ${SUPABASE_ANON_KEY}`},
                      body:JSON.stringify({type:"welcome",to:u.email,name:u.name,subject:replace(tpl.subject),body:replace(tpl.body),showCta:false,logoUrl:logoUrl||null,brandColor:savedBrand.color||"#1A1A1A",brandName:savedBrand.name||"LikeYou SW",brandTextColor:savedBrand.textColor||"#FFFFFF",brandSepColor:savedBrand.sepColor||"#1A7A3A",brandFooterText:savedBrand.footerText||"LikeYou SW — Piattaforma Smart Working",password:welcomePsw})
                    });
                    res.ok ? ok++ : fail++;
                  } catch(e) { fail++; }
                }
                setWelcomeSending(false);
                showToast(`✓ ${ok} email inviate${fail>0?` · ${fail} fallite`:""}`, fail===0);
                if(ok>0){ setWelcomeSel(new Set()); setWelcomePsw(""); }
              }}>
              {welcomeSending?`Invio in corso...`:`Invia email di benvenuto →`}
            </button>
          </div>
        </div>
      </div>}

      {/* ─ ECCEZIONI SW ─ */}
      {aTab==="exceptions"&&<div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <div>
            <div className="st" style={{marginBottom:4}}>⚡ Periodi eccezione SW</div>
            <div style={{fontSize:12,color:SUB}}>Gli utenti con un periodo attivo possono prenotare più giorni nella stessa settimana. Il 1° giorno scala dal budget, i successivi sono eccezioni.</div>
          </div>
        </div>
        {/* Aggiungi nuovo periodo */}
        <div className="card" style={{marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:12}}>➕ Nuovo periodo eccezione</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <label className="lb">Utente</label>
              <select value={excModal?.userId||""} onChange={e=>{const u=team.find(t=>t.id===e.target.value);if(u)setExcModal({userId:u.id,name:u.name,companyId:u.company_id});else setExcModal(null);}} style={{fontSize:12,padding:"6px 9px",width:"100%"}}>
                <option value="">— Seleziona utente —</option>
                {team.filter(u=>u.role!=="admin").map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="lb">Motivo (opz.)</label>
              <input type="text" value={excNote} onChange={e=>setExcNote(e.target.value)} placeholder="Es. assistenza familiare..." style={{fontSize:12,padding:"6px 9px",width:"100%"}}/>
            </div>
            <div>
              <label className="lb">Data inizio</label>
              <input type="date" value={excDateFrom} onChange={e=>{setExcDateFrom(e.target.value);if(!excDateTo)setExcDateTo(e.target.value);}} style={{fontSize:12,padding:"6px 9px",width:"100%"}}/>
            </div>
            <div>
              <label className="lb">Data fine</label>
              <input type="date" value={excDateTo} onChange={e=>setExcDateTo(e.target.value)} min={excDateFrom} style={{fontSize:12,padding:"6px 9px",width:"100%"}}/>
            </div>
          </div>
          <button className="bp" disabled={!excModal?.userId||!excDateFrom||!excDateTo||excSaving} onClick={async()=>{
            setExcSaving(true);
            const {error}=await supabase.from("user_exception_periods").insert({
              user_id:excModal.userId, date_from:excDateFrom, date_to:excDateTo, note:excNote||null
            });
            setExcSaving(false);
            if(error){showToast("Errore: "+error.message,false);return;}
            showToast("✓ Periodo eccezione attivato");
            setExcModal(null);setExcDateFrom("");setExcDateTo("");setExcNote("");
            props.load&&props.load();
          }}>{excSaving?"Salvataggio...":"Attiva periodo →"}</button>
        </div>
        {/* Lista periodi attivi */}
        {excPeriods.length===0
          ?<div className="card" style={{color:SUB,textAlign:"center",padding:24}}>Nessun periodo eccezione attivo</div>
          :<div style={{display:"flex",flexDirection:"column",gap:10}}>
          {[...excPeriods].sort((a,b)=>a.date_from.localeCompare(b.date_from)).map(p=>{
            const u=team.find(t=>t.id===p.user_id);
            const isActive=todayStr>=p.date_from&&todayStr<=p.date_to;
            const isPast=p.date_to<todayStr;
            const excCount=reqs.filter(r=>r.user_id===p.user_id&&r.is_exception&&r.status==="approved"&&r.smart_date>=p.date_from&&r.smart_date<=p.date_to).length;
            return(
            <div key={p.id} className="card" style={{borderLeft:`3px solid ${isActive?"#E0C060":isPast?"#DDD":"#A8D8A8"}`,padding:"12px 16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:34,height:34,borderRadius:"50%",background:LYB,color:"white",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700}}>
                    {u?.name?.split(" ").map(n=>n[0]).join("").slice(0,2)||"?"}
                  </div>
                  <div>
                    <div style={{fontWeight:700,fontSize:13}}>{u?.name||"Utente sconosciuto"}</div>
                    <div style={{fontSize:11,color:SUB}}>{p.date_from} → {p.date_to}</div>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:11,padding:"3px 8px",borderRadius:6,fontWeight:600,
                    background:isActive?"#FFF3CD":isPast?"#F0F0F0":"#D4EDDA",
                    color:isActive?"#856404":isPast?"#888":"#1A7A3A"}}>
                    {isActive?"● Attivo":isPast?"Scaduto":"Futuro"}
                  </span>
                  <button onClick={async()=>{if(!window.confirm("Eliminare questo periodo eccezione?"))return;await supabase.from("user_exception_periods").delete().eq("id",p.id);props.load&&props.load();showToast("Periodo eliminato");}} style={{background:"none",border:"1px solid #EEE",borderRadius:7,cursor:"pointer",color:"#C0392B",fontSize:12,padding:"4px 10px"}}>✕ Elimina</button>
                </div>
              </div>
              {p.note&&<div style={{fontSize:11,color:"#888",marginBottom:4}}>📝 {p.note}</div>}
              <div style={{fontSize:11,color:SUB}}>⚡ {excCount} gg eccezione prenotati in questo periodo</div>
            </div>);
          })}
          </div>
        }
      </div>}

      {/* ─ IMPOSTAZIONI UTENTI ─ */}
      {aTab==="usetsgs"&&(()=>{
        // Gruppi: Fisso, Full Remote, Collaboratori/Tirocinanti
        const groups = [
          {label:"Team fisso", color:LYB, bg:"#E8F0F5", members:team.filter(u=>isFixed(u)&&!isFullRemote(u)&&u.role!=="admin")},
          {label:"Full Remote", color:"#1A7A3A", bg:"#D4EDDA", members:team.filter(u=>isFullRemote(u))},
          {label:"Collaboratori / Tirocinanti", color:SUB, bg:"#F5F5F5", members:team.filter(u=>!isFixed(u)&&!isFullRemote(u)&&u.role!=="admin")},
        ].filter(g=>g.members.length>0);

        const UserRow = ({u}) => {
          const c=uSet.find(s=>s.user_id===u.id);
          const uR=getUR(u.id);
          const yu=yUsed(u.id,today.getFullYear());
          const hdOn=!!u.half_day_enabled;
          const canSW=isFixed(u)||isFullRemote(u)||(u.sw_permission===true);
          return(
          <div className="card" style={{padding:"12px 16px",borderColor:c?LYB+"44":BDR}}>
            <div style={{display:"flex",alignItems:"center",gap:11,flexWrap:"wrap"}}>
              <div className="av" style={{background:u.color||LYB}}>{u.avatar_initials}</div>
              <div style={{flex:1,minWidth:160}}>
                <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap",marginBottom:3}}>
                  <span style={{fontSize:13,fontWeight:700}}>{u.name}</span>
                  {c&&<span style={{background:"#E8F0F5",border:"1px solid "+LYB+"55",color:LYB,borderRadius:4,padding:"1px 6px",fontSize:10,fontWeight:600}}>personalizzato</span>}
                  {hdOn&&<span style={{background:"#F3E8FF",border:"1px solid #C084FC",color:"#7C3AED",borderRadius:4,padding:"1px 6px",fontSize:10}}>½ gg</span>}
                  {!canSW&&<span style={{background:"#FEE8E8",color:"#C0392B",borderRadius:4,padding:"1px 6px",fontSize:10}}>SW off</span>}
                </div>
                <div style={{fontSize:11,color:SUB}}>
                  {canSW?<>{`Mese: `}<b>{uR.days_per_month}</b>{` · Anno: `}<b>{uR.days_per_year}</b>{` · Usati: `}<b>{yu}</b>{(()=>{const ec=reqs.filter(r=>r.user_id===u.id&&r.is_exception&&r.status==="approved"&&r.smart_date&&new Date(r.smart_date).getFullYear()===today.getFullYear()).length;return ec>0?<span style={{color:"#856404",marginLeft:4}}>· <b>⚡{ec} eccezioni</b></span>:null;})()}</>:<span style={{color:"#C0392B"}}>Smart working non abilitato</span>}
                </div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button className="bs ba" onClick={()=>{setUsTgt(u.id);setUsDm(uR.days_per_month);setUsDy(uR.days_per_year);setUsEm(uR.emergency_days);setUsNote(c?.note||"");setUsModal(u.id);}}>⚙ Regole</button>
                <button className="bs" style={{background:u.vacation?"#E8F5E9":"",borderColor:u.vacation?"#A5D6A7":""}} onClick={()=>{setVacTgt(u.id);setVacFrom(u.vacation_from||"");setVacTo(u.vacation_to||"");}}>🌴 Ferie</button>
                {c&&<button className="bs br" onClick={()=>resetUS(u.id)}>Reset</button>}
              </div>
            </div>
          </div>);
        };

        return(
        <div>
          <p style={{color:SUB,fontSize:13,marginBottom:16}}>Regole personalizzate per utente · badge <span style={{background:"#F3E8FF",border:"1px solid #C084FC",color:"#7C3AED",borderRadius:4,padding:"1px 5px",fontSize:10}}>½ gg</span> = mezza giornata abilitata</p>
          {groups.map(g=>(
            <div key={g.label} style={{marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,paddingBottom:6,borderBottom:"2px solid "+g.bg}}>
                <span style={{fontSize:11,fontWeight:700,color:g.color,textTransform:"uppercase",letterSpacing:1}}>{g.label}</span>
                <span style={{fontSize:10,color:SUB}}>({g.members.length})</span>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {g.members.map(u=><UserRow key={u.id} u={u}/>)}
              </div>
            </div>
          ))}
          <div className="card" style={{marginTop:10}}>
            <div className="st" style={{color:"#C0392B"}}>Eccezioni giorni (bonus / riserva)</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div><label className="lb">Utente</label><select value={exUid} onChange={e=>setExUid(e.target.value)}>
                <option value="">Seleziona...</option>
                {team.filter(u=>u.role!=="admin").map(u=><option key={u.id} value={u.id}>{u.name} ({u.extra_days||0})</option>)}
              </select></div>
              <div><label className="lb">Giorni (neg = riserva)</label><input type="number" value={exDays} onChange={e=>setExDays(parseInt(e.target.value)||0)}/></div>
            </div>
            <div style={{marginBottom:13}}><label className="lb">Nota</label><input type="text" value={exNote} onChange={e=>setExNote(e.target.value)} placeholder="Motivazione..."/></div>
            <button className="bp" onClick={saveEx}>Salva eccezione →</button>
          </div>
        </div>
        );
      })()}

      {/* ─ POSTAZIONI ─ */}
      {aTab==="desks"&&<div style={{display:"grid",gridTemplateColumns:"1fr 260px",gap:20}}>
        <div>
          {Object.entries(deskRooms()).map(([room,roomDesks])=>(
            <div key={room} style={{marginBottom:18}}>
              <div style={{fontSize:12,fontWeight:700,textTransform:"uppercase",letterSpacing:1,color:SUB,marginBottom:8,padding:"0 2px"}}>{room}</div>
              <div className="card" style={{padding:0}}>
                {roomDesks.map((d,i)=>{
                  const isExpired = d.expires_date && d.expires_date < todayStr;
                  return(<div key={d.id} style={{display:"flex",alignItems:"center",gap:9,padding:"10px 16px",borderBottom:i<roomDesks.length-1?"1px solid #F5F5F5":"none",opacity:isExpired?.5:1}}>
                  <span style={{fontSize:13}}>{d.assigned_to?"👤":"🪑"}</span>
                  <div style={{flex:1}}>
                    <span style={{fontSize:13,fontWeight:500,color:d.active?BLK:SUB}}>{d.name}</span>
                    {d.assigned_to&&<span style={{fontSize:10,color:LYB,marginLeft:6}}>({d.assigned_to})</span>}
                    {d.expires_date&&<span style={{fontSize:10,color:isExpired?"#C0392B":"#856404",marginLeft:6}}>fino al {d.expires_date}</span>}
                  </div>
                  {!d.assigned_to&&<span style={{fontSize:10,color:d.active?"#1A7A3A":SUB}}>{d.active?"Libera":"Off"}</span>}
                  {!d.assigned_to&&<button className="tgl" style={{background:d.active?BLK:"#DDD"}} onClick={()=>togDesk(d.id,d.active)}><div className="tglk" style={{left:d.active?23:3}}/></button>}
                  <button className="bd" onClick={()=>delDesk(d.id)}>✕</button>
                </div>);})}
              </div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="st">+ Nuova postazione</div>
          <div style={{marginBottom:12}}><label className="lb">Nome postazione</label><input type="text" value={ndName} onChange={e=>setNdName(e.target.value)} placeholder="Es. Scrivania finestra"/></div>
          <div style={{marginBottom:12}}><label className="lb">Sala</label>
            <select value={ndRoom} onChange={e=>setNdRoom(e.target.value)}>
              <option value="Ufficio">Ufficio</option>
              <option value="Sala coworking">Sala coworking</option>
              <option value="Sala call">Sala call</option>
              <option value="Altro">Altro</option>
            </select>
          </div>
          <div style={{marginBottom:12}}><label className="lb">Persona fissa (opz.)</label><input type="text" value={ndAssigned||""} onChange={e=>setNdAssigned&&setNdAssigned(e.target.value)} placeholder="Es. Giovanni Rago"/></div>
          <div style={{marginBottom:14}}><label className="lb">Scadenza (opz.)</label><input type="date" value={ndExpires||""} onChange={e=>setNdExpires&&setNdExpires(e.target.value)}/></div>
          <button className="bp" onClick={addDesk} style={{width:"100%"}}>Aggiungi →</button>
        </div>
      </div>}

      {/* ─ GIORNI BLOCCATI ─ */}
      {aTab==="calendar"&&<div style={{display:"grid",gridTemplateColumns:"1fr 280px",gap:20}}>
        <div>
          {blocked.length===0?<div className="card" style={{color:SUB}}>Nessun giorno bloccato</div>
          :blocked.map(b=>(<div key={b.id} className="card" style={{padding:"11px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:11,borderColor:"#F8D7DA"}}>
            <span>🚫</span>
            <div style={{flex:1}}><div style={{fontFamily:"'Space Mono',monospace",fontSize:13,color:"#C0392B"}}>{b.date}</div>{b.reason&&<div style={{fontSize:11,color:SUB}}>{b.reason}</div>}</div>
            <button className="bd" onClick={()=>unblDay(b.id)}>Sblocca</button>
          </div>))}
        </div>
        <div className="card">
          <div className="st" style={{color:"#C0392B"}}>Blocca una data</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div><label className="lb">Dal</label><input type="date" value={blDate} onChange={e=>setBlDate(e.target.value)}/></div>
            <div><label className="lb">Al (lascia vuoto = solo quel giorno)</label><input type="date" value={blDateTo} onChange={e=>setBlDateTo(e.target.value)} min={blDate}/></div>
          </div>
          <div style={{marginBottom:14}}><label className="lb">Motivo</label><input type="text" value={blReas} onChange={e=>setBlReas(e.target.value)} placeholder="Es. riunione aziendale"/></div>
          <button onClick={()=>blDay()} style={{width:"100%",background:"#FEE8E8",border:"1px solid #F5C0C0",color:"#C0392B",borderRadius:9,padding:10,fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:700,cursor:"pointer"}}>
            {blDateTo && blDateTo > blDate ? `Blocca range (${Math.floor((new Date(blDateTo+"T12:00:00")-new Date(blDate+"T12:00:00"))/86400000)+1} giorni)` : "Blocca data"}
          </button>
        </div>
      </div>}

      {/* ─ CHIUSURE AZIENDALI ─ */}
      {aTab==="closures"&&<ClosuresTab
        blocked={blocked} blDay={blDay} unblDay={unblDay}
        blDate={blDate} blDateTo={blDateTo} blReas={blReas} setBlDate={setBlDate} setBlDateTo={setBlDateTo} setBlReas={setBlReas}
        todayStr={todayStr} BLK={BLK} BDR={BDR} SUB={SUB} LYB={LYB}
      />}

      {/* ─ NOTIFICHE ─ */}
      {aTab==="notifs"&&<NotificationsTab
        notifSettings={notifSettings} setNotifSettings={setNotifSettings}
        supabase={supabase} showToast={showToast}
        BLK={BLK} BDR={BDR} SUB={SUB} LYB={LYB} WHT={WHT}
        logoUrl={logoUrl}
      />}

      {/* ─ LOGO & IMPOSTAZIONI ─ */}
      {aTab==="logo"&&<LogoSettingsTab
        supabase={supabase} logoUrl={logoUrl} load={load} showToast={showToast}
        BLK={BLK} BDR={BDR} SUB={SUB} WHT={WHT} LYB={LYB}
      />}

      {/* ─ DEBUG PANEL ─ */}
      {aTab==="debug"&&<div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div>
            <div style={{fontWeight:700,fontSize:15}}>🧪 Log attività in tempo reale</div>
            <div style={{fontSize:12,color:SUB,marginTop:2}}>Prenotazioni, approvazioni, email — si aggiorna automaticamente</div>
          </div>
          <button onClick={()=>setDebugLogs([])} style={{background:"#FEE8E8",color:"#C0392B",border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            🗑️ Pulisci
          </button>
        </div>
        <div className="card" style={{marginBottom:14,padding:"14px 18px"}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>📐 Regole attive ora</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",gap:8}}>
            {[
              ["Max SW/mese (default)", rules.days_per_month],
              ["Max SW/anno (default)", rules.days_per_year],
              ["Max persone/giorno", rules.max_per_day],
              ["Anticipo ordinario", `${rules.advance_days} gg`],
              ["Cumulo mensile", rules.carry_over?"✅ Sì":"❌ No"],
              ["Periodo speciale", rules.consec_enabled?`${rules.consec_from} → ${rules.consec_to}`:"❌ Disattivo"],
              ["Notifiche email", notifSettings?.enabled!==false?"✅ Attive":"❌ Disattive"],
              ["Logo caricato", logoUrl?"✅ Sì":"❌ No"],
            ].map(([l,v])=>(
              <div key={l} style={{background:"#F7F7F7",borderRadius:8,padding:"8px 12px"}}>
                <div style={{fontSize:10,color:SUB,textTransform:"uppercase",letterSpacing:.5,marginBottom:2}}>{l}</div>
                <div style={{fontSize:13,fontWeight:700}}>{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="card" style={{padding:"14px 18px"}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>📋 Log eventi ({debugLogs.length})</div>
          {debugLogs.length===0&&<div style={{color:SUB,fontSize:13,textAlign:"center",padding:"30px 0"}}>
            Nessun evento ancora.<br/>
            <span style={{fontSize:11}}>Fai una prenotazione, approva/rifiuta una richiesta o invia una mail di test.</span>
          </div>}
          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:500,overflowY:"auto"}}>
            {debugLogs.map(log=>(
              <div key={log.id} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 12px",borderRadius:8,
                background:log.status==="✅"?"#F0FFF4":log.status==="❌"?"#FFF0F0":log.status==="⚠️"?"#FFFBEC":"#F5F5F5",
                border:`1px solid ${log.status==="✅"?"#A5D6A7":log.status==="❌"?"#F5C0C0":log.status==="⚠️"?"#F5D76E":"#E2E2E2"}`}}>
                <span style={{fontSize:16,lineHeight:1.3,flexShrink:0}}>{log.status}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:2}}>
                    <span style={{fontSize:11,fontWeight:700,color:SUB}}>{log.ts}</span>
                    <span style={{fontSize:10,background:"#E8E8E8",borderRadius:4,padding:"1px 6px",color:"#555"}}>{log.category}</span>
                  </div>
                  <div style={{fontSize:13,fontWeight:600}}>{log.message}</div>
                  {log.detail&&<div style={{fontSize:11,color:SUB,marginTop:2,fontFamily:"monospace",wordBreak:"break-word"}}>{log.detail}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>}

      {/* ─ REGOLE GLOBALI ─ */}
      {aTab==="rules"&&editRules&&<div className="card" style={{maxWidth:480}}>
        <div className="st">Regole globali smart working</div>
        {[["days_per_month","Giorni SW per mese (default)"],["days_per_year","Giorni SW per anno (default)"],["max_per_day","Max persone per giorno"],["advance_days","Anticipo min. ordinario (giorni)"]].map(([k,l])=>(
          <div key={k} style={{marginBottom:13}}><label className="lb">{l}</label><input type="number" value={editRules[k]} onChange={e=>setEditRules(p=>({...p,[k]:parseInt(e.target.value)||0}))}/></div>
        ))}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderTop:"1px solid "+BDR,marginBottom:16}}>
          <div><div style={{fontSize:13,fontWeight:600}}>Cumulo giorni mensili</div><div style={{fontSize:11,color:SUB}}>I giorni non usati si accumulano</div></div>
          <button className="tgl" style={{background:editRules.carry_over?BLK:"#DDD"}} onClick={()=>setEditRules(p=>({...p,carry_over:!p.carry_over}))}><div className="tglk" style={{left:editRules.carry_over?23:3}}/></button>
        </div>
        {/* Periodi speciali */}
        <div style={{borderTop:"1px solid #EEE",paddingTop:14,marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>📅 Periodi speciali</div>
          <div style={{fontSize:11,color:SUB,marginBottom:10,lineHeight:1.6}}>
            Aggiungi uno o più periodi (es. estate, Natale) in cui i dipendenti possono prenotare più SW consecutivi.
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <span style={{fontSize:13,fontWeight:600}}>Abilitati</span>
            <button className="tgl" style={{background:editRules.consec_enabled?BLK:"#DDD"}}
              onClick={()=>setEditRules(p=>({...p,consec_enabled:!p.consec_enabled}))}>
              <div className="tglk" style={{left:editRules.consec_enabled?23:3}}/>
            </button>
          </div>
          {editRules.consec_enabled&&<>
            {/* Periodo principale (sempre presente) */}
            <div style={{border:"1px solid #EEE",borderRadius:10,padding:"10px 12px",marginBottom:10,background:"#FAFAFA"}}>
              <div style={{fontSize:12,fontWeight:700,color:SUB,marginBottom:8}}>Periodo 1</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                <div><label className="lb">Dal</label><input type="date" value={(editRules.consec_from||"").substring(0,10)} onChange={e=>setEditRules(p=>({...p,consec_from:e.target.value}))}/></div>
                <div><label className="lb">Al</label><input type="date" value={(editRules.consec_to||"").substring(0,10)} onChange={e=>setEditRules(p=>({...p,consec_to:e.target.value}))}/></div>
              </div>
              <div><label className="lb">Max giorni consecutivi per volta</label>
                <input type="number" min={1} max={30} value={editRules.consec_max||5} onChange={e=>setEditRules(p=>({...p,consec_max:parseInt(e.target.value)||5}))}/>
              </div>
            </div>
            {/* Periodi aggiuntivi */}
            {(editRules.extra_periods||[]).map((p,i)=>(
              <div key={i} style={{border:"1px solid #EEE",borderRadius:10,padding:"10px 12px",marginBottom:10,background:"#FAFAFA"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <span style={{fontSize:12,fontWeight:700,color:SUB}}>Periodo {i+2}</span>
                  <button onClick={()=>setEditRules(prev=>{const ep=[...(prev.extra_periods||[])];ep.splice(i,1);return{...prev,extra_periods:ep};})} style={{background:"none",border:"none",cursor:"pointer",color:"#C0392B",fontSize:13}}>✕</button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  <div><label className="lb">Dal</label><input type="date" value={p.from||""} onChange={e=>setEditRules(prev=>{const ep=[...(prev.extra_periods||[])];ep[i]={...ep[i],from:e.target.value};return{...prev,extra_periods:ep};})}/></div>
                  <div><label className="lb">Al</label><input type="date" value={p.to||""} onChange={e=>setEditRules(prev=>{const ep=[...(prev.extra_periods||[])];ep[i]={...ep[i],to:e.target.value};return{...prev,extra_periods:ep};})}/></div>
                </div>
                <div><label className="lb">Max giorni consecutivi per volta</label>
                  <input type="number" min={1} max={30} value={p.max||5} onChange={e=>setEditRules(prev=>{const ep=[...(prev.extra_periods||[])];ep[i]={...ep[i],max:parseInt(e.target.value)||5};return{...prev,extra_periods:ep};})}/>
                </div>
              </div>
            ))}
            <button onClick={()=>setEditRules(prev=>({...prev,extra_periods:[...(prev.extra_periods||[]),{from:"",to:"",max:5}]}))} style={{width:"100%",padding:"8px",border:"1px dashed #CCC",borderRadius:9,background:"none",cursor:"pointer",fontSize:12,color:SUB,marginBottom:4}}>
              + Aggiungi periodo
            </button>
          </>}
        </div>
        <button className="bp" onClick={saveRules} style={{width:"100%"}}>Salva regole</button>
      </div>}

      {/* ─ STATISTICHE ─ */}
      {aTab==="stats"&&<div>
        <div style={{fontSize:10,color:SUB,marginBottom:12,textTransform:"uppercase",letterSpacing:1}}>Utilizzo {today.getFullYear()}</div>
        {team.filter(u=>isFixed(u)).map(u=>{
          const yu=yUsed(u.id,today.getFullYear());const eu=emUsed(u.id,today.getFullYear());
          const uR=getUR(u.id);const pct=Math.min((yu/uR.days_per_year)*100,100);
          const c=uSet.find(s=>s.user_id===u.id);
          return(<div key={u.id} className="card" style={{padding:"12px 16px",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:8}}>
              <div className="av" style={{background:u.color||LYB}}>{u.avatar_initials}</div>
              <div style={{flex:1}}><div style={{display:"flex",gap:7,alignItems:"center"}}><span style={{fontSize:13,fontWeight:700}}>{u.name}</span>{c&&<span style={{background:"#E8F0F5",color:LYB,borderRadius:4,padding:"1px 5px",fontSize:9,fontWeight:700}}>custom</span>}</div><div style={{fontSize:11,color:SUB}}>Ord: {yu}/{uR.days_per_year} · Em: {eu}/{uR.emergency_days}</div></div>
              <div style={{fontFamily:"'Space Mono',monospace",fontSize:20,fontWeight:700,color:pct>80?"#C0392B":pct>50?"#856404":"#1A7A3A"}}>{yu}</div>
            </div>
            <div className="pb"><div className="pf" style={{width:`${pct}%`,background:pct>80?"#C0392B":pct>50?"#F0A000":BLK}}/></div>
          </div>);
        })}
      </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ForceSwTab — Assegna SW con supporto backdating
// ─────────────────────────────────────────────────────────────
function ForceSwTab({ team, reqs, isFixed, isFullRemote, forceSW, delReq,
  fUser, fDate, fNote, setFUser, setFDate, setFNote,
  BLK, LYB, BDR, SUB, WHT, supabase, load, showToast }) {

  const [isBackdate,   setIsBackdate]   = useState(false);
  const [isEmForce,    setIsEmForce]    = useState(false);
  const [batchMode,    setBatchMode]    = useState(false);
  const [isDeskForce,  setIsDeskForce]  = useState(false);
  // Auto-switch to desk when FR user selected
  const handleUserChange = (uid) => {
    setFUser(uid);
    const u = team.find(t=>t.id===uid);
    if (u && isFullRemote(u)) setIsDeskForce(true);
  };
  const [batchDates,   setBatchDates]   = useState("");
  const [extraDays,    setExtraDays]    = useState(0);
  const [isHalfForce,  setIsHalfForce]  = useState(false);
  const [halfTypeForce,setHalfTypeForce]= useState("morning");

  const handleForce = async () => {
    if (!fUser) { showToast("Seleziona un dipendente", false); return; }
    const recType = isDeskForce ? "desk" : "Smart Working";
    const userName = team.find(t=>t.id===fUser)?.name||"";
    if (batchMode) {
      const dates = batchDates.split("\n").map(d=>d.trim()).filter(d=>d.match(/^\d{4}-\d{2}-\d{2}$/));
      if (dates.length === 0) { showToast("Inserisci almeno una data (formato YYYY-MM-DD)", false); return; }
      let ok = 0, fail = 0, skip = 0;
      for (const date of dates) {
        const alreadyExists = reqs.some(r=>r.user_id===fUser&&r.smart_date===date&&r.status!=="rejected"&&(isDeskForce?r.type==="desk":r.type!=="desk"));
        if (alreadyExists) { skip++; continue; }
        const { error } = await supabase.from("requests").insert({
          user_id: fUser, type: recType,
          date_from: date, date_to: date, smart_date: date,
          note: fNote || (isBackdate ? "Inserito retroattivamente" : "Assegnato dall'amministratore"),
          status: "approved", advance_days: 0,
          forced_by_admin: true, is_emergency: isEmForce, cancel_requested: false,
          half_day: isHalfForce ? halfTypeForce : null, company_id: team.find(t=>t.id===fUser)?.company_id||null
        });
        if (error) fail++; else ok++;
      }
      showToast(`${ok} inseriti${skip>0?`, ${skip} già presenti`:""}${fail>0?`, ${fail} errori`:""}`);
      setBatchDates(""); setFUser(""); setFNote("");
    } else {
      if (!fDate) { showToast("Seleziona una data", false); return; }
      const alreadyExists = reqs.some(r=>r.user_id===fUser&&r.smart_date===fDate&&r.status!=="rejected"&&(isDeskForce?r.type==="desk":r.type!=="desk"));
      if (alreadyExists) { showToast(`${userName} ha già una prenotazione per questa data`, false); return; }
      const { error } = await supabase.from("requests").insert({
        user_id: fUser, type: recType,
        date_from: fDate, date_to: fDate, smart_date: fDate,
        note: fNote || (isBackdate ? "Inserito retroattivamente" : "Assegnato dall'amministratore"),
        status: "approved", advance_days: 0,
        forced_by_admin: true, is_emergency: isEmForce, cancel_requested: false,
        half_day: isHalfForce ? halfTypeForce : null, company_id: team.find(t=>t.id===fUser)?.company_id||null
      });
      if (error) { showToast("Errore: " + error.message, false); return; }
      // Se giorni extra > 0, aggiorna extra_days nel profilo
      if (extraDays > 0) {
        const u = team.find(t=>t.id===fUser);
        const current = u?.extra_days || 0;
        await supabase.from("profiles").update({ extra_days: current + extraDays }).eq("id", fUser);
        showToast(`Inserito ✓ (+${extraDays} giorni extra aggiunti al budget di ${u?.name})`);
      } else {
        showToast("Inserito ✓");
      }
      setFUser(""); setFDate(""); setFNote(""); setExtraDays(0);
    }
    load();
  };

  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
      <div className="card">
        <div className="st" style={{color:"#856404"}}>Assegna / Inserisci Smart Working</div>
        <p style={{fontSize:12,color:SUB,marginBottom:16,lineHeight:1.6}}>
          Usa questa funzione per assegnare giorni futuri <strong>o</strong> per inserire giorni già effettuati (es. accordi precedenti, comunicazioni verbali).
        </p>

        {/* Toggle retroattivo */}
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:9,background:isBackdate?"#FFF8EE":"#F8F8F8",border:`1px solid ${isBackdate?"#F0C080":BDR}`,marginBottom:13}}>
          <button className="tgl" style={{background:isBackdate?BLK:"#DDD"}} onClick={()=>setIsBackdate(b=>!b)}>
            <div className="tglk" style={{left:isBackdate?23:3}}/>
          </button>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:isBackdate?"#856404":BLK}}>Inserimento retroattivo</div>
            <div style={{fontSize:11,color:SUB}}>Per giorni già effettuati o comunicati</div>
          </div>
        </div>

        {/* Toggle emergenza */}
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:9,background:isEmForce?"#FFF3EE":"#F8F8F8",border:`1px solid ${isEmForce?"#F0A060":BDR}`,marginBottom:13}}>
          <button className="tgl" style={{background:isEmForce?"#C05A00":"#DDD"}} onClick={()=>setIsEmForce(b=>!b)}>
            <div className="tglk" style={{left:isEmForce?23:3}}/>
          </button>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:isEmForce?"#C05A00":BLK}}>Giorno di emergenza</div>
            <div style={{fontSize:11,color:SUB}}>Conta nel budget emergenza, scala dal totale annuo</div>
          </div>
        </div>

        {/* Toggle tipo: SW o Postazione */}
        <div style={{display:"flex",gap:8,marginBottom:13}}>
          <button onClick={()=>setIsDeskForce(false)} style={{flex:1,padding:"8px 0",borderRadius:9,border:"2px solid "+(isDeskForce?"#DDD":"#222"),background:isDeskForce?"#F8F8F8":"#222",color:isDeskForce?"#888":"white",fontWeight:700,fontSize:13,cursor:"pointer"}}>🏠 Smart Working</button>
          <button onClick={()=>setIsDeskForce(true)} style={{flex:1,padding:"8px 0",borderRadius:9,border:"2px solid "+(isDeskForce?"#222":"#DDD"),background:isDeskForce?"#222":"#F8F8F8",color:isDeskForce?"white":"#888",fontWeight:700,fontSize:13,cursor:"pointer"}}>🪑 Postazione</button>
        </div>

        {/* Toggle batch */}
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:9,background:"#F8F8F8",border:"1px solid "+BDR,marginBottom:13}}>
          <button className="tgl" style={{background:batchMode?BLK:"#DDD"}} onClick={()=>setBatchMode(b=>!b)}>
            <div className="tglk" style={{left:batchMode?23:3}}/>
          </button>
          <div>
            <div style={{fontSize:13,fontWeight:700}}>Inserimento multiplo</div>
            <div style={{fontSize:11,color:SUB}}>Inserisci più date in una volta</div>
          </div>
        </div>

        {/* Toggle mezza giornata */}
        <div style={{marginBottom:13}}>
          <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:9,background:isHalfForce?"#EEF4FF":"#F8F8F8",border:`1px solid ${isHalfForce?"#93C5FD":BDR}`}}>
            <button className="tgl" style={{background:isHalfForce?"#3B82F6":"#DDD"}} onClick={()=>setIsHalfForce(b=>!b)}>
              <div className="tglk" style={{left:isHalfForce?23:3}}/>
            </button>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:isHalfForce?"#1D4ED8":BLK}}>Mezza giornata</div>
              <div style={{fontSize:11,color:SUB}}>Conta 0.5 giorni nel budget</div>
            </div>
          </div>
          {isHalfForce && (
            <div style={{display:"flex",gap:8,marginTop:8,paddingLeft:4}}>
              <button
                onClick={()=>setHalfTypeForce("morning")}
                style={{flex:1,padding:"7px 0",borderRadius:8,border:`2px solid ${halfTypeForce==="morning"?"#3B82F6":BDR}`,background:halfTypeForce==="morning"?"#EEF4FF":"white",color:halfTypeForce==="morning"?"#1D4ED8":BLK,fontWeight:700,fontSize:13,cursor:"pointer"}}
              >🌅 Mattina</button>
              <button
                onClick={()=>setHalfTypeForce("afternoon")}
                style={{flex:1,padding:"7px 0",borderRadius:8,border:`2px solid ${halfTypeForce==="afternoon"?"#3B82F6":BDR}`,background:halfTypeForce==="afternoon"?"#EEF4FF":"white",color:halfTypeForce==="afternoon"?"#1D4ED8":BLK,fontWeight:700,fontSize:13,cursor:"pointer"}}
              >🌇 Pomeriggio</button>
            </div>
          )}
        </div>

        <div style={{marginBottom:13}}>
          <label style={{fontSize:10,color:SUB,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.8}}>Dipendente</label>
          <select value={fUser} onChange={e=>handleUserChange(e.target.value)} style={{background:"#FAFAFA",border:"1px solid "+BDR,borderRadius:9,color:BLK,padding:"9px 12px",fontFamily:"'DM Sans',sans-serif",fontSize:13,outline:"none",width:"100%"}}>
            <option value="">Seleziona...</option>
            {(isDeskForce
              ? team.filter(u=>u.role!=="admin")
              : team.filter(u=>isFixed(u)||isFullRemote(u)||u.role==="collaborator")
            ).sort((a,b)=>a.name.localeCompare(b.name)).map(u=><option key={u.id} value={u.id}>{u.name}{isFullRemote(u)?" (FR)":isFixed(u)?" (Fisso)":u.role==="collaborator"?" (Collab.)":""}</option>)}
          </select>
        </div>

        {!batchMode ? (
          <div style={{marginBottom:13}}>
            <label style={{fontSize:10,color:SUB,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.8}}>
              Data {isBackdate?"(anche passata)":""}
            </label>
            <input type="date" value={fDate} onChange={e=>setFDate(e.target.value)} style={{background:"#FAFAFA",border:"1px solid "+BDR,borderRadius:9,color:BLK,padding:"9px 12px",fontFamily:"'DM Sans',sans-serif",fontSize:13,outline:"none",width:"100%"}}/>
          </div>
        ) : (
          <div style={{marginBottom:13}}>
            <label style={{fontSize:10,color:SUB,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.8}}>Date (una per riga, formato YYYY-MM-DD)</label>
            <textarea
              rows={6}
              value={batchDates}
              onChange={e=>setBatchDates(e.target.value)}
              placeholder={"2026-01-15\n2026-01-22\n2026-02-03"}
              style={{background:"#FAFAFA",border:"1px solid "+BDR,borderRadius:9,color:BLK,padding:"9px 12px",fontFamily:"'Space Mono',monospace",fontSize:12,outline:"none",width:"100%",resize:"none"}}
            />
            <div style={{fontSize:11,color:SUB,marginTop:4}}>
              {batchDates.split("\n").filter(d=>d.trim().match(/^\d{4}-\d{2}-\d{2}$/)).length} date valide
            </div>
          </div>
        )}

        <div style={{marginBottom:16}}>
          <label style={{fontSize:10,color:SUB,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.8}}>Nota interna</label>
          <input type="text" value={fNote} onChange={e=>setFNote(e.target.value)}
            placeholder={isBackdate?"Es. comunicato il 5 marzo":"Motivazione..."}
            style={{background:"#FAFAFA",border:"1px solid "+BDR,borderRadius:9,color:BLK,padding:"9px 12px",fontFamily:"'DM Sans',sans-serif",fontSize:13,outline:"none",width:"100%"}}/>
        </div>

        {/* Giorni extra bonus */}
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:9,background:extraDays>0?"#F0FFF4":"#F8F8F8",border:`1px solid ${extraDays>0?"#A5D6A7":BDR}`,marginBottom:13}}>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:extraDays>0?"#1A7A3A":BLK}}>Giorni extra bonus</div>
            <div style={{fontSize:11,color:SUB}}>Aggiungi giorni bonus al budget annuo del dipendente</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <button onClick={()=>setExtraDays(d=>Math.max(0,d-1))} style={{width:26,height:26,border:"1px solid #DDD",borderRadius:6,background:"white",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
            <span style={{fontFamily:"monospace",fontSize:15,fontWeight:700,minWidth:24,textAlign:"center",color:extraDays>0?"#1A7A3A":SUB}}>{extraDays>0?`+${extraDays}`:0}</span>
            <button onClick={()=>setExtraDays(d=>d+1)} style={{width:26,height:26,border:"1px solid #DDD",borderRadius:6,background:"white",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
          </div>
        </div>

        <button
          onClick={handleForce}
          style={{width:"100%",background:isBackdate?"#856404":BLK,color:"white",border:"none",borderRadius:9,padding:"10px 18px",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:700,cursor:"pointer"}}
        >
          {isDeskForce ? "Prenota postazione →" : isBackdate ? "Registra giorni passati →" : "Assegna →"}
        </button>
      </div>

      <div className="card">
        <div className="st">Storico assegnazioni admin</div>
        <div style={{display:"flex",gap:7,marginBottom:14,flexWrap:"wrap"}}>
          <span style={{fontSize:11,color:SUB}}>Filtro:</span>
          {["tutti","approvati","emergenza"].map(f=>(
            <span key={f} style={{fontSize:11,color:BLK,fontWeight:600,cursor:"default"}}>{f}</span>
          ))}
        </div>
        {reqs.filter(r=>r.forced_by_admin).length===0
          ? <div style={{color:SUB,fontSize:13}}>Nessuna assegnazione</div>
          : reqs.filter(r=>r.forced_by_admin).map(r=>{
              const u=team.find(t=>t.id===r.user_id);
              return(<div key={r.id} style={{display:"flex",alignItems:"center",gap:9,padding:"8px 0",borderBottom:"1px solid #F2F2F2"}}>
                <div style={{width:28,height:28,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:"#FFF",background:u?.color||"#AAA",flexShrink:0}}>{u?.avatar_initials}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600}}>{u?.name}</div>
                  <div style={{fontSize:11,color:SUB,fontFamily:"'Space Mono',monospace"}}>{r.smart_date}{r.is_emergency?" 🟠":""}</div>
                  {r.note&&<div style={{fontSize:10,color:"#AAA"}}>{r.note}</div>}
                </div>
                <button onClick={()=>delReq(r.id)} style={{background:"#FEE8E8",color:"#C0392B",border:"none",borderRadius:7,padding:"4px 9px",fontSize:11,cursor:"pointer"}}>🗑</button>
              </div>);
            })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ClosuresTab — Chiusure aziendali (distinto dai gg bloccati SW)
// ─────────────────────────────────────────────────────────────
function ClosuresTab({ blocked, blDay, unblDay, blDate, blDateTo, blReas, setBlDate, setBlDateTo, setBlReas, todayStr, BLK, BDR, SUB, LYB }) {
  const closures = blocked.filter(b => b.reason?.startsWith("[CHIUSURA]") || b.is_company_closure);
  const swBlocked = blocked.filter(b => !b.reason?.startsWith("[CHIUSURA]") && !b.is_company_closure);

  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
      <div className="card">
        <div className="st" style={{color:"#C0392B"}}>+ Aggiungi chiusura aziendale</div>
        <p style={{fontSize:12,color:SUB,marginBottom:14,lineHeight:1.6}}>
          Le chiusure aziendali bloccano <strong>tutte</strong> le prenotazioni SW e vengono evidenziate in rosso nel calendario. Vengono sincronizzate con Google Calendar se abilitato.
        </p>
        <div style={{marginBottom:12}}><label style={{fontSize:10,color:SUB,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.8}}>Data</label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.8,display:"block",marginBottom:5}}>Dal</label>
              <input type="date" value={blDate} onChange={e=>setBlDate(e.target.value)} style={{background:"#FAFAFA",border:"1px solid "+BDR,borderRadius:9,color:BLK,padding:"9px 12px",fontFamily:"'DM Sans',sans-serif",fontSize:13,outline:"none",width:"100%"}}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.8,display:"block",marginBottom:5}}>Al (facoltativo)</label>
              <input type="date" value={blDateTo||""} onChange={e=>setBlDateTo&&setBlDateTo(e.target.value)} min={blDate} style={{background:"#FAFAFA",border:"1px solid "+BDR,borderRadius:9,color:BLK,padding:"9px 12px",fontFamily:"'DM Sans',sans-serif",fontSize:13,outline:"none",width:"100%"}}/>
            </div>
          </div>
        </div>
        <div style={{marginBottom:16}}><label style={{fontSize:10,color:SUB,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.8}}>Descrizione</label>
          <input type="text" value={blReas} onChange={e=>setBlReas(e.target.value)} placeholder="Es. Natale, Festa nazionale, Chiusura estiva"
            style={{background:"#FAFAFA",border:"1px solid "+BDR,borderRadius:9,color:BLK,padding:"9px 12px",fontFamily:"'DM Sans',sans-serif",fontSize:13,outline:"none",width:"100%"}}/>
        </div>
        <button onClick={async()=>{
          if(!blDate){return;}
          await blDay("[CHIUSURA] "+(blReas||"Chiusura aziendale"));
        }} style={{width:"100%",background:"#C0392B",color:"white",border:"none",borderRadius:9,padding:10,fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:700,cursor:"pointer"}}>
          Aggiungi chiusura →
        </button>
      </div>
      <div>
        <div className="card" style={{marginBottom:14}}>
          <div className="st">Chiusure aziendali</div>
          {closures.length===0
            ?<div style={{color:SUB,fontSize:13}}>Nessuna chiusura impostata</div>
            :closures.map(b=>(<div key={b.id} style={{display:"flex",alignItems:"center",gap:11,padding:"8px 0",borderBottom:"1px solid #F5F5F5"}}>
              <span style={{fontSize:18}}>🏢</span>
              <div style={{flex:1}}>
                <div style={{fontFamily:"'Space Mono',monospace",fontSize:12,fontWeight:700,color:"#C0392B"}}>{b.date}</div>
                <div style={{fontSize:11,color:SUB}}>{b.reason?.replace("[CHIUSURA] ","")}</div>
              </div>
              <button onClick={()=>unblDay(b.id)} style={{background:"#FEE8E8",color:"#C0392B",border:"none",borderRadius:7,padding:"4px 9px",fontSize:11,cursor:"pointer"}}>✕</button>
            </div>))}
        </div>
        <div className="card">
          <div className="st" style={{fontSize:12}}>Giorni bloccati SW (non chiusure)</div>
          {swBlocked.length===0
            ?<div style={{color:SUB,fontSize:12}}>Nessuno</div>
            :swBlocked.map(b=>(<div key={b.id} style={{display:"flex",alignItems:"center",gap:9,padding:"6px 0",borderBottom:"1px solid #F5F5F5"}}>
              <div style={{fontFamily:"'Space Mono',monospace",fontSize:11,color:BLK,flex:1}}>{b.date} — {b.reason||"—"}</div>
              <button onClick={()=>unblDay(b.id)} style={{background:"#FEE8E8",color:"#C0392B",border:"none",borderRadius:6,padding:"3px 8px",fontSize:10,cursor:"pointer"}}>✕</button>
            </div>))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LogoSettingsTab — Upload logo aziendale + Google Calendar
// ─────────────────────────────────────────────────────────────
function LogoSettingsTab({ supabase, logoUrl, load, showToast, BLK, BDR, SUB, WHT, LYB }) {
  const [uploading, setUploading] = useState(false);
  const [gcalEnabled, setGcalEnabled] = useState(false);
  const [gcalId, setGcalId] = useState("");
  const fileRef = useRef(null);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { showToast("Seleziona un'immagine (PNG, JPG, SVG)", false); return; }
    if (file.size > 2*1024*1024) { showToast("Max 2MB", false); return; }
    setUploading(true);
    try {
      // Converti in base64 e salva come URL data
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target.result;
        const { error } = await supabase.from("app_settings")
          .upsert({ key: "logo_url", value: dataUrl }, { onConflict: "key" });
        if (error) { showToast("Errore upload: " + error.message, false); }
        else { showToast("Logo aggiornato ✓"); load(); }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch(e) { showToast("Errore: " + e.message, false); setUploading(false); }
  };

  const removeLogo = async () => {
    await supabase.from("app_settings").delete().eq("key", "logo_url");
    showToast("Logo rimosso"); load();
  };

  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
      {/* LOGO */}
      <div className="card">
        <div className="st">🎨 Logo aziendale</div>
        <p style={{fontSize:12,color:SUB,marginBottom:16,lineHeight:1.6}}>
          Il logo appare nella sidebar e nella schermata di login. Formati supportati: PNG, JPG, SVG. Max 2MB.
        </p>

        {/* Preview */}
        <div style={{width:80,height:80,borderRadius:16,background:"#1A1A1A",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:16,overflow:"hidden",border:"2px solid #E2E2E2"}}>
          {logoUrl
            ? <img src={logoUrl} alt="logo" style={{width:80,height:80,objectFit:"cover"}}/>
            : <span style={{color:"#666",fontSize:11}}>No logo</span>}
        </div>

        <input type="file" ref={fileRef} accept="image/*" style={{display:"none"}} onChange={e=>uploadLogo(e.target.files[0])}/>
        <button className="bp" onClick={()=>fileRef.current?.click()} disabled={uploading}
          style={{width:"100%",marginBottom:9,opacity:uploading?.7:1}}>
          {uploading ? "Caricamento..." : "📁 Carica logo"}
        </button>
        {logoUrl&&<button onClick={removeLogo} style={{width:"100%",background:"#FEE8E8",color:"#C0392B",border:"none",borderRadius:9,padding:9,fontSize:13,cursor:"pointer",fontWeight:600}}>
          🗑 Rimuovi logo
        </button>}
        <div style={{fontSize:11,color:SUB,marginTop:10,lineHeight:1.5}}>
          Suggerimento: usa un'immagine quadrata (es. 200×200px) per il miglior risultato nella sidebar.
        </div>
      </div>

      {/* GOOGLE CALENDAR */}
      <div className="card">
        <div className="st">📅 Integrazione Google Calendar</div>
        <p style={{fontSize:12,color:SUB,marginBottom:16,lineHeight:1.7}}>
          Sincronizza le approvazioni SW e le chiusure aziendali con un calendario Google condiviso.
        </p>

        <div style={{background:"#FFF8E0",border:"1px solid #F0C040",borderRadius:9,padding:12,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:"#856404",marginBottom:6}}>Come configurare</div>
          <ol style={{fontSize:11,color:"#7A5A00",paddingLeft:16,lineHeight:2}}>
            <li>Apri <strong>Google Calendar</strong></li>
            <li>Crea un calendario "LikeYou SW" e rendilo pubblico</li>
            <li>Vai in Impostazioni → copia l'<strong>ID calendario</strong> (es. abc@group.calendar.google.com)</li>
            <li>Incollalo qui sotto e abilita la sincronizzazione</li>
          </ol>
        </div>

        <div style={{marginBottom:12}}>
          <label style={{fontSize:10,color:SUB,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.8}}>ID Calendario Google</label>
          <input type="text" value={gcalId} onChange={e=>setGcalId(e.target.value)}
            placeholder="es. likeyou@group.calendar.google.com"
            style={{background:"#FAFAFA",border:"1px solid "+BDR,borderRadius:9,color:BLK,padding:"9px 12px",fontFamily:"'DM Sans',sans-serif",fontSize:12,outline:"none",width:"100%"}}/>
        </div>

        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderTop:"1px solid "+BDR,marginBottom:14}}>
          <div><div style={{fontSize:13,fontWeight:600}}>Sincronizzazione attiva</div><div style={{fontSize:11,color:SUB}}>Le approvazioni SW vengono aggiunte al calendario</div></div>
          <button className="tgl" style={{background:gcalEnabled?BLK:"#DDD"}} onClick={()=>setGcalEnabled(g=>!g)}>
            <div className="tglk" style={{left:gcalEnabled?23:3}}/>
          </button>
        </div>

        <button className="bp" onClick={async()=>{
          if(!gcalId.trim()){showToast("Inserisci l'ID del calendario",false);return;}
          const{error}=await supabase.from("app_settings").upsert([
            {key:"gcal_id",value:gcalId.trim()},
            {key:"gcal_enabled",value:gcalEnabled?"true":"false"}
          ],{onConflict:"key"});
          if(error){showToast("Errore: "+error.message,false);return;}
          showToast("Impostazioni Google Calendar salvate ✓");
        }} style={{width:"100%"}}>Salva impostazioni Google Calendar →</button>

        <div style={{marginTop:12,padding:10,background:"#F5F5F5",borderRadius:9,fontSize:11,color:SUB,lineHeight:1.6}}>
          <strong>Nota:</strong> L'integrazione completa richiede una Google Apps Script o un webhook Supabase. 
          Il calendario può essere condiviso in sola lettura con tutto il team tramite il link pubblico Google.
          Contatta Giovanni Rago per la configurazione avanzata.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// NotificationsTab — Pannello gestione notifiche email
// ─────────────────────────────────────────────────────────────

// Testi di default per ogni tipo di notifica

const DEFAULT_NOTIF_TEXTS = {
  sw_approved:     { subject:"✅ Smart Working approvato — {{data}}", body:"Ciao {{nome}},\n\nil tuo smart working del {{data}} è stato approvato.\nSede: {{sede}}\n\nBuon lavoro da casa! 🏠\n\nLikeYou Team" },
  sw_rejected:     { subject:"❌ Smart Working non approvato — {{data}}", body:"Ciao {{nome}},\n\npurtroppo la tua richiesta di smart working per il {{data}} non è stata approvata.\n\nPer informazioni contatta l\'amministratore.\n\nLikeYou Team" },
  new_sw_request:  { subject:"📋 Nuova richiesta SW da {{richiedente}} — {{data}}", body:"Nuova richiesta in attesa di approvazione.\n\nDa: {{richiedente}}\nData: {{data}}\nSede: {{sede}}\n\nAccedi al pannello per approvarla o rifiutarla." },
  swap_request:    { subject:"🔁 Proposta scambio SW da {{richiedente}}", body:"Ciao,\n\n{{richiedente}} ha proposto uno scambio di giornata SW.\n\nData proposta da {{richiedente}}: {{data_richiedente}}\nData richiesta a te: {{data_target}}\n\nAccedi al pannello per accettare o rifiutare lo scambio." },
  cancel_approved: { subject:"🗑 Cancellazione SW approvata — {{data}}", body:"Ciao {{nome}},\n\nla tua richiesta di cancellazione per il {{data}} è approvata. Il giorno è tornato disponibile.\n\nLikeYou Team" },
  cancel_rejected: { subject:"ℹ️ Cancellazione SW non approvata — {{data}}", body:"Ciao {{nome}},\n\nla tua richiesta di cancellazione per il {{data}} non è stata approvata. Il giorno rimane prenotato.\n\nLikeYou Team" },
  desk_deleted:    { subject:"🪑 Postazione rimossa — {{data}}", body:"Ciao {{nome}},\n\nla tua prenotazione della postazione per il {{data}} è stata rimossa dall'amministratore.\n\nSe hai dubbi contatta l'amministratore.\n\nLikeYou Team" },
  cancel_approved_desk: { subject:"🗑 Cancellazione postazione approvata — {{data}}", body:"Ciao {{nome}},\n\nla tua richiesta di cancellazione della postazione per il {{data}} è approvata.\n\nLikeYou Team" },
  cancel_rejected_desk: { subject:"ℹ️ Cancellazione postazione non approvata — {{data}}", body:"Ciao {{nome}},\n\nla tua richiesta di cancellazione della postazione per il {{data}} non è stata approvata.\n\nLikeYou Team" },
  birthday:        { subject:"🎂 Oggi è il compleanno di {{nome}}!", body:"Ciao a tutti,\n\noggi è il compleanno di {{nome}}! 🎂\n\nRicordatevi di fargli/le gli auguri!\n\nLikeYou Team" },
  birthday_self:   { subject:"🎂 Tanti auguri {{nome}}!", body:"Ciao {{nome}},\n\ntutto il team di LikeYou ti fa i migliori auguri per il tuo compleanno! 🎉🎂\n\nBuona giornata speciale!\n\nLikeYou Team" },
  welcome:         { subject:"👋 Benvenuto/a su LikeYou SW — le tue credenziali", body:"Ciao {{nome}},\n\nsei stato/a aggiunto/a alla piattaforma Smart Working di LikeYou!\n\n🔑 Le tue credenziali di accesso:\nEmail: {{email}}\nPassword temporanea: {{password}}\n\n🔗 Accedi su: sw.likeyousrl.com\n\nTi consigliamo di cambiare la password al primo accesso.\n\nBenvenuto/a nel team! 🚀\n\nLikeYou Team" },
};

const NOTIF_LABELS = {
  sw_approved:          "SW approvato",
  sw_rejected:          "SW rifiutato",
  new_sw_request:       "Nuova richiesta SW",
  cancel_approved:      "Cancellazione SW approvata",
  cancel_rejected:      "Cancellazione SW non approvata",
  desk_deleted:         "Postazione rimossa dall'admin",
  cancel_approved_desk: "Cancellazione postazione approvata",
  cancel_rejected_desk: "Cancellazione postazione non approvata",
  birthday:             "Compleanno (ai colleghi)",
  birthday_self:        "Auguri di compleanno (all'interessato)",
  welcome:              "Email di benvenuto",
};

const NOTIF_VARS = {
  sw_approved:          ["{{nome}}", "{{data}}", "{{sede}}"],
  sw_rejected:          ["{{nome}}", "{{data}}"],
  new_sw_request:       ["{{richiedente}}", "{{data}}", "{{sede}}"],
  swap_request:         ["{{richiedente}}", "{{data_richiedente}}", "{{data_target}}"],
  cancel_approved:      ["{{nome}}", "{{data}}"],
  cancel_rejected:      ["{{nome}}", "{{data}}"],
  desk_deleted:         ["{{nome}}", "{{data}}"],
  cancel_approved_desk: ["{{nome}}", "{{data}}"],
  cancel_rejected_desk: ["{{nome}}", "{{data}}"],
  birthday:             ["{{nome}}"],
  birthday_self:        ["{{nome}}"],
  welcome:              ["{{nome}}", "{{email}}", "{{password}}"],
};

function NotificationsTab({ notifSettings, setNotifSettings, supabase, showToast, BLK, BDR, SUB, LYB, WHT, logoUrl }) {
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [activeSection, setActiveSection] = useState("toggles");
  const [editingType, setEditingType] = useState(null);
  const [notifTexts, setNotifTexts] = useState({});
  const [brandColor, setBrandColor] = useState("#1A1A1A");
  const [brandName, setBrandName] = useState("LikeYou SW");
  const [brandTextColor, setBrandTextColor] = useState("#FFFFFF");
  const [brandSepColor, setBrandSepColor] = useState("#1A7A3A");
  const [brandFooterText, setBrandFooterText] = useState("LikeYou SW — Piattaforma Smart Working");
  const [brandSaving, setBrandSaving] = useState(false);

  React.useEffect(() => {
    supabase.from("app_settings").select("*").eq("key","notif_texts").maybeSingle().then(({data}) => {
      if (data?.value) { try { setNotifTexts(JSON.parse(data.value)); } catch(e){} }
    });
    supabase.from("app_settings").select("*").eq("key","email_brand").maybeSingle().then(({data}) => {
      if (data?.value) { try { const b=JSON.parse(data.value); if(b.color) setBrandColor(b.color); if(b.name) setBrandName(b.name); if(b.textColor) setBrandTextColor(b.textColor); if(b.sepColor) setBrandSepColor(b.sepColor); if(b.footerText) setBrandFooterText(b.footerText); } catch(e){} }
    });
  }, []);

  const getTexts = (type) => ({ ...DEFAULT_NOTIF_TEXTS[type], ...(notifTexts[type] || {}) });

  const saveTexts = async (newTexts) => {
    setSaving(true);
    const { error } = await supabase.from("app_settings").upsert({ key:"notif_texts", value:JSON.stringify(newTexts) }, { onConflict:"key" });
    setSaving(false);
    if (error) { showToast("Errore: "+error.message, false); return false; }
    setNotifTexts(newTexts);
    showToast("Testi salvati ✓");
    return true;
  };

  const saveSettings = async (newSettings) => {
    setSaving(true);
    const { error } = await supabase.from("app_settings").upsert({ key:"notif_settings", value:JSON.stringify(newSettings) }, { onConflict:"key" });
    setSaving(false);
    if (error) { showToast("Errore: "+error.message, false); return; }
    setNotifSettings(newSettings);
    showToast("Impostazioni salvate ✓");
  };

  const toggle = (key) => saveSettings({ ...notifSettings, [key]: !notifSettings[key] });
  const toggleAll = (enabled) => saveSettings({ ...notifSettings, enabled });
  const globalOn = notifSettings.enabled !== false;

  const saveBrand = async () => {
    setBrandSaving(true);
    await supabase.from("app_settings").upsert({ key:"email_brand", value:JSON.stringify({ color:brandColor, name:brandName, textColor:brandTextColor, sepColor:brandSepColor, footerText:brandFooterText }) }, { onConflict:"key" });
    try { localStorage.setItem("sw_notif_brand", JSON.stringify({ color:brandColor, name:brandName, textColor:brandTextColor, sepColor:brandSepColor, footerText:brandFooterText })); } catch(e){}
    setBrandSaving(false);
    showToast("Aspetto email salvato ✓");
  };

  // Genera HTML preview uguale a quello dell'edge function
  const buildPreviewHtml = (type, subj, bodyText) => {
    const TYPE_META_LOCAL = {
      sw_approved:{ color:"#1A7A3A",icon:"✅",label:"Smart Working Approvato" },
      sw_rejected:{ color:"#C0392B",icon:"❌",label:"Smart Working Non Approvato" },
      new_sw_request:{ color:"#2563EB",icon:"📋",label:"Nuova Richiesta SW" },
      swap_request:{ color:"#2563EB",icon:"🔁",label:"Proposta Scambio SW" },
      cancel_approved:{ color:"#7C3AED",icon:"🗑️",label:"Cancellazione Approvata" },
      cancel_rejected:{ color:"#92400E",icon:"ℹ️",label:"Cancellazione Non Approvata" },
      desk_deleted:{ color:"#C0392B",icon:"🪑",label:"Postazione Rimossa" },
      cancel_approved_desk:{ color:"#7C3AED",icon:"🗑️",label:"Cancellazione Postazione Approvata" },
      cancel_rejected_desk:{ color:"#92400E",icon:"ℹ️",label:"Cancellazione Postazione Non Approvata" },
      birthday:{ color:"#B45309",icon:"🎂",label:"Compleanno" },
      birthday_self:{ color:"#B45309",icon:"🎉",label:"Tanti Auguri!" },
    };
    const meta = TYPE_META_LOCAL[type] || { color:"#1A1A1A", icon:"📧", label:"Notifica" };
    const accent = meta.color;
    const hdr = brandColor;
    const logoHtml = logoUrl
      ? `<img src="${logoUrl}" alt="logo" style="height:32px;width:32px;object-fit:cover;border-radius:7px;vertical-align:middle;margin-right:10px"/>`
      : `<span style="color:${brandTextColor};font-size:18px;font-weight:900;font-family:'Courier New',monospace">LY</span>`;
    const rows = bodyText.split("\n").map(l => l.trim()===""
      ? `<tr><td style="height:8px"></td></tr>`
      : `<tr><td style="color:#333;font-size:14px;line-height:1.7;padding:0 0 3px 0">${l}</td></tr>`).join("");
    return `<div style="background:#F2F2F2;padding:20px 10px;font-family:Helvetica,Arial,sans-serif">
      <div style="max-width:500px;margin:0 auto;border-radius:14px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,.1)">
        <div style="background:${hdr};padding:16px 24px">
          ${logoHtml}
          <span style="color:${brandTextColor};font-size:10px;font-weight:700;letter-spacing:3px;vertical-align:middle;opacity:.9">${brandName.toUpperCase()}</span>
        </div>
        <div style="height:3px;background:${brandSepColor}"></div>
        <div style="background:#fff;padding:20px 24px 0">
          <div style="display:inline-block;background:${accent}18;border:1px solid ${accent}55;border-radius:7px;padding:5px 12px;font-size:12px;font-weight:700;color:${accent}">${meta.icon} ${meta.label}</div>
        </div>
        <div style="background:#fff;padding:16px 24px 24px">
          <table cellpadding="0" cellspacing="0" width="100%">${rows}</table>
        </div>
        <div style="background:#FAFAFA;border-top:1px solid #EEE;padding:12px 24px;text-align:center">
          <span style="font-size:10px;color:#BBB">${brandFooterText}</span>
        </div>
      </div>
    </div>`;
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { showToast("Inserisci un\'email di test", false); return; }
    setTestSending(true);
    try {
      const fnRes = await fetch("https://kmesrtgnkohmflyoestm.supabase.co/functions/v1/send-notification", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ type:"sw_approved", to:testEmail.trim(), name:"Utente Test", date:new Date().toISOString().slice(0,10), note:"Via Roma 10, Bologna", logoUrl:logoUrl||null, brandColor, brandName, brandTextColor, brandSepColor, brandFooterText })
      });
      const fnData = await fnRes.json().catch(()=>({}));
      if (!fnRes.ok) showToast("❌ Errore "+fnRes.status+": "+(fnData?.error||fnData?.result?.message||"vedi console"), false);
      else if (fnData?.simulated) showToast("⚠️ Simulata — aggiungi RESEND_API_KEY nei Secrets Supabase", false);
      else showToast("✉️ Test inviato a "+testEmail);
    } catch(e) { showToast("Errore: "+e.message, false); }
    setTestSending(false);
  };

  const NOTIF_TYPES = [
    { group:"👤 Dipendente → Admin", items:[
      { key:"new_sw_request", label:"Nuova richiesta SW",    desc:"L\'admin riceve email quando un dipendente invia una richiesta" },
      { key:"swap_request",   label:"Proposta scambio SW",   desc:"L\'admin riceve email quando viene proposto uno scambio" },
    ]},
    { group:"📋 Admin → Dipendente", items:[
      { key:"sw_approved",          label:"Richiesta SW approvata",             desc:"Il dipendente riceve email di conferma" },
      { key:"sw_rejected",          label:"Richiesta SW rifiutata",             desc:"Il dipendente riceve email di rifiuto" },
      { key:"cancel_approved",      label:"Cancellazione SW approvata",         desc:"Il dipendente riceve email di conferma cancellazione" },
      { key:"cancel_rejected",      label:"Cancellazione SW non approvata",     desc:"Il dipendente riceve email di rifiuto cancellazione" },
      { key:"desk_deleted",         label:"Postazione rimossa",                 desc:"Il dipendente riceve email quando l'admin rimuove una postazione" },
      { key:"cancel_approved_desk", label:"Cancellazione postazione approvata", desc:"Il dipendente riceve email di conferma" },
      { key:"cancel_rejected_desk", label:"Cancellazione postazione negata",    desc:"Il dipendente riceve email di rifiuto" },
    ]},
    { group:"🎂 Automatiche", items:[
      { key:"birthday",      label:"Email di compleanno ai colleghi", desc:"Tutti ricevono email il giorno del compleanno di un collega" },
      { key:"birthday_self", label:"Auguri all'interessato",          desc:"La persona festeggiata riceve un'email di auguri dal team" },
    ]},
  ];

  const TAB_STYLE = (active) => ({
    padding:"8px 16px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", border:"none",
    background:active?BLK:"transparent", color:active?"white":SUB,
  });

  // Editor inline
  const TextEditor = ({ type, onClose }) => {
    const cur = getTexts(type);
    const [subj, setSubj] = useState(cur.subject);
    const [body, setBody] = useState(cur.body);
    const vars = NOTIF_VARS[type] || [];
    const hasCustom = !!(notifTexts[type]);
    const preview = (t) => t.replace(/{{nome}}/g,"Mario Rossi").replace(/{{richiedente}}/g,"Mario Rossi").replace(/{{data}}/g,"2025-06-15").replace(/{{sede}}/g,"Via Roma 10, Bologna").replace(/{{data_richiedente}}/g,"2025-06-15").replace(/{{data_target}}/g,"2025-06-20");
    const [showHtmlPreview, setShowHtmlPreview] = React.useState(false);

    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
        <div style={{background:"white",borderRadius:16,padding:24,width:"100%",maxWidth:580,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.3)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}>
            <div style={{flex:1}}>
              <div style={{fontSize:15,fontWeight:700}}>✏️ {NOTIF_LABELS[type]}</div>
              <div style={{fontSize:11,color:SUB}}>Personalizza oggetto e testo</div>
            </div>
            {hasCustom&&<button onClick={async()=>{const r={...notifTexts};delete r[type];await saveTexts(r);setSubj(DEFAULT_NOTIF_TEXTS[type].subject);setBody(DEFAULT_NOTIF_TEXTS[type].body);}} style={{fontSize:11,color:"#C0392B",background:"#FEE8E8",border:"none",borderRadius:7,padding:"4px 9px",cursor:"pointer"}}>↩ Reset</button>}
            <button onClick={onClose} style={{background:"#F0F0F0",border:"none",borderRadius:8,width:32,height:32,cursor:"pointer",fontSize:16,fontWeight:700}}>✕</button>
          </div>
          {vars.length>0&&<div style={{background:"#F0F5FF",border:"1px solid #C8D8FF",borderRadius:9,padding:"9px 13px",marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:"#3B5998",marginBottom:6}}>Variabili — clicca per copiare</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
              {vars.map(v=><span key={v} onClick={()=>{navigator.clipboard?.writeText(v);showToast(v+" copiata");}} style={{background:"#E0EAFF",border:"1px solid #B0CAFF",borderRadius:6,padding:"3px 9px",fontSize:12,fontFamily:"monospace",cursor:"pointer"}}>{v}</span>)}
            </div>
          </div>}
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:4}}>Oggetto</label>
            <input value={subj} onChange={e=>setSubj(e.target.value)} style={{width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid #E2E2E2",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div style={{marginBottom:14}}>
            <label style={{fontSize:11,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:4}}>Testo email</label>
            <textarea value={body} onChange={e=>setBody(e.target.value)} rows={6} style={{width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid #E2E2E2",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box",resize:"vertical",lineHeight:1.8}}/>
          </div>
          {/* Preview toggle */}
          <div style={{marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontSize:11,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.6}}>Anteprima</span>
              <div style={{display:"flex",gap:4}}>
                <button onClick={()=>setShowHtmlPreview(false)} style={{fontSize:11,padding:"3px 10px",borderRadius:6,border:"1px solid #E2E2E2",cursor:"pointer",background:!showHtmlPreview?"#1A1A1A":"#F5F5F5",color:!showHtmlPreview?"white":"#666"}}>📄 Testo</button>
                <button onClick={()=>setShowHtmlPreview(true)}  style={{fontSize:11,padding:"3px 10px",borderRadius:6,border:"1px solid #E2E2E2",cursor:"pointer",background:showHtmlPreview?"#1A1A1A":"#F5F5F5",color:showHtmlPreview?"white":"#666"}}>🖼 Email reale</button>
              </div>
            </div>
            {!showHtmlPreview
              ? <div style={{background:"#F9F9F9",border:"1px solid #E8E8E8",borderRadius:9,padding:12}}>
                  <div style={{fontSize:11,color:"#888",marginBottom:4}}>Oggetto: <strong style={{color:"#333"}}>{preview(subj)}</strong></div>
                  <div style={{fontSize:12,color:"#444",whiteSpace:"pre-wrap",lineHeight:1.7,borderTop:"1px solid #EEE",paddingTop:8}}>{preview(body)}</div>
                </div>
              : <div style={{border:"1px solid #E8E8E8",borderRadius:9,overflow:"hidden",maxHeight:360,overflowY:"auto"}}>
                  <iframe
                    title="preview"
                    srcDoc={buildPreviewHtml(type, preview(subj), preview(body))}
                    style={{width:"100%",height:360,border:"none",display:"block"}}
                    sandbox="allow-same-origin"
                  />
                </div>
            }
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={async()=>{const ok=await saveTexts({...notifTexts,[type]:{subject:subj,body}});if(ok)onClose();}} style={{flex:1,background:BLK,color:"white",border:"none",borderRadius:10,padding:11,fontSize:14,fontWeight:700,cursor:"pointer"}}>Salva</button>
            <button onClick={onClose} style={{flex:1,background:"#F0F0F0",border:"none",borderRadius:10,padding:11,fontSize:14,cursor:"pointer"}}>Annulla</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{maxWidth:620}}>
      {editingType&&<TextEditor type={editingType} onClose={()=>setEditingType(null)}/>}

      {/* Switcher sezioni */}
      <div style={{display:"flex",gap:3,background:"#F5F5F5",borderRadius:10,padding:4,marginBottom:18,width:"fit-content"}}>
        <button style={TAB_STYLE(activeSection==="toggles")} onClick={()=>setActiveSection("toggles")}>⚙️ Attiva/disattiva</button>
        <button style={TAB_STYLE(activeSection==="texts")}   onClick={()=>setActiveSection("texts")}>✏️ Testi email</button>
        <button style={TAB_STYLE(activeSection==="brand")}   onClick={()=>setActiveSection("brand")}>🎨 Aspetto</button>
        <button style={TAB_STYLE(activeSection==="setup")}   onClick={()=>setActiveSection("setup")}>🔧 Setup</button>
      </div>

      {/* SEZIONE: TOGGLES */}
      {activeSection==="toggles"&&<>
        <div className="card" style={{marginBottom:14,padding:"13px 16px",borderColor:globalOn?LYB+"44":"#F5C0C0",background:globalOn?WHT:"#FFF8F8"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,marginBottom:2}}>{globalOn?"📧 Notifiche email attive":"🔕 Notifiche disabilitate"}</div>
              <div style={{fontSize:12,color:SUB}}>{globalOn?"Le email vengono inviate":"Nessuna email viene inviata"}</div>
            </div>
            <button className="tgl" style={{background:globalOn?BLK:"#DDD"}} onClick={()=>toggleAll(!globalOn)}>
              <div className="tglk" style={{left:globalOn?23:3}}/>
            </button>
          </div>
        </div>
        <div style={{opacity:globalOn?1:.45,pointerEvents:globalOn?"auto":"none"}}>
          {NOTIF_TYPES.map(g=>(
            <div key={g.group} className="card" style={{marginBottom:10,padding:"11px 15px"}}>
              <div style={{fontSize:10,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.7,marginBottom:9}}>{g.group}</div>
              {g.items.map((item,i)=>{
                const isOn=notifSettings[item.key]!==false;
                const hasCustom=!!(notifTexts[item.key]);
                return(
                <div key={item.key} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i<g.items.length-1?"1px solid #F5F5F5":"none"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                      {item.label}
                      {hasCustom&&<span style={{fontSize:9,background:"#E0EAFF",color:"#3B5998",borderRadius:4,padding:"1px 5px"}}>personalizzata</span>}
                    </div>
                    <div style={{fontSize:11,color:SUB}}>{item.desc}</div>
                  </div>
                  {DEFAULT_NOTIF_TEXTS[item.key]&&<button onClick={()=>setEditingType(item.key)} style={{fontSize:11,background:"#F5F5F5",border:"1px solid #E2E2E2",borderRadius:6,padding:"3px 8px",cursor:"pointer",color:SUB,whiteSpace:"nowrap"}}>✏️ testo</button>}
                  <button className="tgl" style={{background:isOn?BLK:"#DDD",flexShrink:0}} onClick={()=>toggle(item.key)}>
                    <div className="tglk" style={{left:isOn?23:3}}/>
                  </button>
                </div>
                );
              })}
            </div>
          ))}
        </div>
      </>}

      {/* SEZIONE: TESTI */}
      {activeSection==="texts"&&<div>
        <p style={{fontSize:13,color:SUB,marginBottom:14}}>Clicca Modifica per personalizzare il testo di ogni email. Le variabili come <code style={{background:"#F0F0F0",padding:"1px 4px",borderRadius:3,fontSize:11}}>{"{{nome}}"}</code> vengono sostituite automaticamente.</p>
        {Object.entries(NOTIF_LABELS).map(([key,label])=>{
          const cur=getTexts(key);
          const hasCustom=!!(notifTexts[key]);
          return(
          <div key={key} className="card" style={{marginBottom:8,padding:"11px 15px",borderColor:hasCustom?LYB+"55":undefined}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                  {label}
                  {hasCustom&&<span style={{fontSize:9,background:"#E0EAFF",color:"#3B5998",borderRadius:4,padding:"1px 5px",flexShrink:0}}>personalizzata</span>}
                </div>
                <div style={{fontSize:11,color:SUB,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cur.subject}</div>
              </div>
              <button onClick={()=>setEditingType(key)} style={{background:BLK,color:"white",border:"none",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer",flexShrink:0}}>✏️ Modifica</button>
            </div>
          </div>
          );
        })}
      </div>}

      {/* SEZIONE: BRAND */}
      {activeSection==="brand"&&<div>
        <div className="card" style={{marginBottom:14,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>🎨 Aspetto delle email</div>
          <div style={{fontSize:12,color:SUB,marginBottom:18}}>Personalizza colore header e nome mittente. Le modifiche si riflettono nell'anteprima.</div>

          <div style={{marginBottom:16}}>
            <label style={{fontSize:11,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:6}}>Colore header</label>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <input type="color" value={brandColor} onChange={e=>setBrandColor(e.target.value)}
                style={{width:48,height:48,border:"2px solid #E2E2E2",borderRadius:10,cursor:"pointer",padding:3,background:"none"}}/>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:brandColor}}>●</div>
                <div style={{fontSize:12,color:SUB,fontFamily:"monospace"}}>{brandColor}</div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["#1A1A1A","#FFFFFF","#7A9BAD","#2563EB","#16A34A","#9333EA","#DC2626","#EA580C"].map(c=>(
                  <div key={c} onClick={()=>setBrandColor(c)}
                    style={{width:26,height:26,borderRadius:6,background:c,cursor:"pointer",border:brandColor===c?"3px solid #555":c==="#FFFFFF"?"2px solid #CCC":"2px solid #E2E2E2"}}/>
                ))}
              </div>
            </div>
          </div>

          <div style={{marginBottom:20}}>
            <label style={{fontSize:11,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:6}}>Nome nell'header email</label>
            <input value={brandName} onChange={e=>setBrandName(e.target.value)}
              style={{width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid #E2E2E2",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}
              placeholder="LikeYou SW"/>
            <div style={{fontSize:11,color:SUB,marginTop:4}}>Viene mostrato accanto al logo nell'header delle email</div>
          </div>

          <div style={{marginBottom:16}}>
            <label style={{fontSize:11,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:6}}>Colore testo header</label>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <input type="color" value={brandTextColor} onChange={e=>setBrandTextColor(e.target.value)}
                style={{width:48,height:48,border:"2px solid #E2E2E2",borderRadius:10,cursor:"pointer",padding:3,background:"none"}}/>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:brandTextColor,textShadow:"0 0 2px #999"}}>●</div>
                <div style={{fontSize:12,color:SUB,fontFamily:"monospace"}}>{brandTextColor}</div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["#FFFFFF","#000000","#F5F5F5","#FFD700","#A0C4FF","#C8F7C5"].map(c=>(
                  <div key={c} onClick={()=>setBrandTextColor(c)}
                    style={{width:26,height:26,borderRadius:6,background:c,cursor:"pointer",border:brandTextColor===c?"3px solid #555":c==="#FFFFFF"?"2px solid #CCC":"2px solid #E2E2E2"}}/>
                ))}
              </div>
            </div>
          </div>

          <div style={{marginBottom:16}}>
            <label style={{fontSize:11,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:6}}>Colore riga separatrice</label>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <input type="color" value={brandSepColor} onChange={e=>setBrandSepColor(e.target.value)}
                style={{width:48,height:48,border:"2px solid #E2E2E2",borderRadius:10,cursor:"pointer",padding:3,background:"none"}}/>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:brandSepColor}}>●</div>
                <div style={{fontSize:12,color:SUB,fontFamily:"monospace"}}>{brandSepColor}</div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["#1A7A3A","#1A1A1A","#2563EB","#DC2626","#9333EA","#EA580C","#B45309","#E2E2E2"].map(c=>(
                  <div key={c} onClick={()=>setBrandSepColor(c)}
                    style={{width:26,height:26,borderRadius:6,background:c,cursor:"pointer",border:brandSepColor===c?"3px solid #555":"2px solid #E2E2E2"}}/>
                ))}
              </div>
            </div>
          </div>

          <div style={{marginBottom:20}}>
            <label style={{fontSize:11,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:6}}>Testo footer email</label>
            <input value={brandFooterText} onChange={e=>setBrandFooterText(e.target.value)}
              style={{width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid #E2E2E2",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}
              placeholder="LikeYou SW — Piattaforma Smart Working"/>
            <div style={{fontSize:11,color:SUB,marginTop:4}}>Testo piccolo in fondo a ogni email</div>
          </div>

          {/* Anteprima live header */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:SUB,textTransform:"uppercase",letterSpacing:.6,marginBottom:8}}>Anteprima header</div>
            <div style={{borderRadius:10,overflow:"hidden",border:"1px solid #E2E2E2"}}>
              <div style={{background:brandColor,padding:"14px 20px",display:"flex",alignItems:"center",gap:10}}>
                {logoUrl
                  ? <img src={logoUrl} alt="logo" style={{width:32,height:32,objectFit:"cover",borderRadius:7}}/>
                  : <div style={{width:32,height:32,borderRadius:7,background:"rgba(255,255,255,.15)",display:"flex",alignItems:"center",justifyContent:"center",color:brandTextColor,fontWeight:900,fontSize:14,fontFamily:"monospace"}}>LY</div>
                }
                <span style={{color:brandTextColor,fontSize:10,fontWeight:700,letterSpacing:3,opacity:.9}}>{brandName.toUpperCase()}</span>
              </div>
              <div style={{height:3,background:brandSepColor}}/>
              <div style={{background:"white",padding:"12px 20px"}}>
                <div style={{display:"inline-block",background:"#1A7A3A18",border:"1px solid #1A7A3A55",borderRadius:6,padding:"4px 11px",fontSize:11,fontWeight:700,color:"#1A7A3A"}}>✅ Smart Working Approvato</div>
                <div style={{fontSize:12,color:"#333",marginTop:10,lineHeight:1.7}}>Ciao Mario Rossi,<br/>il tuo smart working del 2025-06-15 è stato approvato.</div>
              </div>
              <div style={{background:"#FAFAFA",borderTop:"1px solid #EEE",padding:"10px 20px",textAlign:"center"}}>
                <span style={{fontSize:10,color:"#BBB"}}>{brandFooterText}</span>
              </div>
            </div>
          </div>

          {!logoUrl&&<div style={{background:"#FFF8E8",border:"1px solid #F5C842",borderRadius:9,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#7A5A00"}}>
            ⚠️ Nessun logo impostato — vai in <strong>Config → Logo</strong> per caricare il tuo logo.
          </div>}

          <button onClick={saveBrand} disabled={brandSaving}
            style={{background:BLK,color:"white",border:"none",borderRadius:10,padding:"10px 22px",fontSize:14,fontWeight:700,cursor:"pointer",opacity:brandSaving?.6:1}}>
            {brandSaving?"Salvataggio...":"Salva aspetto"}
          </button>
        </div>
      </div>}

      {/* SEZIONE: SETUP */}
      {activeSection==="setup"&&<div>
        <div className="card" style={{marginBottom:14,padding:"15px 18px"}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:10}}>🔧 Come configurare Resend</div>
          <div style={{fontSize:12,lineHeight:2,color:"#333"}}>
            <div>1. Vai su <strong>resend.com</strong> → crea account gratuito (3.000 email/mese)</div>
            <div>2. Copia la tua API key (inizia con <code style={{background:"#F0F0F0",padding:"1px 5px",borderRadius:4,fontFamily:"monospace"}}>re_</code>)</div>
            <div>3. In Supabase → <strong>Edge Functions → Secrets</strong> → aggiungi:</div>
          </div>
          <div style={{background:"#1A1A1A",color:"#A8FF78",borderRadius:9,padding:"11px 15px",fontFamily:"monospace",fontSize:12,margin:"10px 0",lineHeight:2.2}}>
            RESEND_API_KEY = re_xxxxxxxxxxxx<br/>
            FROM_EMAIL = onboarding@resend.dev
          </div>
          <div style={{fontSize:11,color:SUB,lineHeight:1.7}}>
            💡 <strong>Usa onboarding@resend.dev</strong> come mittente per iniziare subito — funziona senza verificare il dominio. Potrai cambiare in seguito con noreply@likeyousrl.com dopo aver aggiunto il dominio su Resend.
          </div>
        </div>
        <div className="card" style={{padding:"15px 18px"}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:10}}>✉️ Invia email di test</div>
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            <input type="email" value={testEmail} onChange={e=>setTestEmail(e.target.value)} placeholder="tua@email.com"
              style={{flex:1,fontSize:12,padding:"9px 11px",borderRadius:8,border:"1px solid #E2E2E2",fontFamily:"inherit",outline:"none"}}/>
            <button onClick={sendTest} disabled={testSending}
              style={{background:BLK,color:"white",border:"none",borderRadius:8,padding:"9px 14px",fontSize:12,fontWeight:600,cursor:testSending?"wait":"pointer",opacity:testSending?.7:1}}>
              {testSending?"Invio...":"Invia →"}
            </button>
          </div>
          <div style={{fontSize:11,color:SUB}}>Se non arriva: apri il browser con F12 → Console e cerca i messaggi <code style={{background:"#F0F0F0",padding:"1px 4px",borderRadius:3}}>[SW Notif]</code></div>
        </div>
      </div>}

      {saving&&<div style={{textAlign:"center",fontSize:11,color:SUB,marginTop:8}}>Salvataggio...</div>}
    </div>
  );
}
